import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useLists, fetchBoardLists, readListAt, writeListAt, useBoards, countAllLists } from '../hooks/useData';
import { FREE_TIER, LIMIT_COPY } from '../lib/limits';
import { useOverdueHiddenUntil } from '../lib/overdueHide';
import { useTrash } from '../hooks/useTrash';
import { usePlacedSharedLists } from '../hooks/usePlacedSharedLists';
import BoardPickerModal from '../components/BoardPickerModal';
import { TEMPLATE_BLANK, stripTemplateContent } from '../lib/boardTemplates';
import ListPanel from '../components/ListPanel';
import FocusedPanel from '../components/FocusedPanel';
import SearchModal from '../components/SearchModal';
// ConflictModal removed 2026-05-04 — replaced by silent auto-merge
// in conflictResolver.js (line-aware merge prepends offline-only
// lines to top of server content with a marker the in-list banner
// detects). Walter found the modal confusing + the "keep both"
// duplicate-list output was bad UX.
import Sidebar from '../components/Sidebar';
import UndoToastStack from '../components/UndoToast';
import SaveIndicator from '../components/SaveIndicator';
import GridOverlay from '../components/GridOverlay';
import GridCanvas, { computeProportionalHeights, computeColumnWidths } from '../components/GridCanvas';
import AgendaPanel from '../components/AgendaPanel';
import ReminderEditPopover from '../components/ReminderEditPopover';
import { isRemindersEnabled } from '../hooks/useUserPrefs';
import { reminderTextSimilarity } from '../lib/reconcileReminders';
import { hashReminderLine, computeAclForCreate } from '../hooks/useReminders';
import { resolveCaretReminderId, subscribeCaretLine } from '../lib/caretReminder';
import { formatTimeShort, formatMonthDay, parseReminderLine } from '../lib/reminderParser';
import { formatBellLine, organizeReminderList } from '../lib/formatBellLine';
import SettingsModal from '../components/SettingsModal';
import ShortcutsModal from '../components/ShortcutsModal';
import FormatMenu from '../components/FormatMenu';
import DemoWalkthrough, { hasSeenDemo } from '../components/DemoWalkthrough';
import FindBar from '../components/FindBar';
import Wordmark from '../components/Wordmark';
import LoadStuckScreen from '../components/LoadStuckScreen';
import AccountPopover from '../components/AccountPopover';
import ShareModal from '../components/ShareModal';
import ShareBadge from '../components/ShareBadge';
import { SHORTCUT } from '../utils/platform';
import { getListColorHeaderBg } from '../lib/listColors';
import { MenuItem, SubMenu, ColorDot, ColorSwatches, IconPencil, IconColor, IconShare, IconSort, IconTrash, IconPlus } from '../components/MenuKit';
import MobileBoard from './MobileBoard';
import useIsMobile from '../hooks/useIsMobile';
import useViewportWidth from '../hooks/useViewportWidth';
import { useSettings } from '../hooks/useSettings';
import { processOfflineQueue } from '../lib/conflictResolver';
import { logSync } from '../lib/syncLog';
import { loadQueue } from '../lib/offlineQueue';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

const SIDEBAR_WIDTH_KEY = 'lists_sidebar_width';
const SIDEBAR_OPEN_KEY = 'lists_sidebar_open';

// How often to retry sending edits that are queued on this device (Walter
// 2026-08-12). Only ticks while the tab is visible, and an empty queue costs
// a localStorage read and nothing else — processOfflineQueue returns before
// any Firestore call. See the drain effect below for the full rationale.
const QUEUE_DRAIN_MS = 60 * 1000;

// Current outbox depth — how many edits are saved locally but not yet in the
// cloud. Used only for logging the before/after of a drain.
function queueDepth() {
  try { return Object.keys(loadQueue() || {}).length; } catch { return 0; }
}

// Shared style for items inside the "+" dropdown menu. Kept at module
// scope so JSX maps cleanly without re-creating an object per render.
const addMenuItemStyle = {
  padding: '8px 14px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  cursor: 'pointer',
  color: 'var(--text)',
  transition: 'background 0.1s',
};

export default function BoardPage({
  user, board, boards, userPrefs, updateUserPrefs,
  reminders, createReminder, updateReminder, deleteReminder,
  pushSub, listIdToBoardId, sharedWithMe = [],
  // Walter 2026-05-11: per-user shared-list placements. Read from
  // useSharedListPlacements at app root; piped here so BoardPage
  // can merge placed shared lists into the active board's canvas
  // and so the Sidebar can filter them out of "shared with me".
  sharedListPlacements = {},
  sharedListPlacementsByBoard = {},
  setSharedListPlacement,
  clearSharedListPlacement,
  onCreateBlankBoard,
  onRenameBoard, onDeleteBoard, onSignOut, onLinkGoogle, onLinkMagicLink,
  onSwitchBoard, onNewBoard, onUpdateBoard,
  // Owner uid for the current board. For solo boards this equals
  // user.uid. For shared boards (where the current user is a guest
  // editor/viewer), this is the inviter's uid — used as the doc-path
  // root for all sub-collection reads/writes (lists, etc.).
  boardOwnerUid,
  // True when the current user is NOT the owner. Hides owner-only
  // affordances (delete/rename/ACL edits).
  isGuestOnBoard = false,
}) {
  // Use the BOARD's owner uid for the lists subscription, not the current
  // user's uid. For solo boards these are equal; for shared boards the
  // current user is a guest and the lists live under the inviter's path.
  const listsOwnerUid = boardOwnerUid || user.uid;
  const { lists: ownLists, loading: listsLoading, saveList, deleteList, moveListToBoard, refreshQueueOverlay, queuedCount } = useLists(listsOwnerUid, board?.id);

  // Walter 2026-05-11: subscribe to any shared lists the user has
  // placed on THIS board. Each placement record points at the source
  // list (under another user's subtree); the hook opens a doc
  // listener for each one and returns the resolved list docs,
  // annotated with `_sharedFromOwnerUid` so downstream code can tell
  // them apart from own-board lists.
  //
  // Only meaningful when the current user OWNS the active board.
  // On a board the user is a guest on, placements don't apply
  // (you can't "place a shared list" inside someone else's board).
  const placementsForCurrentBoard = useMemo(() => {
    if (isGuestOnBoard) return [];
    if (!board?.id) return [];
    return sharedListPlacementsByBoard?.[board.id] || [];
  }, [isGuestOnBoard, board?.id, sharedListPlacementsByBoard]);
  const placedSharedLists = usePlacedSharedLists(placementsForCurrentBoard);

  // Merged view: own lists + any shared lists placed here. Used by
  // the sidebar, canvas, agenda, search wiring. saveList/deleteList
  // still write to the OWN-board subcollection — handleUpdate /
  // handleDelete below detect placed shared lists and route through
  // writeListAt against the source owner's subtree instead.
  //
  // Walter 2026-05-11: filter ownLists to exclude any doc whose id
  // matches a sharedListPlacements entry. Those are phantom
  // duplicates from an earlier race where handleUpdate's fallback
  // path wrote to the invitee's own subtree before
  // usePlacedSharedLists had resolved the placement (so the find()
  // in handleUpdate returned undefined). The phantom doc rendered as
  // a small "(empty)" tile glued to the placed shared list. Hiding
  // it via the filter is purely cosmetic — the doc is still in
  // Firestore — but it stops the visible bug while a follow-up
  // sweep cleans up orphan docs.
  //
  // Walter 2026-05-17: bug — the filter was suppressing REAL own-
  // lists if a stray self-referencing placement doc had the same id.
  // Walter hit this with the second "Done" list disappearing on
  // desktop: a placement at users/{uid}/sharedListPlacements/{listId}
  // whose `sourceOwnerUid` was the user himself (no cross-user share
  // involved, just a leftover write) was filtering his own real
  // list out. Tightened: a placement only suppresses an own-list if
  // its sourceOwnerUid is a DIFFERENT user — that's the genuine
  // "phantom invitee-side duplicate" case. Self-referencing
  // placements are bogus by definition (a user can't share a list
  // with themselves) and are cleaned up in useSharedListPlacements.
  const lists = useMemo(() => {
    const hasAny = sharedListPlacements && Object.keys(sharedListPlacements).length > 0;
    const ownNotPlaced = hasAny
      ? ownLists.filter(l => {
          const p = sharedListPlacements[l.id];
          if (!p) return true;
          // Self-referencing placement (no real cross-user share). Keep.
          if (!p.sourceOwnerUid || p.sourceOwnerUid === user.uid) return true;
          // Genuine cross-user placement — hide the phantom own-side doc.
          return false;
        })
      : ownLists;
    if (placedSharedLists.length === 0) return ownNotPlaced;
    return [...ownNotPlaced, ...placedSharedLists];
  }, [ownLists, placedSharedLists, sharedListPlacements, user.uid]);
  // Trash subscription — used solely to render the count badge on
  // the sidebar's TrashFooter. Cheap (one Firestore listener) and
  // already implemented at the /trash page level; we just hoist
  // here so the count is live without the user opening trash first.
  // Walter 2026-06-11: pass the uid even for anonymous (guest) users.
  // A guest on their OWN board has a real trash collection under their
  // anonymous uid — deleting a list there writes trash/{anonUid}/items
  // (the trashList function puts the entry in the OWNER's trash, which
  // for a guest's own list IS the guest). The old `isAnonymous ? null`
  // guard zeroed the badge for guests even when their trash had items,
  // which mismatched the /trash page (which uses user?.uid directly and
  // happily shows guests their own deletions). The hook still returns []
  // gracefully for a missing uid, so signed-out states are unaffected.
  const { items: trashItems } = useTrash(user?.uid || null);
  const trashCount = (trashItems || []).length;
  // Mode resolves to 'free' for any legacy board that was created before
  // the grid/free toggle existed and doesn't have the field set yet.
  const boardMode = board?.mode || 'free';
  // Walter 2026-05-11: split guest-on-board into editor vs viewer.
  // The previous blanket `isGuestOnBoard` gated EVERY list/column
  // mutation, blocking guest editors from creating lists on a shared
  // board. Now: guest editors can create lists (and the list inherits
  // the board's ACL so other members see it); guest viewers stay
  // blocked from any mutation.
  const isGuestEditor = !!(
    isGuestOnBoard
    && user?.uid
    && Array.isArray(board?.editors)
    && board.editors.includes(user.uid)
  );
  // Convenience: "the current user is allowed to add lists / mutate
  // board structure". True for owners and guest-editors; false for
  // guest-viewers.
  const canAddLists = !isGuestOnBoard || isGuestEditor;
  const gridColumnCount = Math.max(1, board?.gridColumnCount || 3);
  const [focusedId, setFocusedId] = useState(null);
  const [expandedListId, setExpandedListId] = useState(null);
  const [showSearch, setShowSearch] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  // Share modal state — alpha-gated. Open when the user clicks
  // "share board" in the board dropdown. Phase 2 will also let
  // per-list ⋯ menus open this with a list-scoped target.
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareTarget, setShareTarget] = useState(null); // null = whole board
  const [showAgenda, setShowAgenda] = useState(false);
  // Walter 2026-05-14: digest emails link to listicity.app/?agenda=1.
  // The catch-all in App.js preserves the query when redirecting to
  // /board/<lastBoardId>, so when BoardPage mounts with agenda=1 in
  // the URL, auto-open the agenda panel. Cleared from the URL after
  // consumption so a refresh doesn't re-trigger.
  const _location = useLocation();
  const _navigate = useNavigate();
  useEffect(() => {
    const params = new URLSearchParams(_location.search || '');
    if (params.get('agenda') === '1') {
      setShowAgenda(true);
      // Strip the param without adding a history entry.
      params.delete('agenda');
      const rest = params.toString();
      _navigate(
        { pathname: _location.pathname, search: rest ? `?${rest}` : '' },
        { replace: true },
      );
    }
    // Intentionally only watches pathname + search so a navigation
    // INTO this board with ?agenda=1 fires the open, but state churn
    // (board data, lists, etc.) doesn't.
  }, [_location.pathname, _location.search, _navigate]);
  // Walter 2026-05-11: when the user clicks the 🔁 glyph on a 🔔 line
  // in a source list, we set this id and open the agenda. AgendaPanel
  // reads it on mount to auto-open the structured edit popover for
  // that reminder. Cleared once consumed.
  const [pendingEditReminderId, setPendingEditReminderId] = useState(null);
  // Walter 2026-10-09 (#248): re-render when the caret moves onto or off a
  // reminder line, so the agenda's mark follows it live rather than being a
  // snapshot taken when the panel opened. The caret itself lives outside
  // React (lib/caretReminder) because the editor must set it from a CM6
  // update listener; this is the bridge back in.
  const [caretTick, setCaretTick] = useState(0);
  useEffect(() => subscribeCaretLine(() => setCaretTick(t => t + 1)), []);
  const openReminderEditor = useCallback((reminderId) => {
    if (!reminderId) return;
    setPendingEditReminderId(reminderId);
    setShowAgenda(true);
  }, []);
  // Walter 2026-05-12: "+ new reminder" flow — clicked from a
  // designated reminder list's tile header (ListPanel). Holds the
  // source list + anchor rect so we can render a blank create-mode
  // popover positioned over the button.
  const [createReminderTarget, setCreateReminderTarget] = useState(null);
  const openCreateReminder = useCallback((list, anchorRect) => {
    if (!list) return;
    setCreateReminderTarget({ list, anchorRect: anchorRect || null });
  }, []);
  // openCreateReminderFromAgenda — Walter 2026-05-13: + button in
  // the agenda header. Auto-picks the user's first designated
  // reminder list as the source. Cross-board: if the list lives
  // outside the active board, fetch it via readListAt so the popover
  // gets a real list object (not just an id). No-designated-list
  // case surfaces a toast pointing the user at the designate step.
  // pushToast is declared below in this file (useCallback at ~540),
  // so this useCallback declared HERE can't depend on it — we look
  // it up lazily via pushToastRef instead.
  const pushToastRef = useRef(null);
  // Walter 2026-05-16: returns true when the create popover is
  // actually opened (target list resolved), false on every bail-out
  // (no designated list, cross-board lookup failed, etc.). The agenda
  // header's caller uses the return to decide whether to close the
  // agenda — closing optimistically before knowing if the popover
  // would open meant the toast fired into an empty board and users
  // saw "the button just closed the agenda."
  const openCreateReminderFromAgenda = useCallback(async (anchorRect) => {
    const ids = Array.isArray(userPrefs?.designatedReminderListIds)
      ? userPrefs.designatedReminderListIds
      : [];
    if (ids.length === 0) {
      if (pushToastRef.current) {
        pushToastRef.current(
          'designate a list as a reminder list first (settings → reminders)',
          null,
          { ttlMs: 5000 },
        );
      }
      return false;
    }

    // Walter 2026-05-16: try EACH designated id in order until one
    // resolves to a real list. The earlier shape only tried ids[0] —
    // if the user had multiple designated lists and the first one
    // was stale (list deleted but pref not cleaned up, or list on a
    // board not yet covered by listIdToBoardId), the lookup failed
    // even when later ids would have worked. Also fall back to
    // userPrefs.designatedReminderBoardId as a hint for the
    // legacy-singular case where boardId is stored alongside the
    // list id.
    let list = null;
    for (const id of ids) {
      // Same-board fast path.
      const local = lists.find(l => l.id === id);
      if (local) { list = local; break; }
      // Cross-board: prefer the global map, fall back to the legacy
      // singular designatedReminderBoardId if it matches this id.
      let boardId = listIdToBoardId && listIdToBoardId[id];
      if (!boardId
          && userPrefs?.designatedReminderListId === id
          && userPrefs?.designatedReminderBoardId) {
        boardId = userPrefs.designatedReminderBoardId;
      }
      if (!boardId) continue;
      try {
        const fetched = await readListAt(listsOwnerUid, boardId, id);
        if (fetched) { list = fetched; break; }
      } catch (err) {
        console.warn('[create-reminder] cross-board fetch failed for id', id, err);
      }
    }

    if (!list) {
      if (pushToastRef.current) {
        pushToastRef.current(
          'couldn\'t find your designated reminder list — open it once or re-designate (settings → reminders)',
          null,
          { ttlMs: 6000 },
        );
      }
      return false;
    }
    setCreateReminderTarget({ list, anchorRect: anchorRect || null });
    return true;
  }, [
    userPrefs?.designatedReminderListIds,
    userPrefs?.designatedReminderListId,
    userPrefs?.designatedReminderBoardId,
    lists,
    listIdToBoardId,
    listsOwnerUid,
  ]);
  // handleCreateReminderFromPopover is declared further below — it
  // depends on pushToast, which uses useCallback (TDZ until its const
  // is reached during component render). Keeping the openCreateReminder
  // entry-point here (no TDZ deps) means tile-header wiring can use it
  // directly; the save handler attaches once pushToast is in scope.
  const [showShortcuts, setShowShortcuts] = useState(false);
  // Walter 2026-06-16: Ctrl/Cmd+F find bar (in-list find; Shift+Ctrl+F is
  // the cross-board SearchModal).
  const [showFind, setShowFind] = useState(false);
  // Walter 2026-06-16: first-visit demo walkthrough (replaces the /help
  // page). Auto-shows once per device BEFORE the user starts, and is
  // re-openable on demand via the `listicity:show-walkthrough` event.
  const [showDemo, setShowDemo] = useState(false);
  // Walter 2026-05-06 round 6: bell badge respects the
  // "hide until tomorrow" suppression set from the agenda panel.
  const overdueHiddenForBadge = useOverdueHiddenUntil();
  const [showAccount, setShowAccount] = useState(false);
  // Walter 2026-06-13 (s3): the App-level GuestUpgradeHint banner can't reach
  // this popover directly, so its "sign in" dispatches a window event; open the
  // account popover (desktop) in response. (Mobile has its own listener in
  // MobileBoard, since this component returns <MobileBoard> early there.)
  useEffect(() => {
    const open = () => setShowAccount(true);
    window.addEventListener('listicity:request-account', open);
    return () => window.removeEventListener('listicity:request-account', open);
  }, []);
  // Walter 2026-06-16: on-demand "how it works" walkthrough trigger.
  useEffect(() => {
    const open = () => setShowDemo(true);
    window.addEventListener('listicity:show-walkthrough', open);
    return () => window.removeEventListener('listicity:show-walkthrough', open);
  }, []);
  // First-visit auto-show: BEFORE the user starts (Walter's "more
  // conventional" call), once per device (localStorage flag). Existing
  // users on a new device see it once and can "skip"; it never returns
  // unless re-opened on demand. Marketing routes never trigger it.
  useEffect(() => {
    if (hasSeenDemo()) return;
    const onMarketing = /^\/(landing|about|help|privacy|terms|contact)\b/.test(window.location.pathname);
    if (onMarketing) return;
    setShowDemo(true);
  }, []);
  const {
    theme, setTheme,
    palette, setPalette,
    fontSize, setFontSize,
    lineHeight, setLineHeight,
  } = useSettings();
  const [showBoardMenu, setShowBoardMenu] = useState(false);
  // Walter 2026-06-26: board ⋯ menu (rename / colour / share / sort / delete),
  // split out of the board switcher so the name button is a pure switcher.
  const [showBoardActions, setShowBoardActions] = useState(false);
  // Inline board rename (replaces the old window.prompt). renamingBoard toggles
  // the name button into a text input; boardNameDraft holds the in-flight value.
  const [renamingBoard, setRenamingBoard] = useState(false);
  const [boardNameDraft, setBoardNameDraft] = useState('');
  // Board drag-reorder state. boardDragId is the id being dragged;
  // boardDropTarget tells the row UI where to draw the insertion
  // line (above or below the hovered row).
  const [boardDragId, setBoardDragId] = useState(null);
  const [boardDropTarget, setBoardDropTarget] = useState(null);
  // "+" dropdown — new list / new board / new column.
  const [showAddMenu, setShowAddMenu] = useState(false);
  // Walter 2026-05-11: "place in board…" picker state. Opened from
  // the list ⋯-menu when the list is shared with the current user
  // (i.e. they're not the owner). Holds the target list so onPick can
  // write a placement doc against the right (listId, sourceOwnerUid,
  // sourceBoardId) tuple. Null when the modal is closed.
  const [placeListTarget, setPlaceListTarget] = useState(null);
  const addMenuRef = useRef(null);
  const boardMenuRef = useRef(null);
  const boardActionsRef = useRef(null);
  // Set true when a rename is cancelled (Escape) so the input's blur-commit
  // that fires as it unmounts skips writing the draft.
  const renameCancelRef = useRef(false);
  // Walter 2026-05-15: extended save-status vocabulary.
  // Values: 'synced' (resting), 'typing' (keystroke <500ms ago),
  // 'pending' (debouncing, countdown ticking down), 'syncing' (write
  // in flight). The 'local' caption is derived inside SaveIndicator
  // from (status === 'synced' && !isOnline). Replaces the old
  // 'saving' / 'saved' / 'idle' triplet.
  const [saveStatus, setSaveStatus] = useState('synced');
  // Registry of pending-flush callbacks. Each ListPanel / FocusedPanel
  // registers its flushSave function on mount via the
  // `registerPendingFlush` prop and unregisters on unmount. Tapping
  // the SaveIndicator's floppy fires all registered callbacks, so
  // simultaneously-pending edits across multiple open lists all commit
  // together when the user force-syncs.
  const pendingFlushesRef = useRef(new Set());
  const registerPendingFlush = useCallback((fn) => {
    pendingFlushesRef.current.add(fn);
    return () => { pendingFlushesRef.current.delete(fn); };
  }, []);
  /**
   * flushAllPending — like forceSyncAll, but AWAITABLE.
   *
   * Walter 2026-09-03 (content-writer audit, fix 2). The reminder-side
   * mutators below build a whole document out of `lists[…].content` — the last
   * SAVED value — and write it back. The panels hold keystrokes for up to 4s
   * (save debounce) and can hold them far longer while the user is actively
   * typing (#162's sticky-editing refusal), so acting from the agenda
   * mid-sentence wrote a document missing everything typed since the last
   * flush. Organize already guards against this by flushing first (#192);
   * these paths didn't. Same fix, one await.
   */
  /**
   * readFreshListContent — the list's text AS THE SERVER HAS IT, right now.
   *
   * Walter 2026-09-04 (content-writer audit, fix 1). The reminder-side
   * mutators below each build a whole document out of `lists[…].content` — the
   * last value this client happened to receive — change one line, and write
   * the lot back. Anything another window (or this one) wrote since is
   * overwritten. Flushing first (fix 2) closes the unsaved-buffer half; this
   * closes the stale-snapshot half.
   *
   * Same shape organize has used since #191 and #192: re-read, do the surgery
   * on THAT text, and tag the write with what it was based on so a race lands
   * as a conflict band instead of a silent clobber. Not a transaction — a
   * write landing between the read and our write is caught by
   * `__baseContent`, which is the outcome we want anyway.
   */
  const readFreshListContent = useCallback(async (listId, boardId, fallback) => {
    if (!listId || !boardId) return fallback ?? null;
    try {
      const fresh = await readListAt(listsOwnerUid, boardId, listId);
      if (fresh && typeof fresh.content === 'string') return fresh.content;
    } catch { /* offline or denied — fall through to what we have */ }
    return fallback ?? null;
  }, [listsOwnerUid]);

  const flushAllPending = useCallback(async () => {
    const fns = Array.from(pendingFlushesRef.current);
    await Promise.all(fns.map((fn) => {
      try { return Promise.resolve(fn()); } catch (err) {
        console.warn('[flushAllPending] flush failed', err);
        return null;
      }
    }));
  }, []);
  const forceSyncAll = useCallback(() => {
    // Snapshot so callbacks that delete themselves during flush don't
    // mutate the iterator.
    const fns = Array.from(pendingFlushesRef.current);
    for (const fn of fns) {
      try { fn(); } catch (err) { console.warn('[forceSync] flush failed', err); }
    }
  }, []);
  // Walter 2026-06-20 sync hardening: flush pending edits the moment we regain
  // connectivity OR return to the foreground — the panels already flush on
  // BACKGROUNDING (hidden / pagehide), but never on the way back. A device
  // that edited offline (or while dormant) otherwise sits on an un-flushed
  // buffer until it's reopened, which is how a stale edit ends up colliding
  // with a second device's later edit (Walter's dormant-device repro). This
  // only changes WHEN we push — every flush still routes through the 3-way
  // conflict detector — and is a no-op when nothing is pending.
  useEffect(() => {
    const onBackOnline = () => forceSyncAll();
    const onVisible = () => { if (document.visibilityState === 'visible') forceSyncAll(); };
    window.addEventListener('online', onBackOnline);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('online', onBackOnline);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [forceSyncAll]);
  const [jumpTarget, setJumpTarget] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    try { const v = localStorage.getItem(SIDEBAR_OPEN_KEY); return v == null ? true : v === '1'; }
    catch { return true; }
  });
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    try { const v = parseInt(localStorage.getItem(SIDEBAR_WIDTH_KEY) || '', 10); return Number.isFinite(v) && v >= 180 ? v : 240; }
    catch { return 240; }
  });
  const [toasts, setToasts] = useState([]);
  // Walter 2026-05-13: pending-organize state. When the user runs an
  // organize pass on a list, we write the new content but lock the
  // list (effectiveReadOnly forced true on the tile) and surface a
  // header band with "Accept" / "Undo" buttons until they commit.
  const [arrangeUndo, setArrangeUndo] = useState(null);
  const [arrangePicker, setArrangePicker] = useState(null); // { detected, maxFit, selected } | null
  const [gridMode, setGridMode] = useState(false); // green-line post-arrange tweak overlay
  const isMobile = useIsMobile();
  // Header priority-collapse (Walter 2026-06-24; reworked 2026-07-19 after
  // the format strip grew): as the window narrows, low-priority chrome gives
  // way FIRST so the board name + format controls shrink cleanly —
  //   <1450 searchCompact — search drops its label (icon only) BEFORE the
  //          format strip collapses at 1200 (Walter: "make search smaller
  //          before collapsing the format box")
  //   <1200 format strip → `format ▾` dropdown (FormatMenu.INLINE_MIN_WIDTH)
  //   <1100 headerCompact — the "board:" prefix hides
  //   <1000 wordmark hides — frees ~100px so the board name and the format
  //          dropdown survive the super-narrow desktop range (768–1000)
  const viewportWidth = useViewportWidth();
  const headerCompact = viewportWidth < 1100;
  const searchCompact = viewportWidth < 1450;
  const showWordmark = viewportWidth >= 1000;
  const [showMobileHint, setShowMobileHint] = useState(false);
  const prevIsMobileRef = useRef(isMobile);
  const saveStatusTimer = useRef(null);
  const canvasRef = useRef(null);
  const nextZRef = useRef(1);
  // Walter 2026-05-11: tracks lists that have been requested via
  // addList() but whose Firestore snapshot hasn't propagated yet.
  // Keyed initially by a synthetic reservationKey, then re-keyed to
  // the real Firestore ID once saveList returns. Used so concurrent
  // addList calls don't collide on the same default title ("list 1")
  // or the same x/y position before the lists prop catches up.
  // Cleanup runs in the effect below this declaration.
  const inFlightCreatesRef = useRef(new Map());
  const firedForFocusRef = useRef(null);
  const arrangeUndoTimer = useRef(null);
  // eslint-disable-next-line no-unused-vars
  const navigate = useNavigate();

  // Walter 2026-06-11: OPTIMISTIC open/close. Firestore's realtime channel
  // goes dormant when the tab is idle/backgrounded, so the FIRST close or
  // open after a lull waits several seconds for the channel to wake before
  // the snapshot reflects the new state. We override the RENDERED state
  // locally so the tile appears/disappears instantly; the real snapshot
  // reconciles (and clears) the override when it lands. Failsafe: if the
  // write throws, the handler reverts its own override.
  const [optimisticListState, setOptimisticListState] = useState({}); // { [id]: 'open' | 'closed' }
  const canvasLists = useMemo(
    () => lists.filter(l => {
      // Walter's QA pass 2026-08-25: a 'trashed' override hides the tile the
      // instant he confirms, instead of after the trashList Cloud Function
      // returns. His report: "it takes a few seconds for the tile to actually
      // disappear" — the function log shows why, and it isn't our code:
      // 16:39:19.507 AUTOSCALING cold start → 16:39:21.067 container ready.
      // ~2.5s of container boot before any work begins. The delete must still
      // go through the function (it cascade-deletes the list's reminders into
      // the same trash entry), so the only thing to fix is the WAIT. The
      // reconcile effect below drops the override once the list really goes,
      // and deleteList reverts it if the call fails.
      if (optimisticListState[l.id] === 'trashed') return false;
      const eff = optimisticListState[l.id] || l.state;
      return !eff || eff === 'open' || (l.state === 'archived' && l.id === focusedId);
    }),
    [lists, focusedId, optimisticListState]
  );
  // Clear an override once the snapshot's real state matches it (or the
  // list is gone) — so a stale override never masks a later reopen/undo.
  useEffect(() => {
    setOptimisticListState(prev => {
      const ids = Object.keys(prev);
      if (!ids.length) return prev;
      let changed = false;
      const next = { ...prev };
      for (const id of ids) {
        const l = lists.find(x => x.id === id);
        const real = l ? (l.state || 'open') : 'gone';
        if (real === 'gone' || real === prev[id]) { delete next[id]; changed = true; }
      }
      return changed ? next : prev;
    });
  }, [lists]);

  const canvasContentSize = useMemo(() => {
    let w = 0, h = 0;
    for (const l of canvasLists) {
      w = Math.max(w, (l.x || 0) + (l.w || 280) + 40);
      h = Math.max(h, (l.y || 0) + (l.h || 320) + 40);
    }
    return { w, h };
  }, [canvasLists]);

  // When a panel gains focus, check whether any part of it is actually outside
  // the canvas viewport. If so, smooth-scroll it into view.
  // Runs at most once per focusedId change (tracked via firedForFocusRef), and
  // reruns when canvasLists updates so closed->open reopens get a chance to
  // check once the note actually enters the list.
  useEffect(() => {
    if (!focusedId) {
      firedForFocusRef.current = null;
      return;
    }
    if (firedForFocusRef.current === focusedId) return;
    const el = canvasRef.current;
    if (!el) return;
    const target = canvasLists.find(l => l.id === focusedId);
    if (!target) return;
    firedForFocusRef.current = focusedId;

    const raf = requestAnimationFrame(() => {
      const tx = target.x || 0;
      const ty = target.y || 0;
      const tw = target.w || 280;
      const th = target.h || 320;
      const vx = el.scrollLeft;
      const vy = el.scrollTop;
      const vw = el.clientWidth;
      const vh = el.clientHeight;
      if (vw < 50 || vh < 50) return;
      const TOL = 16;
      const offscreen =
        tx < vx - TOL ||
        ty < vy - TOL ||
        tx + tw > vx + vw + TOL ||
        ty + th > vy + vh + TOL;
      if (!offscreen) return;
      const nextLeft = Math.max(0, tx + tw / 2 - vw / 2);
      const nextTop  = Math.max(0, ty + th / 2 - vh / 2);
      try {
        el.scrollTo({ left: nextLeft, top: nextTop, behavior: 'smooth' });
      } catch {
        el.scrollLeft = nextLeft;
        el.scrollTop = nextTop;
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [focusedId, canvasLists]);

  useEffect(() => {
    try { localStorage.setItem(SIDEBAR_OPEN_KEY, sidebarOpen ? '1' : '0'); } catch {}
  }, [sidebarOpen]);
  useEffect(() => {
    try { localStorage.setItem(SIDEBAR_WIDTH_KEY, String(sidebarWidth)); } catch {}
  }, [sidebarWidth]);

  useEffect(() => {
    const maxZ = lists.reduce((m, l) => Math.max(m, l.z || 0), 0);
    if (maxZ >= nextZRef.current) {
      nextZRef.current = maxZ + 1;
    }
  }, [lists]);

  // Walter 2026-05-11: drop in-flight create reservations once their
  // list ID actually appears in lists. The Map keyed by real Firestore
  // IDs gets pruned here; the parallel 10s sweeper below handles any
  // stale `pending:*` synthetic keys whose saveList never resolved
  // (network failure mid-write).
  useEffect(() => {
    const presentIds = new Set(lists.map(l => l.id));
    for (const key of inFlightCreatesRef.current.keys()) {
      if (presentIds.has(key)) inFlightCreatesRef.current.delete(key);
    }
  }, [lists]);
  useEffect(() => {
    const id = setInterval(() => {
      for (const key of Array.from(inFlightCreatesRef.current.keys())) {
        if (typeof key === 'string' && key.startsWith('pending:')) {
          inFlightCreatesRef.current.delete(key);
        }
      }
    }, 10000);
    return () => clearInterval(id);
  }, []);

  // Walter 2026-05-10: tab title reflects the active board so users
  // can identify the right tab when they have several boards open in
  // parallel browser tabs. Resets to 'listicity' on unmount so the
  // tab name doesn't carry a stale board name into other routes.
  useEffect(() => {
    const name = (board?.name || '').trim();
    // Walter 2026-05-15: when running as an installed PWA the OS / browser
    // window manager already prepends the manifest's `name: "listicity"`
    // — so a title of `Foo · listicity` reads as "listicity - Foo ·
    // listicity" in the title bar (double brand). Drop the suffix when
    // standalone; keep it in normal browser tabs where it's the only
    // brand context.
    const standalone = (typeof window !== 'undefined') && (
      window.navigator.standalone === true
      || (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches)
    );
    if (standalone) {
      document.title = name || 'listicity';
    } else {
      document.title = name ? `${name} · listicity` : 'listicity';
    }
    return () => { document.title = 'listicity'; };
  }, [board?.name]);

  // Walter 2026-05-19: ref declaration (initialized here; the
  // mirror-effect lives down below, AFTER addList is defined to
  // avoid the temporal-dead-zone ReferenceError that crashed the
  // first attempt at this fix). The keydown handler reads
  // `addListRef.current?.()` at invocation time — so even though the
  // ref starts null on first render, by the time the user actually
  // presses 'n' the mirror-effect has run and the ref points at the
  // latest addList.
  const addListRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      // Walter 2026-06-16: Ctrl/Cmd+F = browser-style FIND BAR in the
      // current list(s) (FindBar). Shift+Ctrl/Cmd+F = the cross-board
      // results search (SearchModal). Native find can't see inside the
      // list textareas, so Ctrl+F drives our own find instead.
      if ((e.ctrlKey || e.metaKey) && (e.key === 'f' || e.key === 'F')) {
        e.preventDefault();
        if (e.shiftKey) { setShowFind(false); setShowSearch(true); }
        else { setShowSearch(false); setShowFind(true); }
      }
      // Walter 2026-05-06 round 8: Ctrl+N is intercepted by the
      // browser on Windows (opens a new window) and `e.preventDefault`
      // can't override OS-level shortcuts. Switched to plain `n`
      // gated on non-editable focus — same gating pattern as `\`
      // and `?`. Cleaner anyway: matches Notion's "n for new" muscle
      // memory. Cmd+N retained on Mac (browser doesn't grab it on
      // every Mac browser, and the muscle memory is widespread).
      if (e.metaKey && e.key === 'n') {
        e.preventDefault();
        setExpandedListId(null);
        addListRef.current?.();
      }
      if (e.key === 'n' && !e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey) {
        const el = document.activeElement;
        const tag = el?.tagName;
        const editable = tag === 'INPUT' || tag === 'TEXTAREA' || el?.isContentEditable;
        if (!editable) {
          e.preventDefault();
          setExpandedListId(null);
          addListRef.current?.();
        }
      }
      if (e.key === '\\' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        const el = document.activeElement;
        const tag = el?.tagName;
        const editable = tag === 'INPUT' || tag === 'TEXTAREA' || el?.isContentEditable;
        if (!editable) {
          e.preventDefault();
          setSidebarOpen(v => !v);
        }
      }
      // Walter 2026-05-06 round 7: `?` (Shift+/) opens the
      // keyboard cheat-sheet. Skip when the user is typing into
      // an input/textarea — they likely meant to type a literal "?".
      if (e.key === '?' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        const el = document.activeElement;
        const tag = el?.tagName;
        const editable = tag === 'INPUT' || tag === 'TEXTAREA' || el?.isContentEditable;
        if (!editable) {
          e.preventDefault();
          setShowShortcuts(v => !v);
        }
      }
      if (e.key === 'Escape') { setShowSearch(false); setShowFind(false); setShowBoardMenu(false); setShowBoardActions(false); setShowAddMenu(false); setArrangePicker(null); setGridMode(false); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line
  }, [board]);

  // Outside-click dismiss for the board switcher dropdown. Only installs
  // listeners while the menu is open so we don't pay the cost every click.
  useEffect(() => {
    if (!showBoardMenu) return;
    // Listen on `click` (not mousedown) so the listener fires AFTER
    // React-synthetic events have fully resolved. The setTimeout(0)
    // defers binding by a tick — without it, the same click that
    // opened the menu would immediately close it. Capture phase so
    // we still catch clicks even if a child stops propagation.
    const onClick = (e) => {
      if (boardMenuRef.current && !boardMenuRef.current.contains(e.target)) {
        setShowBoardMenu(false);
      }
    };
    const timer = setTimeout(() => {
      document.addEventListener('click', onClick, true);
      document.addEventListener('touchstart', onClick, true);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('touchstart', onClick, true);
    };
  }, [showBoardMenu]);

  // Outside-click dismiss for the board ⋯ actions menu (same pattern).
  useEffect(() => {
    if (!showBoardActions) return undefined;
    const onClick = (e) => {
      if (boardActionsRef.current && !boardActionsRef.current.contains(e.target)) {
        setShowBoardActions(false);
      }
    };
    const timer = setTimeout(() => {
      document.addEventListener('click', onClick, true);
      document.addEventListener('touchstart', onClick, true);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('touchstart', onClick, true);
    };
  }, [showBoardActions]);

  // Inline board rename helpers (used by the board ⋯ menu).
  const startBoardRename = useCallback(() => {
    renameCancelRef.current = false;
    setBoardNameDraft(board?.name || '');
    setRenamingBoard(true);
    setShowBoardActions(false);
  }, [board?.name]);
  const commitBoardRename = useCallback(() => {
    setRenamingBoard(false);
    if (renameCancelRef.current) { renameCancelRef.current = false; return; }
    const n = boardNameDraft.trim();
    if (n && n !== board?.name) onRenameBoard(board.id, n);
  }, [boardNameDraft, board?.name, board?.id, onRenameBoard]);
  const cancelBoardRename = useCallback(() => {
    renameCancelRef.current = true;
    setRenamingBoard(false);
  }, []);

  // Outside-click dismiss for the "+" dropdown.
  useEffect(() => {
    if (!showAddMenu) return;
    const onDown = (e) => {
      if (addMenuRef.current && !addMenuRef.current.contains(e.target)) {
        setShowAddMenu(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [showAddMenu]);


  // Walter 2026-05-15: state machine for the new SaveIndicator. The
  // values ListPanel / FocusedPanel can report:
  //   - 'typing'  → keystroke; reset the typing→pending timer
  //   - 'syncing' → write in flight; cancel any typing timer
  //   - 'synced'  → write done; cancel any typing timer
  // Legacy values 'saving' and 'saved' map to 'typing' and 'synced'
  // respectively so any caller we missed still produces something
  // sensible.
  const typingIdleTimer = useRef(null);
  const handleSaveStatus = useCallback((rawStatus) => {
    // Map legacy values forward to the new vocabulary.
    let status = rawStatus;
    if (status === 'saving') status = 'typing';
    if (status === 'saved')  status = 'synced';

    if (status === 'typing') {
      // Each keystroke restarts a 500ms idle timer. If 500ms passes
      // with no further 'typing' calls, we transition to 'pending'
      // (which is when the SaveIndicator starts ticking down).
      setSaveStatus('typing');
      if (typingIdleTimer.current) clearTimeout(typingIdleTimer.current);
      typingIdleTimer.current = setTimeout(() => {
        setSaveStatus((s) => (s === 'typing' ? 'pending' : s));
      }, 500);
      return;
    }
    if (status === 'syncing' || status === 'synced') {
      if (typingIdleTimer.current) {
        clearTimeout(typingIdleTimer.current);
        typingIdleTimer.current = null;
      }
      setSaveStatus(status);
      return;
    }
    // Unknown value — fall through with no state change.
  }, []);

  // Offline-queue drain. The resolver is silent: every queued patch either
  // auto-commits (clean baseline) or auto-merges (line-aware merge) — see the
  // conflict-redesign comment in lib/conflictResolver.js.
  //
  // Walter 2026-08-12: this used to run ONLY on mount and on the browser's
  // `online` event — and that is the "sync doesn't happen" bug. A save can
  // fail while genuinely online (contention, a blocked WebChannel, a transient
  // 5xx); the edit is queued to localStorage; and a device that never
  // disconnects never fires `online`, so the queue sat there until the app was
  // restarted. His words, three times in five days: "neither device was
  // offline", "a conflict even though both were online all day", and a
  // divergence that resolved ~30 minutes after reopening the app.
  //
  // So also drain on a timer while the tab is VISIBLE, and whenever the tab
  // comes back to the foreground. Cost is nil when there's nothing to send:
  // processOfflineQueue reads the queue from localStorage and returns before
  // touching Firestore when it's empty, so an idle tick is zero reads and zero
  // writes. This changes only WHEN we retry — never what gets written; every
  // replay still goes through the same 3-way conflict detection.
  useEffect(() => {
    if (!user?.uid) return;
    let cancelled = false;
    let inFlight = false;
    const runResolver = async () => {
      if (cancelled || inFlight) return;
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
      // Overlap guard: the timer, the foreground handler and `online` can all
      // fire at once, and two concurrent drains would replay the same patch
      // twice.
      inFlight = true;
      try {
        const before = queueDepth();
        const res = await processOfflineQueue(user.uid);
        if (cancelled) return;
        // Only log when there was actually something to send — otherwise a
        // 60s timer would fill the log with "nothing to do" forever.
        if (before > 0) {
          logSync('drained', {
            before,
            after: queueDepth(),
            committed: res?.autoCommitted ?? 0,
            merged: res?.autoMerged ?? 0,
          });
        }
        refreshQueueOverlay();
      } catch (err) {
        console.warn('[conflict] resolver failed:', err);
        logSync('drain-failed', { reason: err?.code || err?.message || 'unknown' });
      } finally {
        inFlight = false;
      }
    };
    const runIfVisible = () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      runResolver();
    };
    runResolver();
    window.addEventListener('online', runResolver);
    document.addEventListener('visibilitychange', runIfVisible);
    const timer = setInterval(runIfVisible, QUEUE_DRAIN_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
      window.removeEventListener('online', runResolver);
      document.removeEventListener('visibilitychange', runIfVisible);
    };
  }, [user?.uid, refreshQueueOverlay]);

  const focusPanel = useCallback((id) => {
    nextZRef.current += 1;
    setFocusedId(id);
    saveList(id, { z: nextZRef.current });
  }, [saveList]);

  // Toast helpers — declared above addList because addList fires a guest
  // reminder toast on first save when the user is anonymous.
  const pushToast = useCallback((message, undoAction, opts = {}) => {
    const id = Date.now() + Math.random();
    // opts.onClick — optional tap-the-whole-toast handler (the toast
    // becomes a clickable card with a small chevron affordance). Used
    // by the morning reminder summary to deep-link into the agenda.
    setToasts(ts => [...ts, {
      id,
      message,
      undoAction,
      onClick: opts.onClick,
      ttlMs: opts.ttlMs,
    }]);
  }, []);
  // Keep pushToastRef in sync so callbacks declared above pushToast
  // (which is here for historical reasons — see addList) can still
  // reach the latest pushToast without participating in its deps.
  pushToastRef.current = pushToast;
  const dismissToast = useCallback((id) => {
    setToasts(ts => ts.filter(t => t.id !== id));
  }, []);
  // Create-reminder save handler — placed AFTER pushToast since it
  // depends on pushToast in its deps array. Putting it above pushToast's
  // const declaration would TDZ-throw at render and blank the screen.
  const handleCreateReminderFromPopover = useCallback(async (payload /* , notesText (deferred) */) => {
    // Flush any editor still holding keystrokes before we read `lists` —
    // otherwise this whole-document write discards them (audit fix 2).
    await flushAllPending();
    const target = createReminderTarget?.list;
    if (!target || !createReminder) {
      setCreateReminderTarget(null);
      return;
    }
    const synthetic = {
      text: payload.text || '',
      parsedDate: payload.parsedDate || null,
      recurrence: payload.recurrence || null,
      status: 'open',
    };
    const bellLine = formatBellLine(synthetic);
    const PREFIX = '🔔 ';
    const postPrefix = bellLine.startsWith(PREFIX) ? bellLine.slice(PREFIX.length) : bellLine;
    const sourceLineHash = hashReminderLine(postPrefix);
    try {
      await createReminder({
        sourceListId: target.id,
        sourceBoardId: board?.id || null,
        sourceLineHash,
        text: payload.text || '',
        parsedDate: payload.parsedDate || null,
        recurrence: payload.recurrence || null,
        // Bug #12 — denormalised ACL so collaborators on the source
        // list see this reminder via the collectionGroup query in
        // useReminders. See docs/proposals/shared-reminder-sync.md.
        aclUids: computeAclForCreate(target, user?.uid),
      });
      // Audit fix 1: append to the SERVER's text. Appending to a stale copy
      // rewrites the whole document and drops anything typed since.
      const existing = await readFreshListContent(
        target.id, board?.id, target.content || '',
      ) || '';
      const sep = existing.length > 0 && !existing.endsWith('\n') ? '\n' : '';
      const newContent = existing + sep + bellLine;
      await saveList(target.id, { content: newContent, __baseContent: existing });
      pushToast(`🔔 reminder added`, null, { ttlMs: 3000 });
    } catch (err) {
      console.error('[create-reminder] failed', err);
      pushToast('couldn\'t create reminder — try again', null, { ttlMs: 4500 });
    }
    setCreateReminderTarget(null);
  }, [createReminderTarget, createReminder, board?.id, saveList, pushToast, user?.uid, flushAllPending, readFreshListContent]);
  const runUndo = useCallback((id) => {
    setToasts(ts => {
      const t = ts.find(x => x.id === id);
      if (t) Promise.resolve(t.undoAction()).catch(err => console.warn('[undo] failed:', err));
      return ts.filter(x => x.id !== id);
    });
  }, []);

  // Walter 2026-05-13: shared organize trigger used by both the
  // tile-header button (per-list) and the agenda toolbar button.
  // Resolves the source list (local-board or cross-board via
  // readListAt), runs organizeReminderList, writes the new content,
  // and pushes an undo toast so the pre-sort content can be restored
  // in one tap. Walter 2026-07-04: applies directly (no review band).
  const triggerOrganize = useCallback(async ({ listId, sourceBoardId }) => {
    const localTarget = lists.find(l => l.id === listId);
    const resolvedBoardId = sourceBoardId
      || (localTarget ? board?.id : (listIdToBoardId && listIdToBoardId[listId]) || null);
    if (!resolvedBoardId) {
      pushToast('couldn\'t locate the reminder list', null, { ttlMs: 2400 });
      return;
    }
    // Walter 2026-06-16: read the FRESHEST content from the server rather
    // than the local snapshot. The editor flushes its pending content +
    // capture write right before calling this, so a server read here gets
    // read-your-writes consistency — the just-persisted lines. Using the
    // local `lists` snapshot instead lagged that flush, so organize would
    // rewrite stale content and race the capture write, dropping a
    // just-typed reminder line from the list text (its reminder doc
    // survived, so the reminder still fired — but the text desynced).
    let sourceList = null;
    try {
      sourceList = await readListAt(listsOwnerUid, resolvedBoardId, listId);
    } catch { /* fall back to the local snapshot below */ }
    if (!sourceList) sourceList = localTarget;
    if (!sourceList) {
      pushToast('couldn\'t open the reminder list', null, { ttlMs: 2400 });
      return;
    }
    const prevContent = sourceList.content || '';
    const { newContent, changed } = organizeReminderList(
      prevContent,
      (reminders || []).filter(r => r.sourceListId === listId),
    );
    if (!changed) {
      pushToast('nothing to organise', null, { ttlMs: 1800 });
      return null;
    }
    if (resolvedBoardId === board?.id) {
      // Walter 2026-08-24: tag the sort with the exact text it sorted, so the
      // write is CONFLICT-CHECKED instead of a blind last-writer-wins. His
      // "sort, type, sort in quick succession" run showed the document
      // oscillating between two versions inside the same second — organize's
      // write and the panel's own save racing — and an untagged write means
      // whichever lands last silently wins, which is how lines went missing.
      // With the base attached, a sort that raced someone else's write bands
      // instead of clobbering it.
      saveList(listId, { content: newContent, __baseContent: prevContent });
    } else {
      writeListAt(listsOwnerUid, resolvedBoardId, listId, { content: newContent });
    }
    // Walter 2026-07-04: organize applies directly now — the old
    // review/accept band was needless friction now that the sort is
    // reliable. Offer a one-tap undo via toast that restores the
    // pre-sort content to whichever board the list lives on.
    pushToast('✓ reminders organised', () => {
      if (resolvedBoardId === board?.id) {
        saveList(listId, { content: prevContent });
      } else {
        writeListAt(listsOwnerUid, resolvedBoardId, listId, { content: prevContent });
      }
    }, { ttlMs: 6000 });
    // Walter 2026-08-24: hand the sorted text BACK to the caller so the open
    // panel can put it straight into its own buffer. Organize rewrites every
    // line, and routing that through Firestore turned a local command into a
    // foreign change: the panel's merge-on-receive then had to reconcile
    // "every line moved" against whatever the user typed during the
    // round-trip, which cannot merge cleanly. That is what produced his
    // 2026-08-24 conflict band — one device, both ends online, two clashing
    // regions, base differing from line 1. Adopting our own sort locally
    // removes the divergence instead of merging it.
    return { listId, newContent, prevContent };
  }, [lists, board, listIdToBoardId, listsOwnerUid, reminders, saveList, pushToast]);

  // BoardRoute stashes a "no-access redirect" reason in sessionStorage
  // when it has to bounce the user from a board URL they don't have
  // access to. We surface that as a toast on the destination board so
  // the user doesn't silently land on a different board than they
  // typed. Read-once: consume the flag and clear it.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('lists_redirect_reason');
      if (!raw) return;
      const data = JSON.parse(raw);
      sessionStorage.removeItem('lists_redirect_reason');
      // Only show toasts for recent redirects (avoid showing stale
      // ones from a previous session).
      if (Date.now() - (data.at || 0) > 10_000) return;
      if (data.kind === 'no-access') {
        pushToast(
          "you don't have access to that board — opened your default instead",
          null,
          { ttlMs: 7000 }
        );
      }
    } catch {}
    // Run once on mount; don't re-fire when pushToast identity changes.
    // eslint-disable-next-line
  }, []);

  // Remove a specific (empty) grid column. Shifts every higher-indexed
  // list's gridCol down by 1, drops the slot from gridColumnWeights
  // if present, and decrements gridColumnCount. Caller is responsible
  // for confirming the column is actually empty — the button only
  // surfaces on empty columns. Always preserves at least one column.
  const removeGridColumn = useCallback(async (columnIndex) => {
    if (!board) return;
    const cur = board?.gridColumnCount || 1;
    if (cur <= 1) return;
    const writes = (lists || [])
      .filter(l => typeof l.gridCol === 'number' && l.gridCol > columnIndex)
      .map(l => saveList(l.id, { gridCol: l.gridCol - 1 }));
    await Promise.all(writes);
    const patch = { gridColumnCount: cur - 1 };
    const weights = board?.gridColumnWeights;
    if (Array.isArray(weights) && weights.length === cur) {
      const next = [...weights];
      next.splice(columnIndex, 1);
      patch.gridColumnWeights = next;
    }
    await onUpdateBoard(board.id, patch);
  }, [board, lists, saveList, onUpdateBoard]);

  // Reorder boards by writing fresh `order` values to every board in
  // the new sequence. We re-issue every `order` (not just the moved
  // one) so the sort stays stable across future inserts. N writes
  // per drag — fine for single-digit board counts. The local
  // useBoards listener picks up the change and re-sorts.
  const reorderBoards = useCallback(async (sourceId, targetId, position) => {
    if (!sourceId || sourceId === targetId) return;
    const ordered = [...boards];
    const sourceIdx = ordered.findIndex(b => b.id === sourceId);
    if (sourceIdx < 0) return;
    const [moved] = ordered.splice(sourceIdx, 1);
    let targetIdx = ordered.findIndex(b => b.id === targetId);
    if (targetIdx < 0) targetIdx = ordered.length - 1;
    const insertIdx = position === 'before' ? targetIdx : targetIdx + 1;
    ordered.splice(insertIdx, 0, moved);
    await Promise.all(ordered.map((b, i) => onUpdateBoard(b.id, { order: i })));
  }, [boards, onUpdateBoard]);

  // Daily morning summary — once per local-day, push a toast that
  // counts what's due today and how many undated reminders are
  // hanging around. Walter's spec: "every morning we say something
  // like 'Today: some reminders and 7 reminders w/o dates'". Gated
  // on the reminders feature so we don't surprise users with a toast
  // about a feature they haven't enabled. Stored last-shown date in
  // localStorage so it survives reloads but doesn't sync across
  // devices (deliberate — each device gets its own morning ping).
  const summaryShownRef = useRef(false);
  useEffect(() => {
    if (summaryShownRef.current) return;
    if (!user || !reminders) return;
    if (!isRemindersEnabled(userPrefs)) return;
    let lastShown;
    try { lastShown = localStorage.getItem('lists_last_summary_date'); } catch { lastShown = null; }
    const todayStr = (() => {
      const d = new Date();
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    })();
    if (lastShown === todayStr) { summaryShownRef.current = true; return; }
    // Compute counts.
    const now = new Date();
    const today0 = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow0 = new Date(today0.getTime() + 86400000);
    let dueToday = 0;
    let overdue = 0;
    let undated = 0;
    for (const r of reminders) {
      if (r.status === 'done') continue;
      const d = r.parsedDate?.toDate?.() || (r.parsedDate ? new Date(r.parsedDate) : null);
      if (!d) { undated++; continue; }
      if (d < today0) { overdue++; continue; }
      if (d < tomorrow0) { dueToday++; }
    }
    if (dueToday + overdue + undated === 0) {
      // Nothing to nudge about — still mark today as shown so we
      // don't keep checking on every render.
      try { localStorage.setItem('lists_last_summary_date', todayStr); } catch {}
      summaryShownRef.current = true;
      return;
    }
    // Walter 2026-05-15: copy reworked. Previously read "good morning
    // · 3 no date" which didn't make clear it was about reminders. Now
    // leads with 🔔 (the brand reminder glyph) and the word
    // "reminders". Toast is also clickable now — taps open the agenda
    // panel, so the user can act on the summary directly.
    const parts = [];
    if (dueToday > 0) parts.push(`${dueToday} due today`);
    if (overdue > 0) parts.push(`${overdue} overdue`);
    if (undated > 0) parts.push(`${undated} with no date`);
    const total = dueToday + overdue + undated;
    const noun = total === 1 ? 'reminder' : 'reminders';
    const message = `🔔 good morning — ${total} ${noun}: ${parts.join(' · ')}`;
    pushToast(message, null, {
      ttlMs: 9000,
      onClick: () => setShowAgenda(true),
    });
    try { localStorage.setItem('lists_last_summary_date', todayStr); } catch {}
    summaryShownRef.current = true;
  }, [user, reminders, userPrefs, pushToast]);

  /**
   * updateReminderAndSource — agenda edits update the Firestore reminder
   * doc AND rewrite the 🔔 line in the source list so the two stay
   * consistent. The line is found by sourceLineHash. We rebuild its
   * visible text from the merged reminder (post-patch) so date, time
   * and text edits all reflect immediately. Day-name is recomputed
   * from the new parsedDate's weekday so "Sunday" → "Tue" when the
   * user moves the date. Cleared dates collapse to "🔔 text".
   */
  const updateReminderAndSource = useCallback(async (id, patch) => {
    // Flush any editor still holding keystrokes before we read `lists` —
    // otherwise this whole-document write discards them (audit fix 2).
    await flushAllPending();
    const r = (reminders || []).find(x => x.id === id);
    if (!r) return updateReminder(id, patch);
    const merged = {
      ...r,
      ...patch,
      parsedDate: patch.parsedDate !== undefined
        ? patch.parsedDate
        : (r.parsedDate?.toDate?.() || r.parsedDate),
    };

    const PREFIX = '🔔 ';
    const DONE_PREFIX = '[DONE] ';
    const usePrefix = merged.status === 'done' ? DONE_PREFIX : PREFIX;
    // Reuse the shared formatter so the source line we write back
    // here is byte-identical to the live preview shown in
    // ReminderEditPopover.
    const newBellLine = formatBellLine(merged);
    // Recompute cleanText for the post-update text patch (keeps the
    // existing behavior of persisting a clean, non-drifting text
    // field even when the user's patch didn't include text).
    const rawText = merged.text || '';
    const tp = parseReminderLine(rawText);
    const cleanText = tp.isReminder && tp.kind === 'dated' && tp.dateIndex === 0
      ? (tp.restText || '')
      : rawText;

    // Resolve source list. Common case: it's on the active board and
    // in the live `lists` array. Cross-board fallback (Walter
    // 2026-05-06): when a reminder originated on a different board
    // than the one currently viewed, fetch the list directly via
    // readListAt and write back via writeListAt. Without the
    // fallback the source-line rewrite silently no-op'd, leaving
    // the bell line stale even though the reminder doc updated.
    let sourceList = lists.find(l => l.id === r.sourceListId);
    let sourceBoardForWrite = board?.id;
    let sourceUidForWrite = listsOwnerUid;
    if (!sourceList && r.sourceListId) {
      const targetBoardId = r.sourceBoardId
        || (listIdToBoardId && listIdToBoardId[r.sourceListId])
        || null;
      if (targetBoardId) {
        // Stay within the current user's own boards. Cross-USER
        // shared-list reminder editing is a separate case (the
        // owner uid would come from share metadata, and writing
        // there has its own permissions story); deferred.
        sourceList = await readListAt(listsOwnerUid, targetBoardId, r.sourceListId);
        sourceBoardForWrite = targetBoardId;
      }
    }
    let nextPatch = patch;
    if (sourceList && r.sourceLineHash) {
      // Walter 2026-05-11: tolerate optional ↻ recurrence glyph.
      // Walter 2026-05-13: done detection accepts "[DONE] " (current),
      // "🔕 " (round-1 fallback), and "~~🔔 " (legacy strikethrough).
      // The escaped "\[" / "\]" matches the literal brackets — square
      // brackets aren't special in this alternation context but the
      // escape makes the intent explicit.
      const DONE_BELL_RE = /^(?:\[DONE\]|🔕|~~🔔)↻?\s?/;
      const BELL_RE = /^🔔↻?\s?/;
      // Audit fix 1 (Walter 2026-09-04): operate on the SERVER's text, not the
      // copy this client happens to hold. `lists[…]` trails by at least the
      // save debounce and can trail much further if another window wrote in
      // between — and this rewrites the WHOLE document, so anything it hasn't
      // seen is destroyed. Same re-read organize has used since #191.
      const freshContent = await readFreshListContent(
        sourceList.id, sourceBoardForWrite, sourceList.content || '',
      );
      const linesArr = (freshContent || '').split('\n');
      let foundIdx = -1;
      for (let i = 0; i < linesArr.length; i++) {
        const ln = linesArr[i];
        let post = null;
        const doneM = ln.match(DONE_BELL_RE);
        const bellM = !doneM ? ln.match(BELL_RE) : null;
        if (doneM) post = ln.slice(doneM[0].length);
        else if (bellM) post = ln.slice(bellM[0].length);
        if (post != null && hashReminderLine(post) === r.sourceLineHash) {
          foundIdx = i;
          break;
        }
      }
      // Walter 2026-08-25: the similarity fallback that used to live here is
      // REVERTED. It found the most-similar bell line when the hash missed and
      // rewrote that whole line from the reminder doc — which silently
      // replaces whatever the user has typed since the doc was last written.
      // Walter, hours after it shipped: "two edits that I made, next time I
      // looked were gone — one a deletion of a couple of words, the other a
      // couple of words inserted." That is exactly this write. It was built to
      // fix a COSMETIC problem (a date chip disagreeing with its line), and a
      // chip out of step is worth far less than a sentence of his text.
      //
      // So: hash match only. A miss logs and changes the doc alone, which is
      // the behaviour that shipped for months before yesterday. We also log
      // what the fallback WOULD have rewritten, so the drift stays diagnosable
      // without anything being overwritten to learn it.
      if (foundIdx < 0) {
        let bestScore = 0;
        let candidate = -1;
        for (let i = 0; i < linesArr.length; i++) {
          const ln = linesArr[i];
          const doneM = ln.match(DONE_BELL_RE);
          const bellM = !doneM ? ln.match(BELL_RE) : null;
          if (!doneM && !bellM) continue;
          const post = ln.slice((doneM || bellM)[0].length);
          const lp = parseReminderLine(post);
          const lineText = (lp.isReminder && lp.kind === 'dated' && lp.dateIndex === 0)
            ? (lp.restText || '')
            : post;
          const score = reminderTextSimilarity(lineText, r.text || '');
          if (score > bestScore) { bestScore = score; candidate = i; }
        }
        if (candidate >= 0) {
          logSync('reminder-line-drift', {
            list: sourceList.id,
            reminder: id,
            score: Math.round(bestScore * 100) / 100,
          });
        }
      }
      if (foundIdx >= 0) {
        linesArr[foundIdx] = newBellLine;
        const newContent = linesArr.join('\n');
        if (sourceBoardForWrite === board?.id) {
          // Active board → the existing offline-aware saveList, tagged with
          // the text this edit was based on so a write that landed in between
          // bands instead of being silently overwritten.
          saveList(sourceList.id, { content: newContent, __baseContent: freshContent });
        } else {
          // Cross-board → direct setDoc.
          await writeListAt(sourceUidForWrite, sourceBoardForWrite, sourceList.id, { content: newContent });
        }
        nextPatch = {
          ...patch,
          sourceLineHash: hashReminderLine(newBellLine.slice(usePrefix.length)),
          // Persist the cleaned text so future writebacks start from a
          // clean state — even if the patch we received didn't include
          // text, the merged value (= existing doc) might have drifted.
          text: cleanText,
        };
      } else {
        // Couldn't find the line at all. The doc still updates (the user's
        // action must not be lost), but record it — a chip that disagrees
        // with its line now leaves a trace instead of being a mystery.
        logSync('reminder-line-miss', { list: sourceList.id, reminder: id });
      }
    }

    return updateReminder(id, nextPatch);
  }, [reminders, lists, board?.id, listsOwnerUid, listIdToBoardId, updateReminder, saveList, flushAllPending, readFreshListContent]);

  /**
   * Internal helper — locate the source line for a reminder. Returns
   * { sourceList, lineIndex, prefix } or null. Used by both
   * deactivate and fully-delete; keeps the prefix-detection +
   * hash-match logic in one place.
   */
  // `contentOverride` (audit fix 1, Walter 2026-09-04): locate against the
  // SERVER's current text rather than the in-memory copy. Callers that are
  // about to rewrite the whole document pass a fresh read; anything else can
  // omit it and get the old behaviour.
  const locateSourceLine = useCallback((r, contentOverride) => {
    if (!r || !r.sourceListId || !r.sourceLineHash) return null;
    const sl = lists.find(l => l.id === r.sourceListId);
    if (!sl) return null;
    const sourceText = typeof contentOverride === 'string' ? contentOverride : (sl.content || '');
    // Match either prefix form, with OR without a space after the
    // bell. Required-space was an arbitrary restriction — users
    // typing "🔔Wed call mom" without spacing now still match.
    // Walter 2026-05-11: tolerate optional ↻ recurrence glyph.
    const DONE_BELL_RE = /^(?:\[DONE\]|🔕|~~🔔)↻?\s?/;
    const BELL_RE = /^🔔↻?\s?/;
    const linesArr = sourceText.split('\n');
    for (let i = 0; i < linesArr.length; i++) {
      const ln = linesArr[i];
      let post = null;
      let prefix = null;
      const doneM = ln.match(DONE_BELL_RE);
      const bellM = !doneM ? ln.match(BELL_RE) : null;
      if (doneM) { post = ln.slice(doneM[0].length); prefix = doneM[0]; }
      else if (bellM) { post = ln.slice(bellM[0].length); prefix = bellM[0]; }
      if (post != null && hashReminderLine(post) === r.sourceLineHash) {
        return { sourceList: sl, linesArr, lineIndex: i, prefix };
      }
    }
    return null;
  }, [lists]);

  /**
   * deactivateReminder — agenda "deactivate" action: strip the bell
   * prefix from the source line (text stays put, line just becomes
   * normal text again) and delete the Firestore reminder doc. Push
   * undo toast that re-prefixes the line and recreates the doc.
   *
   * This is the default "I'm done with this reminder" action. The
   * full-delete sibling below removes the line itself.
   */
  const deactivateReminder = useCallback(async (id) => {
    // Flush any editor still holding keystrokes before we read `lists` —
    // otherwise this whole-document write discards them (audit fix 2).
    await flushAllPending();
    const r = (reminders || []).find(x => x.id === id);
    if (!r) return deleteReminder(id);
    // Audit fix 1: locate and rewrite against the SERVER's text — this path
    // writes the whole document back, so a stale copy destroys anything it
    // hasn't seen.
    const freshContent = await readFreshListContent(r?.sourceListId, board?.id, null);
    const located = locateSourceLine(r, freshContent);
    let strippedSnapshot = null;
    if (located) {
      const { sourceList, linesArr, lineIndex, prefix } = located;
      const before = linesArr.join('\n');
      linesArr[lineIndex] = linesArr[lineIndex].slice(prefix.length);
      const after = linesArr.join('\n');
      strippedSnapshot = { listId: sourceList.id, before };
      saveList(sourceList.id, { content: after, __baseContent: before });
    }
    const docSnapshot = {
      sourceListId: r.sourceListId,
      sourceLineHash: r.sourceLineHash,
      text: r.text,
      parsedDate: r.parsedDate?.toDate?.() || r.parsedDate || null,
      recurrence: r.recurrence || null,
      status: r.status || 'open',
      // Bug #12 — preserve the doc's ACL on recreate so the restored
      // reminder is still visible to collaborators. If the original
      // doc never had aclUids (legacy / pre-#12 doc), recompute from
      // the current source list state.
      aclUids: Array.isArray(r.aclUids) && r.aclUids.length > 0
        ? r.aclUids
        : computeAclForCreate(located?.sourceList, user?.uid),
    };
    try { await deleteReminder(id); } catch (e) { console.warn('[reminder deactivate] failed', e); }
    if (pushToast) {
      pushToast(
        'reminder deactivated',
        async () => {
          if (strippedSnapshot) {
            saveList(strippedSnapshot.listId, { content: strippedSnapshot.before });
          }
          try {
            if (createReminder) await createReminder(docSnapshot);
          } catch (e) { console.warn('[reminder undo-deactivate] recreate failed', e); }
        },
      );
    }
  }, [reminders, locateSourceLine, deleteReminder, createReminder, saveList, pushToast, user?.uid, flushAllPending, readFreshListContent, board?.id]);

  /**
   * fullyDeleteReminder — agenda "delete" action: remove the entire
   * 🔔 line from the source list AND delete the Firestore doc. More
   * destructive than deactivate; labelled distinctly in the UI so the
   * user has to opt in.
   *
   * Continuation lines that follow the 🔔 line stay (we don't try to
   * guess whether they were specifically "for" this reminder vs the
   * broader paragraph context). User can clean those up by hand.
   */
  const fullyDeleteReminder = useCallback(async (id) => {
    // Flush any editor still holding keystrokes before we read `lists` —
    // otherwise this whole-document write discards them (audit fix 2).
    await flushAllPending();
    const r = (reminders || []).find(x => x.id === id);
    if (!r) return deleteReminder(id);
    // Audit fix 1: locate and rewrite against the SERVER's text — this path
    // writes the whole document back, so a stale copy destroys anything it
    // hasn't seen.
    const freshContent = await readFreshListContent(r?.sourceListId, board?.id, null);
    const located = locateSourceLine(r, freshContent);
    let removedSnapshot = null;
    if (located) {
      const { sourceList, linesArr, lineIndex } = located;
      const before = linesArr.join('\n');
      linesArr.splice(lineIndex, 1);
      const after = linesArr.join('\n');
      removedSnapshot = { listId: sourceList.id, before };
      saveList(sourceList.id, { content: after, __baseContent: before });
    }
    const docSnapshot = {
      sourceListId: r.sourceListId,
      sourceLineHash: r.sourceLineHash,
      text: r.text,
      parsedDate: r.parsedDate?.toDate?.() || r.parsedDate || null,
      recurrence: r.recurrence || null,
      status: r.status || 'open',
      // Bug #12 — see deactivateReminder for rationale.
      aclUids: Array.isArray(r.aclUids) && r.aclUids.length > 0
        ? r.aclUids
        : computeAclForCreate(located?.sourceList, user?.uid),
    };
    try { await deleteReminder(id); } catch (e) { console.warn('[reminder full-delete] failed', e); }
    if (pushToast) {
      pushToast(
        'reminder + line deleted',
        async () => {
          if (removedSnapshot) {
            saveList(removedSnapshot.listId, { content: removedSnapshot.before });
          }
          try {
            if (createReminder) await createReminder(docSnapshot);
          } catch (e) { console.warn('[reminder undo-fully-delete] recreate failed', e); }
        },
      );
    }
  }, [reminders, locateSourceLine, deleteReminder, createReminder, saveList, pushToast, user?.uid, flushAllPending, readFreshListContent, board?.id]);

  /**
   * updateReminderNotes — replace the continuation-line block that
   * follows a reminder's 🔔 line with the user's new notes text.
   *
   * `newNotesText` is the raw textarea contents from the agenda. We
   * split into lines, drop blanks, and re-prefix each as "- " so the
   * source list reads naturally and the continuation rule still
   * picks the lines back up next time we render. Existing block (the
   * run of continuation lines after the bell line) is replaced
   * wholesale; the bell line itself isn't touched.
   */
  const updateReminderNotes = useCallback(async (r, newNotesText) => {
    // Flush any editor still holding keystrokes before we read `lists` —
    // otherwise this whole-document write discards them (audit fix 2).
    await flushAllPending();
    if (!r || !r.sourceListId || !r.sourceLineHash) return;
    // Cross-board fallback (Walter 2026-05-06 — same fix as
    // updateReminderAndSource above): if the reminder's source list
    // isn't in the active board's lists, fetch it directly from the
    // user's other boards and write back there. Previously this
    // returned silently and the user's notes edit appeared to do
    // nothing.
    let sl = lists.find(l => l.id === r.sourceListId);
    let writeBoardId = board?.id;
    const writeOwnerUid = listsOwnerUid;
    if (!sl) {
      const targetBoardId = r.sourceBoardId
        || (listIdToBoardId && listIdToBoardId[r.sourceListId])
        || null;
      if (!targetBoardId) return;
      sl = await readListAt(listsOwnerUid, targetBoardId, r.sourceListId);
      if (!sl) return;
      writeBoardId = targetBoardId;
    }
    // Walter 2026-05-11: tolerate optional ↻ recurrence glyph.
    const DONE_BELL_RE = /^(?:\[DONE\]|🔕|~~🔔)↻?\s?/;
    const BELL_RE = /^🔔↻?\s?/;
    // Audit fix 1: the server's text, not this client's copy — the write
    // below replaces the whole document.
    const freshContent = await readFreshListContent(sl.id, writeBoardId, sl.content || '');
    const linesArr = (freshContent || '').split('\n');
    let bellIdx = -1;
    for (let i = 0; i < linesArr.length; i++) {
      const ln = linesArr[i];
      const doneM = ln.match(DONE_BELL_RE);
      const bellM = !doneM ? ln.match(BELL_RE) : null;
      if (!doneM && !bellM) continue;
      const stripped = doneM ? ln.slice(doneM[0].length) : ln.slice(bellM[0].length);
      if (hashReminderLine(stripped) === r.sourceLineHash) {
        bellIdx = i;
        break;
      }
    }
    if (bellIdx < 0) return;
    // Walter 2026-08-25: the note block is everything under the bell line up
    // to the next blank line or the next reminder — the same paragraph rule
    // the agenda and organize now use.
    let endIdx = bellIdx + 1;
    while (endIdx < linesArr.length
      && linesArr[endIdx].trim() !== ''
      && !DONE_BELL_RE.test(linesArr[endIdx])
      && !BELL_RE.test(linesArr[endIdx])) {
      endIdx++;
    }
    // Write the notes back AS TYPED — plain lines. The old code forced a "- "
    // onto every one because that marker was what made a line a note; the
    // paragraph rule replaced it, so adding punctuation the user didn't type
    // is now just noise (Walter: "write plain lines unless user explicitly
    // puts in those markers"). A marker they DID type is left alone.
    const newNoteLines = (newNotesText || '')
      .split('\n')
      .map(l => l.trim())
      .filter(l => l.length > 0);
    linesArr.splice(bellIdx + 1, endIdx - (bellIdx + 1), ...newNoteLines);
    const newContent = linesArr.join('\n');
    if (writeBoardId === board?.id) {
      saveList(sl.id, { content: newContent, __baseContent: freshContent });
    } else {
      await writeListAt(writeOwnerUid, writeBoardId, sl.id, { content: newContent });
    }
  }, [lists, board?.id, listsOwnerUid, listIdToBoardId, saveList, flushAllPending, readFreshListContent]);

  const addList = useCallback(async () => {
    if (!board) return null;
    // Walter 2026-05-11: guest editors can now create lists on shared
    // boards. Block guest viewers at the handler layer in case the
    // gating UI is ever bypassed (e.g. keyboard shortcut). The
    // Firestore rules also enforce this — the early return is a
    // friendlier failure than a permission-denied toast.
    if (isGuestOnBoard && !isGuestEditor) {
      pushToast('only the board owner or editors can add lists', null, { ttlMs: 4500 });
      return null;
    }
    // Walter 2026-05-31: free-tier list cap (soft, client-side). Only
    // enforced on the user's OWN boards — a guest editor's lists count
    // against the OWNER's quota, not theirs, and counting the owner's
    // cross-board total from a guest context isn't feasible client-
    // side. countAllLists fails OPEN (returns null) on a read error,
    // so a hiccup never blocks a legit create.
    if (!isGuestOnBoard) {
      const totalLists = await countAllLists(user?.uid, boards);
      if (typeof totalLists === 'number' && totalLists >= FREE_TIER.maxListsTotal) {
        pushToast(LIMIT_COPY.lists, null, { ttlMs: 8000 });
        return null;
      }
    }
    // Walter 2026-05-11: rapid 'n' presses (or rapid clicks of "+ new
    // list") created multiple lists that all sat on top of each other
    // and all got named "list 1" — same race in two places. Both root
    // causes were that `lists` / `canvasLists` are stale until the
    // Firestore snapshot arrives, but the user can fire many addList()
    // calls before then. Track an in-flight delta in a ref so we
    // assign distinct positions + names even during the snapshot lag.
    // The delta is cleaned up once the newly-created list IDs appear
    // in the lists prop (see the useEffect below this useCallback).
    const inFlightCount = inFlightCreatesRef.current.size;
    const offset = ((canvasLists.length + inFlightCount) % 10) * 28;
    nextZRef.current += 1;
    const maxOrder = Math.max(
      lists.reduce((m, l) => (l.order != null && l.order > m ? l.order : m), 0),
      inFlightCreatesRef.current.size
        ? Math.max(...Array.from(inFlightCreatesRef.current.values()).map(p => p.order))
        : 0,
    );
    // "list 1" / "list 2" / … picked from the lowest unused number
    // across both committed lists and currently-in-flight creates.
    const taken = new Set(
      lists
        .map(l => (l.title || '').trim().toLowerCase())
        .filter(t => /^list \d+$/.test(t))
        .map(t => parseInt(t.slice(5), 10))
        .filter(n => !Number.isNaN(n))
    );
    // Add in-flight reservations to the taken set so concurrent
    // addList calls don't collide on the same number.
    for (const p of inFlightCreatesRef.current.values()) taken.add(p.titleNum);
    let n = 1;
    while (taken.has(n)) n += 1;
    const defaultTitle = `list ${n}`;
    // x/y/w/h are still seeded even in grid mode so a later grid→free flip
    // has sensible fallback positions.
    // Walter 2026-05-13: leave `order` unset on new lists. The sidebar
    // sortByOrder puts null-order lists at the END (default "unplaced"
    // group) with createdAt as a tiebreaker, so a newly-created list
    // lands at the bottom of the sidebar even when other null-order
    // lists exist above. The earlier `maxOrder + 1` formula failed for
    // users whose existing lists had null orders: maxOrder was 0–2
    // even with many lists, so the new list sorted MIDWAY through the
    // sidebar instead of at the bottom. Drag-reorder writes explicit
    // orders later if the user wants a custom position.
    const payload = {
      title: defaultTitle,
      content: '',
      x: 80 + offset,
      y: 60 + offset,
      w: 280,
      h: 320,
      z: nextZRef.current,
      state: 'open',
    };
    // Walter 2026-05-11: new lists on a SHARED board (whether created
    // by the owner or a guest editor) explicitly carry the board's
    // editors/viewers arrays on the list doc. Defense in depth: even
    // if the board ACL changes later, the list keeps its own copy of
    // who had access at creation time — so a "remove from board"
    // doesn't accidentally lock the user out of the per-list view
    // either. Solo boards (no editors/viewers set) still create lists
    // with empty arrays; no inheritance means no surface for ghost
    // ACL state.
    const boardHasShares =
      (Array.isArray(board?.editors) && board.editors.length > 0) ||
      (Array.isArray(board?.viewers) && board.viewers.length > 0);
    if (boardHasShares) {
      payload.editors = Array.isArray(board?.editors) ? [...board.editors] : [];
      payload.viewers = Array.isArray(board?.viewers) ? [...board.viewers] : [];
    }
    // Reserve a temporary in-flight slot under a synthetic key so
    // concurrent addList calls see this one's reservation even before
    // the Firestore newId comes back. We swap the synthetic key for
    // the real newId after saveList resolves so the cleanup useEffect
    // (which keys off lists' actual IDs) can find it.
    //
    // Walter 2026-05-22 (F1 fix): this reservation MUST be set before
    // ANY `await` below. It used to sit after the grid-mode
    // `await Promise.all(...)` row-resequence — so two rapid `n`
    // presses on a GRID board each computed `n`, then parked at the
    // grid await with no reservation made yet; neither saw the other,
    // and both new lists got the same "list N" name. Reserving here —
    // synchronously, right after `n` is computed — closes that window:
    // a concurrent addList now always sees prior reservations.
    const reservationKey = `pending:${nextZRef.current}:${Math.random()}`;
    inFlightCreatesRef.current.set(reservationKey, { titleNum: n, order: payload.order });
    if (boardMode === 'grid') {
      // New tile lands at top of column 0; every open tile currently in
      // column 0 shifts down one row. Sort first so gridRow assignment is
      // stable across ties (matches GridCanvas's byGridRow tie-break).
      const col0Tiles = lists
        .filter(l => (!l.state || l.state === 'open') && (l.gridCol ?? 0) === 0)
        .sort((a, b) => {
          const ar = a.gridRow == null ? Number.POSITIVE_INFINITY : a.gridRow;
          const br = b.gridRow == null ? Number.POSITIVE_INFINITY : b.gridRow;
          if (ar !== br) return ar - br;
          return (a.id || '').localeCompare(b.id || '');
        });
      // Resequence to rows 1..N so the new tile's row 0 slides cleanly in.
      await Promise.all(
        col0Tiles.map((t, idx) => saveList(t.id, { gridRow: idx + 1 })),
      );
      payload.gridCol = 0;
      payload.gridRow = 0;
    }
    const newId = await saveList(null, payload);
    if (newId) {
      // Re-key from synthetic to real ID so the cleanup effect can
      // drop the reservation once the snapshot delivers this list.
      inFlightCreatesRef.current.delete(reservationKey);
      inFlightCreatesRef.current.set(newId, { titleNum: n, order: payload.order });
    } else {
      // saveList failed; release the reservation immediately.
      inFlightCreatesRef.current.delete(reservationKey);
    }

    // Guest mode reminder: once per anonymous session, after their first list
    // is saved, surface a quiet "saved · this device only" toast pointing
    // them at sign-in. Localstorage flag scoped per-uid so different guest
    // sessions on the same browser each see it once.
    if (newId && user?.isAnonymous) {
      const seenKey = `lists_guest_save_toast_${user.uid}`;
      try {
        if (!localStorage.getItem(seenKey)) {
          localStorage.setItem(seenKey, '1');
          pushToast('saved · this device only — sign in to sync', null, { ttlMs: 6000 });
        }
      } catch {}
    }
    // Walter 2026-05-06 round 8: first-tile drag tip — fire once per
    // device, but only on free-canvas mode (grid mode auto-arranges,
    // so a "drag to reposition" hint is misleading). Bumped the
    // storage key from the round-7 version so users who saw the tip
    // before the gating fix get a clean shot at the new behaviour.
    try {
      const dragTipKey = 'lists_tip_first_tile_drag_seen_v2';
      const isFreeMode = boardMode !== 'grid';
      if (isFreeMode && !localStorage.getItem(dragTipKey)) {
        localStorage.setItem(dragTipKey, '1');
        pushToast('tip: drag the title bar to reposition this tile', null, { ttlMs: 6000 });
      }
    } catch {}
    // Walter 2026-05-11: shared-board hint — when the new list is on a
    // board that has other members, surface a quiet reminder that the
    // owner + co-editors will see this list too. Once per session per
    // board so creating multiple lists in a row doesn't spam toasts;
    // sessionStorage so a fresh tab gets a fresh reminder.
    if (newId && boardHasShares) {
      try {
        const sessionKey = `lists_shared_create_hint_${board.id}`;
        if (!sessionStorage.getItem(sessionKey)) {
          sessionStorage.setItem(sessionKey, '1');
          const editorCount = Array.isArray(board?.editors) ? board.editors.length : 0;
          const viewerCount = Array.isArray(board?.viewers) ? board.viewers.length : 0;
          // Build a short audience summary. Excludes the current user
          // from the count for the guest-editor case (they're in the
          // editors array but shouldn't count themselves as "an editor
          // I'm sharing with"). The owner is implicitly the +1.
          const myUid = user?.uid;
          const otherEditors = Array.isArray(board?.editors)
            ? board.editors.filter(uid => uid !== myUid).length
            : editorCount;
          const audienceParts = [];
          audienceParts.push(isGuestOnBoard ? 'the owner' : null);
          if ((isGuestOnBoard ? otherEditors : editorCount) > 0) {
            const n = isGuestOnBoard ? otherEditors : editorCount;
            audienceParts.push(`${n} editor${n === 1 ? '' : 's'}`);
          }
          if (viewerCount > 0) {
            audienceParts.push(`${viewerCount} viewer${viewerCount === 1 ? '' : 's'}`);
          }
          const audience = audienceParts.filter(Boolean).join(' + ');
          const boardLabel = (board?.name || 'this board').trim() || 'this board';
          pushToast(
            `new list on shared board "${boardLabel}"${audience ? ` — visible to ${audience}` : ''}`,
            null,
            { ttlMs: 7000 },
          );
        }
      } catch {}
    }
    return newId ? { id: newId } : null;
  }, [board, boardMode, canvasLists.length, lists, saveList, user, pushToast, isGuestOnBoard, isGuestEditor]);

  // Walter 2026-05-19: mirror the LATEST addList into addListRef so
  // the keydown handler (bound once per board with [board]-only deps,
  // declared above) always dispatches to the current closure. Must
  // live AFTER the addList useCallback or the dep `addList` is in
  // its temporal dead zone and the render crashes (which is exactly
  // what happened on the first attempt at this fix — blank page).
  useEffect(() => { addListRef.current = addList; }, [addList]);

  // Walter 2026-05-31: free-tier board cap (soft). `boards` is the
  // user's OWN boards (useBoards(user.uid)) — shared boards arrive via
  // sharedWithMe and don't count against the create-quota, which is
  // the intended behavior. At the cap we surface the Pro-tease toast
  // instead of opening the new-board modal. Pass this wrapper wherever
  // onNewBoard was passed (Sidebar + MobileBoard).
  const handleNewBoardGuarded = useCallback(() => {
    if ((boards?.length || 0) >= FREE_TIER.maxBoards) {
      pushToast(LIMIT_COPY.boards, null, { ttlMs: 8000 });
      return;
    }
    onNewBoard?.();
  }, [boards, onNewBoard, pushToast]);

  // Convenience wrapper for the "+ new reminder list" menu item: creates a
  // fresh list, pre-titles it "Reminders" with a short starter tip, and
  // adds its ID to the user's designated-reminder-list array. After this
  // runs, every paragraph the user types in the new list is auto-parsed
  // as a reminder. Reuses addList so column/row placement logic stays in
  // one spot.
  const addReminderList = useCallback(async () => {
    const created = await addList();
    if (!created?.id) return null;
    await saveList(created.id, {
      title: 'Reminders',
      // Walter 2026-06-16: seed EMPTY. The how-it-works examples now live as
      // a textarea PLACEHOLDER (see getListEmptyHint / the editors), not as
      // content — so they can never be auto-captured as reminders once the
      // user starts typing. Replaces the old seeded-instructions +
      // seededFromTemplate approach, which captured the leftover instruction
      // lines as undated reminders after the first edit.
      content: '',
    });
    const existing = Array.isArray(userPrefs?.designatedReminderListIds)
      ? userPrefs.designatedReminderListIds
      : [];
    await updateUserPrefs({
      designatedReminderListIds: Array.from(new Set([...existing, created.id])),
      // Drop legacy singular fields once we move to the plural model.
      designatedReminderListId: null,
      designatedReminderBoardId: null,
    });
    return created;
  }, [addList, saveList, userPrefs, updateUserPrefs]);

  // Walter 2026-05-11: parallel to addReminderList. Creates a fresh
  // list, pre-titles it "Log", seeds a short //Note explainer, and
  // registers it in userPrefs.designatedLogListIds so every new line
  // auto-stamps with @today. Same "save the user a 3-step dance"
  // motivation as the reminder shortcut. Lives next to addReminderList
  // so the two parallel patterns stay legible.
  const addLogList = useCallback(async () => {
    const created = await addList();
    if (!created?.id) return null;
    await saveList(created.id, {
      title: 'Log',
      // Walter 2026-06-16: seed EMPTY — examples live as the empty-state
      // placeholder now (see getListEmptyHint), not as content, so the
      // instruction lines can't be auto-@date-stamped after the first edit.
      content: '',
    });
    const existing = Array.isArray(userPrefs?.designatedLogListIds)
      ? userPrefs.designatedLogListIds
      : [];
    await updateUserPrefs({
      designatedLogListIds: Array.from(new Set([...existing, created.id])),
    });
    return created;
  }, [addList, saveList, userPrefs, updateUserPrefs]);

  // Open the Share modal for a specific list (Phase 2). The modal is
  // generic — it accepts either a board target or a list target. We
  // hand it list-scoped state when invoked from a list ⋯ menu.
  const openShareForList = useCallback((list) => {
    if (!list) return;
    // Walter 2026-05-25 (Bug #14 — round-2 tester report): block
    // share for anonymous (guest) sessions. Guest data lives only on
    // the local device, so any "shared" link would be a dead end the
    // moment the inviter clears their browser. The toast nudges them
    // to upgrade via Google or magic-link from the account popover.
    if (user?.isAnonymous) {
      pushToastRef.current?.(
        'sign in to share — guest data lives only on this device',
        null,
        { ttlMs: 5000 },
      );
      return;
    }
    setShareTarget({
      kind: 'list',
      ownerUid: listsOwnerUid,
      boardId: board?.id,
      listId: list.id,
      title: list.title || '(untitled list)',
      editors: list.editors || [],
      viewers: list.viewers || [],
      shareToken: list.shareToken || null,
      shareTokenRole: list.shareTokenRole || 'viewer',
      // Walter 2026-05-25 — needed by the reminder-share gate in
      // ShareModal so it can count 🔔 lines BEFORE the doc snapshot
      // subscription has fired. Without this seed, a fast invite
      // click loses the race against the first onSnapshot and the
      // gate sees liveTarget.content === undefined → skips warning.
      content: list.content || '',
    });
    setShowShareModal(true);
  }, [user, board]);

  // Walter 2026-05-11: open the "place in board…" picker for a shared
  // list. ListPanel + FocusedPanel call this from their ⋯-menu when
  // the list isn't owned by the current user. We need to figure out
  // where the source list actually lives — for a list currently
  // viewed on a SHARED board, it's the board's owner uid + board id.
  // For an already-placed shared list, we read the source from the
  // list's `_sharedFromOwnerUid` / `_sharedFromBoardId` annotations.
  const openPlaceListPicker = useCallback((list) => {
    if (!list) return;
    const sourceOwnerUid = list._sharedFromOwnerUid
      || (isGuestOnBoard ? boardOwnerUid : null);
    const sourceBoardId = list._sharedFromBoardId
      || (isGuestOnBoard ? board?.id : null);
    if (!sourceOwnerUid || !sourceBoardId) {
      // Defensive: nothing to do for an own-board list (the menu
      // item gates on isSharedWithMe so we shouldn't get here).
      console.warn('[openPlaceListPicker] missing source info for', list.id);
      return;
    }
    // Best-effort owner name: prefer what's already annotated on the
    // list (when it came in via usePlacedSharedLists), otherwise look
    // up the sharedWithMe pointer doc for this list/board.
    const fromList = list._sharedFromOwnerName;
    const fromSharedWithMe = (() => {
      if (!Array.isArray(sharedWithMe)) return '';
      // For placed shared lists (list-kind shares), the pointer is
      // keyed by listId.
      const direct = sharedWithMe.find(s => s.refId === list.id && s.kind === 'list');
      if (direct?.cachedOwnerName) return direct.cachedOwnerName;
      // For board-kind shares (current user is a guest viewing
      // someone else's board), the pointer is keyed by boardId.
      const board = sharedWithMe.find(s => s.refId === sourceBoardId && s.kind === 'board');
      return board?.cachedOwnerName || '';
    })();
    // Best-effort cached title: prefer the list's live title (when
    // non-empty), otherwise pull from any already-set placement
    // record or the matching sharedWithMe pointer. Empty string is
    // an acceptable end state — the rendering layer falls back to
    // "(empty)" if nothing's available.
    const cachedTitleFromList = (list.title || '').trim();
    const cachedTitleFromPlacement = (list._sharedFromCachedTitle || '').trim();
    const cachedTitleFromSharedWithMe = (() => {
      if (!Array.isArray(sharedWithMe)) return '';
      const direct = sharedWithMe.find(s => s.refId === list.id && s.kind === 'list');
      return (direct?.cachedTitle || '').trim();
    })();
    const cachedTitle = cachedTitleFromList
      || cachedTitleFromPlacement
      || cachedTitleFromSharedWithMe;
    setPlaceListTarget({
      listId: list.id,
      title: list.title || '(untitled list)',
      sourceOwnerUid,
      sourceBoardId,
      sourceOwnerName: fromList || fromSharedWithMe || '',
      sourceCachedTitle: cachedTitle,
    });
  }, [isGuestOnBoard, boardOwnerUid, board?.id, sharedWithMe]);

  // Picker submit. Identical shape to AcceptInvitePage's handler —
  // either pick an existing board (boardId) or create a new blank
  // board (createWithName) and use its id. On success, write the
  // placement doc and navigate to the chosen board so the user
  // sees the result. For "place to current own board" cases we
  // skip the navigation (we're already there).
  const handlePlaceListPick = useCallback(async (pick) => {
    if (!placeListTarget) return;
    const { listId, sourceOwnerUid, sourceBoardId } = placeListTarget;
    let targetBoardId = pick.boardId;
    let targetLists = [];
    if (pick.createWithName) {
      if (!onCreateBlankBoard) {
        throw new Error('cannot create new boards from here');
      }
      targetBoardId = await onCreateBlankBoard(pick.createWithName);
      // New board → empty list of existing tiles. Default position
      // (80, 60) is fine; no overlap risk.
    } else if (targetBoardId === board?.id) {
      // Placing onto the current board — reuse the live lists array
      // we already have to compute a non-overlapping position.
      targetLists = Array.isArray(lists) ? lists : [];
    } else if (targetBoardId) {
      // Placing onto a DIFFERENT existing board. Fetch its lists once
      // so we can compute a safe starting position relative to what's
      // already there. Otherwise the placed list would inherit the
      // owner's x/y (potentially overlapping other tiles on the
      // invitee's chosen board — Walter saw a hidden "(empty)" list
      // surfacing as soon as he dragged the shared list to reveal it).
      try {
        targetLists = await fetchBoardLists(user.uid, targetBoardId);
      } catch (err) {
        console.warn('[placeList] fetchBoardLists failed; using default position', err);
      }
    }
    if (!targetBoardId) throw new Error('could not pick a board');
    // Walter 2026-05-26: position the placed tile at top-left and
    // sort it FIRST on the destination board. Earlier this path
    // cascaded (x: 80 + offset, y: 60 + offset, order: maxOrder + 1)
    // which slotted the tile somewhere mid-canvas + last-in-order —
    // technically safe (no exact overlap) but didn't match user
    // intent ("I just placed it, it should be the first thing I
    // see"). Now (80, 60) for x/y and (minOrder − 1) for order so
    // the tile sits at top-left and sorts before everything else.
    // z still bumps above maxZ + 1 so the tile sits on top in any
    // overlap until the user drags it. Unified with ListPage's
    // place-on-board handler shipped same day.
    const maxZ = targetLists.reduce((m, l) => Math.max(m, l.z || 0), 0);
    const minOrder = targetLists.reduce(
      (m, l) => (l.order != null && l.order < m ? l.order : m),
      Infinity,
    );
    const firstOrder = minOrder === Infinity ? 1 : minOrder - 1;
    const placementPosition = {
      x: 80,
      y: 60,
      w: 280,
      h: 320,
      z: maxZ + 1,
      order: firstOrder,
    };
    await setSharedListPlacement(listId, {
      sourceOwnerUid,
      sourceBoardId,
      boardId: targetBoardId,
      sourceOwnerName: placeListTarget.sourceOwnerName || '',
      sourceCachedTitle: placeListTarget.sourceCachedTitle || '',
      ...placementPosition,
    });
    setPlaceListTarget(null);
    // Navigate to the target board so the user lands on the place
    // they just placed the list. Same full-reload reasoning as the
    // accept-flow: cleanest way to re-resolve all subscriptions
    // including the placements collection on the new board's mount.
    if (targetBoardId !== board?.id) {
      window.location.href = `/board/${targetBoardId}`;
    } else {
      // Already on the target board (e.g. user picked the current
      // own board for a shared list they'd been viewing in the
      // shared-with-me page). Just close — the placements live
      // subscription picks up the new doc and the merge layer
      // renders it.
    }
  }, [placeListTarget, setSharedListPlacement, onCreateBlankBoard, board?.id, lists, user?.uid]);

  // Open the Share modal for an arbitrary OWN board (used by the
  // ShareBadge in the dropdown — clicking it on any board entry, not
  // just the current one). The modal handles the rest based on the
  // target's editors/viewers state. ownerUid is always user.uid here
  // since the dropdown only shows boards from useBoards (own boards).
  const openShareForAnyBoard = useCallback((b) => {
    if (!b) return;
    // Bug #14 guest-share gate — see openShareForList above.
    if (user?.isAnonymous) {
      pushToastRef.current?.(
        'sign in to share — guest data lives only on this device',
        null,
        { ttlMs: 5000 },
      );
      return;
    }
    setShareTarget({
      kind: 'board',
      ownerUid: user?.uid,
      boardId: b.id,
      title: b.name,
      editors: b.editors || [],
      viewers: b.viewers || [],
      shareToken: b.shareToken || null,
      shareTokenRole: b.shareTokenRole || 'viewer',
    });
    setShowShareModal(true);
  }, [user]);

  // Toggle whether a list is in the user's designated-reminder-list set.
  // Called from list-level ⋯ menus (sidebar Row, ListPanel header,
  // FocusedPanel header). The settings-level picker was retired in
  // favour of these — designation is a per-list affordance now, with
  // the bell indicators in the sidebar serving as the read-only view
  // of the current set.
  const toggleReminderList = useCallback((listId) => {
    if (!listId) return;
    const ids = Array.isArray(userPrefs?.designatedReminderListIds)
      ? userPrefs.designatedReminderListIds
      : [];
    const next = ids.includes(listId)
      ? ids.filter(id => id !== listId)
      : Array.from(new Set([...ids, listId]));
    updateUserPrefs?.({
      designatedReminderListIds: next,
      designatedReminderListId: null,
      designatedReminderBoardId: null,
    });
  }, [userPrefs, updateUserPrefs]);

  // Route saves for placed shared lists. Content / title fields go
  // to the OWNER's subtree (writeListAt at users/{sourceOwnerUid}/
  // boards/{sourceBoardId}/lists/{listId}) so edits sync to everyone
  // who has access. Positional / canvas-state fields (x/y/w/h/z/
  // gridCol/gridRow/order/state) go to the per-user placement doc
  // instead — they're invitee-side organisation and shouldn't move
  // the list around for the owner or other invitees.
  //
  // Own-board lists (no _sharedFromOwnerUid annotation) keep using
  // saveList for everything as before.
  //
  // Walter 2026-05-11: split the write so dragging a placed shared
  // list doesn't churn the owner's canvas, AND so a placed list can
  // sit at its own position without two invitees fighting over x/y
  // on a single doc. The patch is partitioned per field; both halves
  // fire independently when present.
  const POSITIONAL_KEYS = [
    'x', 'y', 'w', 'h', 'z',
    'gridCol', 'gridRow', 'order',
    // state (open/closed/archived) is owner-controlled for now — if
    // the owner archives the list, every invitee loses access from
    // their canvas, which matches the "owner deletes/archives →
    // invitees see it disappear" semantic. Per-user archive is a
    // future enhancement.
  ];
  const handleUpdate = useCallback((listId, rawPatch) => {
    // Walter 2026-06-15: the editor threads the 3-way conflict base via a
    // reserved `__baseContent` key. Pull it off BEFORE any routing so it
    // can never leak into the placed-shared-list writes below (which are
    // plain writeListAt calls that would persist it). Re-attached only on
    // the saveList path, which strips it again before the Firestore write.
    let patch = rawPatch;
    let baseContent;
    if (rawPatch && Object.prototype.hasOwnProperty.call(rawPatch, '__baseContent')) {
      baseContent = rawPatch.__baseContent;
      const { __baseContent, ...rest } = rawPatch;
      void __baseContent;
      patch = rest;
    }
    // Walter 2026-05-11: route writes for placed shared lists with
    // TWO independent lookups. The primary path (placedSharedLists)
    // is the merged list array — it has the source-owner annotations
    // we need. The secondary path (sharedListPlacements map) covers
    // a startup race where the placement doc has loaded but the
    // source-list listener hasn't resolved yet — without this
    // fallback, handleUpdate would fall through to saveList() and
    // create a phantom empty list doc on the invitee's own subtree
    // at the placed list's id. The cosmetic merge-layer filter
    // hides such phantoms, but this is the upstream fix.
    const sharedTarget = placedSharedLists.find(l => l.id === listId);
    if (sharedTarget && sharedTarget._sharedFromOwnerUid && sharedTarget._sharedFromBoardId) {
      const positional = {};
      const remote = {};
      for (const [k, v] of Object.entries(patch || {})) {
        if (POSITIONAL_KEYS.includes(k)) positional[k] = v;
        else remote[k] = v;
      }
      const writes = [];
      if (Object.keys(remote).length > 0) {
        writes.push(writeListAt(
          sharedTarget._sharedFromOwnerUid,
          sharedTarget._sharedFromBoardId,
          listId,
          remote,
        ));
      }
      if (Object.keys(positional).length > 0 && setSharedListPlacement) {
        writes.push(setSharedListPlacement(listId, {
          sourceOwnerUid: sharedTarget._sharedFromOwnerUid,
          sourceBoardId: sharedTarget._sharedFromBoardId,
          boardId: sharedTarget._placementTargetBoardId,
          ...positional,
        }));
      }
      return Promise.all(writes);
    }
    // Fallback for the pre-resolved-listener race: check the
    // placement map directly. If a placement exists for this listId,
    // route the write through it instead of letting saveList create
    // a phantom doc on the invitee's own subtree.
    const placement = sharedListPlacements?.[listId];
    if (placement && placement.sourceOwnerUid && placement.sourceBoardId) {
      const positional = {};
      const remote = {};
      for (const [k, v] of Object.entries(patch || {})) {
        if (POSITIONAL_KEYS.includes(k)) positional[k] = v;
        else remote[k] = v;
      }
      const writes = [];
      if (Object.keys(remote).length > 0) {
        writes.push(writeListAt(
          placement.sourceOwnerUid,
          placement.sourceBoardId,
          listId,
          remote,
        ));
      }
      if (Object.keys(positional).length > 0 && setSharedListPlacement) {
        writes.push(setSharedListPlacement(listId, {
          sourceOwnerUid: placement.sourceOwnerUid,
          sourceBoardId: placement.sourceBoardId,
          boardId: placement.boardId,
          ...positional,
        }));
      }
      return Promise.all(writes);
    }
    return saveList(listId, baseContent !== undefined ? { ...patch, __baseContent: baseContent } : patch, {
      // Walter 2026-05-16: conflict-detection toast on prose writes. The
      // helper only fires this when another device wrote between our
      // baseline and now; the displaced content is in the list's
      // /history subcollection and reachable via the HistoryPanel.
      onConflict: () => {
        pushToastRef.current?.(
          'this list was edited in two places at once — your version saved, previous version backed up.',
          null,
          { ttlMs: 6000 },
        );
      },
    });
  }, [saveList, placedSharedLists, setSharedListPlacement, sharedListPlacements]);

  // Auto-arrange open canvas notes into columns that follow their existing
  // X-clustering, sized to exactly fill the viewport with no gaps. Heights
  // vary per column: each column divides the viewport vertical space evenly
  // among its notes (so a column with 4 notes gets shorter tiles than a
  // column with 2).
  const handleAutoArrange = useCallback((forcedCols) => {
    const canvasEl = canvasRef.current;
    if (!canvasEl) return;
    const viewportW = canvasEl.clientWidth;
    const viewportH = canvasEl.clientHeight;
    const openNotes = lists.filter(l => !l.state || l.state === 'open');
    if (openNotes.length === 0) return;

    // Snapshot so undo can fully restore.
    const snapshot = openNotes.map(l => ({
      id: l.id,
      x: l.x || 0, y: l.y || 0,
      w: l.w || 280, h: l.h || 320,
    }));

    const MIN_COL = 240;
    const CLUSTER_THRESH = 140;

    // 1. Cluster by X to discover natural columns.
    const sorted = [...openNotes].sort((a, b) => (a.x || 0) - (b.x || 0));
    const clusters = [];
    for (const n of sorted) {
      const x = n.x || 0;
      if (clusters.length === 0) { clusters.push([n]); continue; }
      const last = clusters[clusters.length - 1];
      const lastMaxX = Math.max(...last.map(m => m.x || 0));
      if (x - lastMaxX > CLUSTER_THRESH) clusters.push([n]);
      else last.push(n);
    }

    const detected = clusters.length;
    const maxFit = Math.max(1, Math.floor(viewportW / MIN_COL));

    // 2. Ambiguity check: whenever there's more than one cluster and the user
    //    hasn't already picked a count, pop the picker so they can confirm or
    //    override. Detection is only a hint, not a decision.
    if (forcedCols == null && detected >= 2) {
      setArrangePicker({ detected, maxFit, selected: detected });
      return;
    }

    // 3. Decide the target column count. If forced by the picker, honor it
    //    (may be tighter than MIN_COL — user explicitly asked for skinny).
    const targetCols = forcedCols != null
      ? Math.max(1, forcedCols)
      : Math.min(detected, maxFit);

    // 4a. If user asked for MORE columns than we detected, redistribute all
    //     tiles across targetCols columns by their current X order. We can't
    //     guess the "right" split from clustering alone — the user has told us
    //     what they want, so honor it directly.
    if (clusters.length < targetCols) {
      const all = clusters.flat().sort((a, b) => (a.x || 0) - (b.x || 0));
      const total = all.length;
      const effectiveCols = Math.min(targetCols, total);
      const base = Math.floor(total / effectiveCols);
      const extra = total % effectiveCols;
      clusters.length = 0;
      let idx = 0;
      for (let c = 0; c < effectiveCols; c++) {
        const take = base + (c < extra ? 1 : 0);
        clusters.push(all.slice(idx, idx + take));
        idx += take;
      }
    }

    // 4b. Merge adjacent clusters with the smallest gap until we hit target.
    while (clusters.length > targetCols) {
      let bestI = 0, bestGap = Infinity;
      for (let i = 0; i < clusters.length - 1; i++) {
        const leftMax  = Math.max(...clusters[i].map(m => (m.x || 0) + (m.w || 280)));
        const rightMin = Math.min(...clusters[i + 1].map(m => m.x || 0));
        const g = rightMin - leftMax;
        if (g < bestGap) { bestGap = g; bestI = i; }
      }
      clusters[bestI] = [...clusters[bestI], ...clusters[bestI + 1]];
      clusters.splice(bestI + 1, 1);
    }

    setArrangePicker(null);
    const numCols = clusters.length;
    const colW = Math.floor(viewportW / numCols);

    // 3. Place each column, filling full viewport height with no gaps.
    //    Heights within a column are proportional to original heights so
    //    previously-tall tiles stay taller than their neighbors.
    //    MIN_TILE_H enforces ListPanel's CSS minHeight so the column sum
    //    doesn't silently overflow the viewport.
    const MIN_TILE_H = 70; // matches ListPanel CSS minHeight
    const updates = [];
    clusters.forEach((col, i) => {
      const sortedCol = [...col].sort((a, b) => (a.y || 0) - (b.y || 0));
      const n = sortedCol.length;
      const origH = sortedCol.map(m => Math.max(60, m.h || 320));
      const x = i * colW;
      // Last column absorbs any remainder so we reach the right edge exactly.
      const w = (i === numCols - 1) ? (viewportW - x) : colW;

      // Allocate heights. If all tiles can't fit at MIN_TILE_H, give each
      // MIN_TILE_H and accept that the column overflows (it has to).
      const heights = new Array(n).fill(0);
      if (n * MIN_TILE_H >= viewportH) {
        for (let j = 0; j < n; j++) heights[j] = MIN_TILE_H;
      } else {
        // Iteratively pin any tile whose proportional share falls below
        // MIN_TILE_H, then redistribute the leftover space proportionally
        // among unpinned tiles. Continues until all unpinned tiles get a
        // share >= MIN_TILE_H.
        const pinned = new Array(n).fill(false);
        for (let iter = 0; iter < n + 1; iter++) {
          let pinnedSpace = 0;
          let freeOrig = 0;
          for (let j = 0; j < n; j++) {
            if (pinned[j]) pinnedSpace += MIN_TILE_H;
            else freeOrig += origH[j];
          }
          const freeSpace = viewportH - pinnedSpace;
          if (freeOrig <= 0) break;
          let newPin = -1;
          let smallestShare = Infinity;
          for (let j = 0; j < n; j++) {
            if (pinned[j]) continue;
            const share = (origH[j] / freeOrig) * freeSpace;
            if (share < MIN_TILE_H && share < smallestShare) {
              smallestShare = share;
              newPin = j;
            }
          }
          if (newPin === -1) {
            // All unpinned tiles get their proportional share (floored).
            for (let j = 0; j < n; j++) {
              if (!pinned[j]) {
                heights[j] = Math.max(MIN_TILE_H, Math.floor((origH[j] / freeOrig) * freeSpace));
              } else {
                heights[j] = MIN_TILE_H;
              }
            }
            break;
          }
          pinned[newPin] = true;
        }
        // Fold any rounding leftover into the tallest non-pinned tile so
        // the column hits the viewport bottom exactly.
        const sumNow = heights.reduce((a, b) => a + b, 0);
        const diff = viewportH - sumNow;
        if (diff !== 0) {
          let tallest = 0;
          for (let j = 1; j < n; j++) if (heights[j] > heights[tallest]) tallest = j;
          heights[tallest] = Math.max(MIN_TILE_H, heights[tallest] + diff);
        }
      }

      let y = 0;
      sortedCol.forEach((note, j) => {
        const h = heights[j];
        updates.push({ id: note.id, x, y, w, h });
        y += h;
      });
    });

    // 4. Write.
    updates.forEach(u => saveList(u.id, { x: u.x, y: u.y, w: u.w, h: u.h }));

    // 5. Header undo — replaces the bottom toast for this action.
    if (arrangeUndoTimer.current) clearTimeout(arrangeUndoTimer.current);
    setArrangeUndo({
      action: () => {
        snapshot.forEach(s => saveList(s.id, { x: s.x, y: s.y, w: s.w, h: s.h }));
      },
    });
    arrangeUndoTimer.current = setTimeout(() => setArrangeUndo(null), 10000);

    // Fade in the green grid so the user can fine-tune column / tile sizes.
    setGridMode(true);
  }, [lists, saveList]);

  // Clear arrange undo timer on unmount.
  useEffect(() => {
    return () => { if (arrangeUndoTimer.current) clearTimeout(arrangeUndoTimer.current); };
  }, []);

  // ── Free ↔ Grid mode transitions ───────────────────────────
  //
  // State for the two flows:
  //   enterGridPicker — column-count picker shown when flipping free → grid
  //   exitGridChoice  — "Keep / Restore" popup shown when flipping grid → free
  //                     (only when a prior free snapshot exists; silent otherwise)
  const [enterGridPicker, setEnterGridPicker] = useState(null); // { detected, maxFit, selected } | null
  const [exitGridChoice, setExitGridChoice] = useState(false);

  // Cluster the open tiles into columns by their horizontal centers.
  // Tiles whose centers are within MIN_COL/2 of each other are the same
  // column; anything further apart starts a new column. This mirrors
  // how a person would eyeball the layout — tiles "at roughly the same
  // X" are one column, regardless of exact left-edge position.
  //
  // Using centers rather than left or right edges avoids the pathology
  // where a single wide tile plus a narrow one nearby fake-out a left-
  // edge comparator, or where same-column tiles with drifted X values
  // fake-out a right-edge comparator.
  const clusterOpenListsIntoColumns = useCallback((targetCols) => {
    const openNotes = lists.filter(l => !l.state || l.state === 'open');
    if (openNotes.length === 0) return [];
    const MIN_COL_LOCAL = 240;
    // 2/3 of MIN_COL: tiles whose horizontal centers are within ~160px
    // are the same column. Tighter than MIN_COL (prevents merging clearly
    // different columns) but looser than MIN_COL/2 (which over-split when
    // tiles in the same column drifted ~130px apart).
    const CENTER_TOL = Math.round(MIN_COL_LOCAL * 2 / 3);
    const centerOf = t => (t.x || 0) + (t.w || 280) / 2;
    const sorted = [...openNotes].sort((a, b) => centerOf(a) - centerOf(b));
    const clusters = [];
    // Running mean x-center per cluster, used as the comparison point
    // for subsequent tiles so we don't anchor on a single outlier.
    const clusterCenters = [];
    for (const n of sorted) {
      const c = centerOf(n);
      if (clusters.length === 0) {
        clusters.push([n]);
        clusterCenters.push(c);
        continue;
      }
      const last = clusters.length - 1;
      if (Math.abs(c - clusterCenters[last]) <= CENTER_TOL) {
        clusters[last].push(n);
        clusterCenters[last] =
          (clusterCenters[last] * (clusters[last].length - 1) + c) / clusters[last].length;
      } else {
        clusters.push([n]);
        clusterCenters.push(c);
      }
    }
    // Caller can force a specific column count by passing a finite
    // targetCols. When the caller just wants the natural detected count
    // (Infinity or null), skip both redistribute/merge passes so we
    // return exactly what the clustering loop found.
    const forced = Number.isFinite(targetCols) && targetCols > 0;
    // If the user asked for more columns than we detected, redistribute
    // tiles evenly by X across the requested number of columns.
    if (forced && clusters.length < targetCols) {
      const all = clusters.flat().sort((a, b) => (a.x || 0) - (b.x || 0));
      const total = all.length;
      const effectiveCols = Math.min(targetCols, total);
      const base = Math.floor(total / effectiveCols);
      const extra = total % effectiveCols;
      clusters.length = 0;
      let idx = 0;
      for (let c = 0; c < effectiveCols; c++) {
        const take = base + (c < extra ? 1 : 0);
        clusters.push(all.slice(idx, idx + take));
        idx += take;
      }
    }
    // If more clusters than requested, merge adjacent clusters with the
    // smallest gap until we hit the target.
    while (forced && clusters.length > targetCols) {
      let bestI = 0, bestGap = Infinity;
      for (let i = 0; i < clusters.length - 1; i++) {
        const leftMax  = Math.max(...clusters[i].map(m => (m.x || 0) + (m.w || 280)));
        const rightMin = Math.min(...clusters[i + 1].map(m => m.x || 0));
        const g = rightMin - leftMax;
        if (g < bestGap) { bestGap = g; bestI = i; }
      }
      clusters[bestI] = [...clusters[bestI], ...clusters[bestI + 1]];
      clusters.splice(bestI + 1, 1);
    }
    // Sort top-to-bottom within each column.
    for (const col of clusters) col.sort((a, b) => (a.y || 0) - (b.y || 0));
    return clusters;
  }, [lists]);

  // Open the column picker. Called from the mode toggle when the current
  // mode is 'free'. Pre-selects the detected cluster count if any, or 2.
  const startFreeToGrid = useCallback(() => {
    const canvasEl = canvasRef.current;
    const viewportW = canvasEl?.clientWidth || 1200;
    // Must match GridCanvas's MIN_COL — lowered to 192 (Walter
    // 2026-05-05) so narrow viewports still fit 3–4 columns.
    const MIN_COL = 192;
    const maxFit = Math.max(1, Math.floor(viewportW / MIN_COL));
    const detectedClusters = clusterOpenListsIntoColumns(Infinity);
    const detected = Math.max(1, detectedClusters.length);
    // Walter 2026-05-05: when the user already arranged tiles in N
    // columns in free mode, offer N as an option even if the viewport
    // can't strictly fit N at MIN_COL (the grid math accepts
    // horizontal overflow gracefully). Better to honor the user's
    // intent and let them scroll than to silently down-rank to 3.
    const selected = Math.max(detected, 2);
    setEnterGridPicker({ detected, maxFit, selected });
  }, [clusterOpenListsIntoColumns]);

  // Commit the free→grid transition after the picker confirms. Captures
  // a snapshot of every open list's x/y/w/h for later restore, writes
  // gridCol/gridRow derived from clustering, and flips board.mode.
  const commitFreeToGrid = useCallback(async (columnCount) => {
    setEnterGridPicker(null);
    const openLists = lists.filter(l => !l.state || l.state === 'open');
    if (openLists.length === 0) {
      // Nothing to arrange — still flip the mode so the toggle responds.
      await onUpdateBoard(board.id, { mode: 'grid', gridColumnCount: columnCount, freeSnapshot: {} });
      return;
    }
    // Snapshot: only open lists, since closed/archived tiles don't affect
    // grid layout and we'll restore only what was on canvas at switch time.
    const freeSnapshot = {};
    for (const l of openLists) {
      freeSnapshot[l.id] = {
        x: l.x || 0, y: l.y || 0,
        w: l.w || 280, h: l.h || 320,
      };
    }
    const clusters = clusterOpenListsIntoColumns(columnCount);
    // Write grid positions for every open list; closed lists keep whatever
    // gridCol/gridRow they already have (or none, which grid mode tolerates).
    await Promise.all(
      clusters.flatMap((col, ci) =>
        col.map((tile, ri) => saveList(tile.id, { gridCol: ci, gridRow: ri })),
      ),
    );
    await onUpdateBoard(board.id, {
      mode: 'grid',
      gridColumnCount: columnCount,
      freeSnapshot,
    });
    // Undo restores the pre-transition free positions exactly and flips
    // mode back. Captures freeSnapshot in closure so the undo is self-
    // sufficient even after this function returns.
    pushToast('switched to grid', async () => {
      await Promise.all(
        Object.entries(freeSnapshot).map(([id, pos]) =>
          saveList(id, { x: pos.x, y: pos.y, w: pos.w, h: pos.h }),
        ),
      );
      await onUpdateBoard(board.id, { mode: 'free', freeSnapshot: null });
    });
  }, [lists, clusterOpenListsIntoColumns, board, onUpdateBoard, saveList, pushToast]);

  // Commit the grid→free transition. 'keep' derives x/y/w/h from the
  // current grid layout + viewport; 'restore' applies the stored snapshot.
  // Either way, clears the snapshot and flips mode back to free.
  const commitGridToFree = useCallback(async (choice) => {
    setExitGridChoice(false);
    // Capture everything the undo toast will need BEFORE we mutate state:
    //   - the current freeSnapshot (about to be cleared)
    //   - each open list's current x/y/w/h (about to be overwritten in
    //     the 'keep' path; unchanged in 'restore', but harmless to re-write)
    //   - the column count (still in board doc but cheaper to capture)
    const currentGridColumnCount = gridColumnCount;
    const preCommitSnapshot = board?.freeSnapshot
      ? { ...board.freeSnapshot }
      : null;
    const openLists = lists.filter(l => !l.state || l.state === 'open');
    const preCommitPositions = {};
    for (const l of openLists) {
      preCommitPositions[l.id] = {
        x: l.x || 0, y: l.y || 0,
        w: l.w || 280, h: l.h || 320,
      };
    }
    if (choice === 'restore' && board?.freeSnapshot) {
      // Apply snapshot back to each list it covers. Lists that weren't in
      // the snapshot (e.g. newly created during grid session) keep whatever
      // x/y/w/h they had — grid mode never wrote to those fields so they're
      // untouched from the moment the tile was last in free mode.
      await Promise.all(
        Object.entries(board.freeSnapshot).map(([id, pos]) =>
          saveList(id, { x: pos.x, y: pos.y, w: pos.w, h: pos.h }),
        ),
      );
    } else {
      // 'keep': derive x/y/w/h from the grid layout at current viewport.
      // Matches GridCanvas's gap-based layout exactly — column widths
      // come from the weights, each column has GAP between tiles, and
      // there's OUTER_PAD of frame around the whole grid. Positions
      // start at (OUTER_PAD, OUTER_PAD) so the first tile sits inside
      // the outer frame, not flush against the canvas edge.
      const GRID_GAP = 6; // keep in sync with GridCanvas GAP
      const GRID_OUTER = 6; // keep in sync with GridCanvas OUTER_PAD
      const canvasEl = canvasRef.current;
      const viewportW = canvasEl?.clientWidth || 1200;
      const viewportH = canvasEl?.clientHeight || 700;
      const n = Math.max(1, gridColumnCount);
      const usableW = Math.max(
        0,
        viewportW - Math.max(0, n - 1) * GRID_GAP - 2 * GRID_OUTER,
      );
      const colWidths = computeColumnWidths(board?.gridColumnWeights, n, usableW);
      // x offsets begin at OUTER_PAD to account for the outer frame.
      const colOffsets = [GRID_OUTER];
      for (let i = 0; i < colWidths.length - 1; i++) {
        colOffsets.push(colOffsets[i] + colWidths[i] + GRID_GAP);
      }
      const byCol = Array.from({ length: n }, () => []);
      for (const l of openLists) {
        const c = Math.max(0, Math.min(n - 1, l.gridCol ?? 0));
        byCol[c].push(l);
      }
      for (const col of byCol) {
        col.sort((a, b) => (a.gridRow ?? 0) - (b.gridRow ?? 0));
      }
      const MIN_TILE_H = 70;
      const writes = [];
      byCol.forEach((col, ci) => {
        if (col.length === 0) return;
        const gapsV = Math.max(0, col.length - 1) * GRID_GAP;
        const usableViewportH = Math.max(0, viewportH - 2 * GRID_OUTER);
        const columnH = Math.max(usableViewportH, col.length * MIN_TILE_H + gapsV);
        const usableH = Math.max(col.length * MIN_TILE_H, columnH - gapsV);
        const heights = computeProportionalHeights(col, usableH);
        // y starts at OUTER_PAD for the top tile, then advances by
        // tile height + gap between each subsequent tile.
        let yCursor = GRID_OUTER;
        col.forEach((tile, ri) => {
          const h = heights[ri];
          writes.push(saveList(tile.id, {
            x: colOffsets[ci],
            y: yCursor,
            w: colWidths[ci],
            h,
          }));
          yCursor += h + GRID_GAP;
        });
      });
      await Promise.all(writes);
    }
    await onUpdateBoard(board.id, { mode: 'free', freeSnapshot: null });
    // Undo: reverse both pieces of the commit. Restore each list's
    // x/y/w/h to pre-commit values (noop for 'restore', actual revert for
    // 'keep'), and flip the board back to grid with the original snapshot
    // so a later grid→free toggle still has something to restore to.
    pushToast('switched to free', async () => {
      await Promise.all(
        Object.entries(preCommitPositions).map(([id, pos]) =>
          saveList(id, { x: pos.x, y: pos.y, w: pos.w, h: pos.h }),
        ),
      );
      await onUpdateBoard(board.id, {
        mode: 'grid',
        gridColumnCount: currentGridColumnCount,
        freeSnapshot: preCommitSnapshot,
      });
    });
  }, [lists, board, onUpdateBoard, saveList, gridColumnCount, pushToast]);

  // Start the grid→free flow. If we have a previous free snapshot, ask the
  // user whether to restore it or keep the current grid layout as free
  // positions. If no snapshot, just convert silently.
  const startGridToFree = useCallback(() => {
    if (board?.freeSnapshot && Object.keys(board.freeSnapshot).length > 0) {
      setExitGridChoice(true);
    } else {
      commitGridToFree('keep');
    }
  }, [board, commitGridToFree]);

  // User-facing toggle. Free → picker, grid → exit flow.
  const handleToggleMode = useCallback(() => {
    if (boardMode === 'free') startFreeToGrid();
    else startGridToFree();
  }, [boardMode, startFreeToGrid, startGridToFree]);

  // Surface the mode switch when the user narrows the window below the mobile
  // breakpoint — the layout change is otherwise a silent jump and first-timers
  // may not realize it was triggered by the resize. Only on desktop→mobile
  // (the surprising direction); going back to desktop is obvious.
  useEffect(() => {
    const was = prevIsMobileRef.current;
    prevIsMobileRef.current = isMobile;
    if (was === false && isMobile === true) {
      setShowMobileHint(true);
      // 2.5x slower than before — hint lingers ~5.25s before unmount.
      const t = setTimeout(() => setShowMobileHint(false), 5250);
      return () => clearTimeout(t);
    }
  }, [isMobile]);

  const runArrangeUndo = useCallback(() => {
    if (!arrangeUndo) return;
    if (arrangeUndoTimer.current) { clearTimeout(arrangeUndoTimer.current); arrangeUndoTimer.current = null; }
    try { arrangeUndo.action(); } catch (e) { console.warn('[arrange undo] failed:', e); }
    setArrangeUndo(null);
    setGridMode(false);
  }, [arrangeUndo]);

  // One commit per grid-drag — just applies the new sizes. No undo toasts
  // (the grid itself is the live control; dragging again to reverse is
  // faster than reading a stack of toasts).
  const handleGridCommit = useCallback(({ updates }) => {
    updates.forEach(u => {
      const { id, ...patch } = u;
      saveList(id, patch);
    });
  }, [saveList]);

  // Auto-dismiss the green grid when the user shifts attention elsewhere:
  // opens search/settings, expands a note, or a conflict modal shows up.
  const gridModeRef = useRef(false);
  useEffect(() => { gridModeRef.current = gridMode; }, [gridMode]);
  useEffect(() => {
    if (!gridModeRef.current) return;
    if (showSearch || showSettings || expandedListId) {
      setGridMode(false);
    }
  }, [showSearch, showSettings, expandedListId]);

  // Also dismiss when focus shifts to a different note after the grid opens.
  // Capture the focus snapshot at the moment the grid opens so we ignore
  // that initial baseline and only react to subsequent shifts.
  const gridFocusBaseline = useRef(null);
  useEffect(() => {
    if (gridMode) {
      gridFocusBaseline.current = focusedId;
    } else {
      gridFocusBaseline.current = null;
    }
  }, [gridMode]);
  useEffect(() => {
    if (!gridModeRef.current) return;
    if (gridFocusBaseline.current === null) return;
    if (focusedId !== gridFocusBaseline.current) setGridMode(false);
  }, [focusedId]);

  // Ctrl+Shift+A used to trigger auto-arrange; that action is now subsumed
  // by the grid/free mode toggle, so the shortcut is intentionally gone.
  // Leaving handleAutoArrange in place as dead code for now — it can be
  // cleaned up with the rest of the arrange scaffolding in a later pass.

  // Tab / Shift+Tab cycle focus through open canvas notes in sidebar order.
  // Fires from anywhere (no longer requires focus in a note textarea or on
  // body) — only bails when a modal is open or the FocusedPanel is active,
  // where Tab should do its native thing inside the modal/focused view.
  useEffect(() => {
    const h = (e) => {
      if (e.key !== 'Tab' || e.ctrlKey || e.metaKey || e.altKey) return;
      if (showSettings || showSearch || expandedListId) return;
      // Let Tab behave natively inside the search modal's input or other
      // inputs OUTSIDE of tiles — we only want to hijack it when the user
      // is in the canvas itself. A title <input> inside a tile (renaming)
      // passes through, because they'd expect Tab to confirm and move on.
      const el = document.activeElement;
      if (el && el.tagName === 'INPUT' && !el.closest?.('[data-list-id]')) return;

      const openLists = lists
        .filter(l => !l.state || l.state === 'open')
        .sort((a, b) => (a.order || 0) - (b.order || 0));
      if (openLists.length < 2) return;

      e.preventDefault();
      const curIdx = openLists.findIndex(l => l.id === focusedId);
      const nextIdx = e.shiftKey
        ? (curIdx <= 0 ? openLists.length - 1 : curIdx - 1)
        : (curIdx < 0 || curIdx >= openLists.length - 1 ? 0 : curIdx + 1);
      const target = openLists[nextIdx];
      focusPanel(target.id);
      requestAnimationFrame(() => {
        const ta = document.querySelector(`[data-list-id="${target.id}"] textarea`);
        ta?.focus();
      });
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [lists, focusedId, showSettings, showSearch, expandedListId, focusPanel]);

  // Grid-mode helper: after a tile is removed from the canvas (via close /
  // archive / delete), any column it was the last tile of should collapse
  // so the grid doesn't leave a visible gap. Recomputes columns from the
  // post-change canvas set, filters out empties, rewrites gridCol for any
  // tile whose column index shifted, and updates the board's count +
  // weights atomically. No-op outside grid mode.
  // Walter 2026-06-09 (bug: closing a list collapsed unrelated columns):
  // this previously filtered out EVERY empty column and renumbered all
  // tiles — so removing the only tile in column 1 of a 3-column board
  // also destroyed an empty column 2/3. Empty columns are deliberate
  // user structure (same principle as the GridCanvas drag prune). Now
  // it collapses ONLY the SOURCE column (the one the removed tile
  // vacated), and only if that column is now empty; every other column,
  // empty or not, is left exactly as-is. `srcCol` is the removed tile's
  // gridCol, captured by the caller BEFORE the delete/archive.
  const pruneEmptyGridColumns = useCallback(async (removedListId, srcCol) => {
    if (boardMode !== 'grid') return;
    const n = gridColumnCount;
    if (n <= 1) return;
    if (srcCol == null) return;
    const col = Math.max(0, Math.min(n - 1, srcCol));
    const openLists = lists.filter(l =>
      l.id !== removedListId && (!l.state || l.state === 'open'),
    );
    // Only collapse if the SOURCE column is now empty.
    const srcStillOccupied = openLists.some(
      l => Math.max(0, Math.min(n - 1, l.gridCol ?? 0)) === col,
    );
    if (srcStillOccupied) return;
    // Shift tiles in columns to the right of the source one column left;
    // columns before it (and any other empty columns) are untouched.
    const writes = [];
    for (const l of openLists) {
      const c = Math.max(0, Math.min(n - 1, l.gridCol ?? 0));
      if (c > col) writes.push(saveList(l.id, { gridCol: c - 1 }));
    }
    let currentWeights = (board?.gridColumnWeights && board.gridColumnWeights.length > 0)
      ? board.gridColumnWeights.slice(0, n)
      : new Array(n).fill(1);
    while (currentWeights.length < n) currentWeights.push(1);
    const newWeights = currentWeights.filter((_, i) => i !== col);
    await Promise.all(writes);
    await onUpdateBoard(board.id, {
      gridColumnCount: n - 1,
      gridColumnWeights: newWeights,
    });
  }, [boardMode, gridColumnCount, lists, board, onUpdateBoard, saveList]);

  const handleClose = useCallback(async (listId) => {
    const target = lists.find(l => l.id === listId);
    if (focusedId === listId) setFocusedId(null);
    if (expandedListId === listId) setExpandedListId(null);
    if (target?.state === 'archived') return;
    // Optimistically hide the tile right now — don't wait for the
    // (possibly cold) Firestore round-trip. Reverted below if the write
    // throws; otherwise the snapshot reconciles the override.
    setOptimisticListState(s => ({ ...s, [listId]: 'closed' }));
    const revertOptimistic = () => setOptimisticListState(s => { const n = { ...s }; delete n[listId]; return n; });
    // Walter 2026-06-11: close ALWAYS tucks to the sidebar — never
    // deletes. The old behaviour trashed an empty, auto-named list on
    // close, but (a) `isEmpty` was checked against the SAVED content, so
    // a note typed-but-not-yet-saved read as empty and got lost with the
    // list, and (b) the optimistic-close made the "it just got deleted"
    // surprise much sharper. Close is now a pure, reversible tuck;
    // removing a list is its own explicit action (⋯ → delete, with undo).
    try { await saveList(listId, { state: 'closed' }); } catch (e) { revertOptimistic(); }
    // NOTE: don't prune empty columns here. Closing is meant to be
    // reversible — pruning the column would shift gridColumnCount
    // down, and on reopen the list's saved gridCol would be invalid
    // (clamped to col 0 by the layout). Pruning still runs on
    // archive and delete, where the removal is intended to be
    // permanent.
  }, [lists, saveList, deleteList, pruneEmptyGridColumns, focusedId, expandedListId]);

  const handleArchive = useCallback(async (listId) => {
    const target = lists.find(l => l.id === listId);
    if (!target) return;
    const prevState = target.state || 'open';
    const label = (target.title || '').trim() || '(untitled)';
    if (focusedId === listId) setFocusedId(null);
    if (expandedListId === listId) setExpandedListId(null);
    const srcCol = target.gridCol;
    await saveList(listId, { state: 'archived' });
    await pruneEmptyGridColumns(listId, srcCol);
    pushToast(`archived "${label}"`, async () => {
      await saveList(listId, { state: prevState });
    });
  }, [lists, saveList, focusedId, expandedListId, pushToast, pruneEmptyGridColumns]);

  const handleUnarchive = useCallback(async (listId) => {
    const target = lists.find(l => l.id === listId);
    if (!target) return;
    const label = (target.title || '').trim() || '(untitled)';
    await saveList(listId, { state: 'open' });
    pushToast(`unarchived "${label}"`, async () => {
      await saveList(listId, { state: 'archived' });
    });
  }, [lists, saveList, pushToast]);

  const handleDelete = useCallback(async (listId) => {
    const target = lists.find(l => l.id === listId);
    if (!target) return;
    const label = (target.title || '').trim() || '(untitled)';
    // Walter 2026-05-11: delete on a placed shared list means
    // "remove from MY board" — clear the per-user placement record,
    // not trash the underlying list (which belongs to someone else).
    // The list stays accessible from the "shared with me" sidebar
    // section and can be re-placed via the list's ⋯-menu. Undo
    // restores the placement record at its previous target board.
    if (target._sharedFromOwnerUid && target._placementId && setSharedListPlacement && clearSharedListPlacement) {
      if (!window.confirm(`Remove "${label}" from this board?\n\nThis is a shared list — it stays accessible from your "shared with me" sidebar section, and you can re-place it anywhere via the list's ⋯-menu.`)) return;
      if (focusedId === listId) setFocusedId(null);
      if (expandedListId === listId) setExpandedListId(null);
      const prevPlacement = sharedListPlacements?.[listId];
      await clearSharedListPlacement(listId);
      pushToast(`removed "${label}" from this board`, async () => {
        if (prevPlacement) {
          await setSharedListPlacement(listId, {
            sourceOwnerUid: prevPlacement.sourceOwnerUid,
            sourceBoardId: prevPlacement.sourceBoardId,
            boardId: prevPlacement.boardId,
          });
        }
      });
      return;
    }
    // Walter 2026-05-25 / 2026-05-26 (Bug #10 — round-2 tester
    // report). Originally shipped as a warning-only ("this list has
    // N reminder(s) attached") with orphan-firing behavior — the
    // tester expected the reminders to be deleted with the list,
    // and Walter agreed. Now the trashList Cloud Function
    // cascade-deletes the caller's reminders for this list into the
    // same trash entry; restoreFromTrash brings them back together.
    // Copy is explicit about what's going where.
    const activeReminderCount = (reminders || []).filter(
      r => r.sourceListId === listId && r.status !== 'done'
    ).length;
    const reminderClause = activeReminderCount > 0
      ? `\n\nThis list has ${activeReminderCount} active reminder${activeReminderCount === 1 ? '' : 's'}. Deleting the list will delete ${activeReminderCount === 1 ? 'it' : 'them'} too.`
      : '';
    const restoreClause = activeReminderCount > 0
      ? '\n\nBoth the list and its reminders will be in trash for 30 days — restore from the account popover → trash if you change your mind.'
      : '\n\nYou can restore the list within 30 days from the account popover → trash.';
    if (!window.confirm(`Move "${label}" to trash?${reminderClause}${restoreClause}`)) return;
    if (focusedId === listId) setFocusedId(null);
    if (expandedListId === listId) setExpandedListId(null);
    const srcCol = target?.gridCol;
    // Take the tile off the canvas NOW — see canvasLists. The confirm has
    // already been answered, so waiting on a cold start to acknowledge it just
    // reads as "did that work?".
    setOptimisticListState(prev => ({ ...prev, [listId]: 'trashed' }));
    const result = await deleteList(listId);
    // Walter 2026-07-19: a failed trashList call used to fall through and
    // show "deleted" while the list sat there. Surface the failure instead.
    if (result?.error) {
      // Walter 2026-08-07: the advice now follows what the probe in
      // lib/callableDiagnostics actually found, instead of blaming the
      // ad-blocker on every failure (#161 guessed; this one knows).
      const advice = result.blocked
        ? ' Allow listicity.app in your blocker, then try again.'
        : '';
      // Put it back — the delete didn't happen.
      setOptimisticListState(prev => {
        const next = { ...prev };
        delete next[listId];
        return next;
      });
      pushToast(
        `couldn't delete "${label}" — ${result.error}.${advice}`,
        null, { ttlMs: 8000 },
      );
      return;
    }
    await pruneEmptyGridColumns(listId, srcCol);
    const trashId = result?.trashId || null;
    const remindersTrashed = result?.remindersTrashed || 0;
    // Compose a toast that reflects what actually went to trash.
    const summary = remindersTrashed > 0
      ? `deleted "${label}" + ${remindersTrashed} reminder${remindersTrashed === 1 ? '' : 's'}`
      : `deleted "${label}"`;
    // Undo path: if we have a trashId, restore via the Cloud
    // Function (brings back the list AND any packaged reminders).
    // If the function call returned null (idempotent skip, e.g.,
    // the list was already gone), the undo is a no-op — we still
    // show the toast so the user gets feedback, but the undo
    // action has nothing to undo.
    const undo = trashId
      ? async () => {
          try {
            const fn = httpsCallable(functions, 'restoreFromTrash');
            const res = await fn({ trashId });
            const data = res?.data || {};
            // Walter 2026-05-26 diagnostic — surface the function's
            // own count so the user sees what came back. Helps
            // diagnose partial-cascade reports without needing to
            // check Cloud Function logs.
            const r = data.remindersRestored || 0;
            if (r > 0) {
              pushToast(`restored "${label}" + ${r} reminder${r === 1 ? '' : 's'}`, null, { ttlMs: 3500 });
            }
          } catch (e) {
            // Distinguish "trash entry not found" (e.g., already
            // restored via another toast click) from genuine errors.
            // The Cloud Function throws HttpsError with code
            // 'not-found' for the former.
            const code = e?.code || '';
            console.warn('[restoreFromTrash] failed:', code, e);
            if (code.includes('not-found') || code.includes('functions/not-found')) {
              pushToast('already restored', null, { ttlMs: 3000 });
            } else {
              pushToast('couldn\'t restore — try the trash page', null, { ttlMs: 5000 });
            }
          }
        }
      : null;
    pushToast(summary, undo);
  }, [lists, reminders, deleteList, focusedId, expandedListId, pushToast, pruneEmptyGridColumns, setSharedListPlacement, clearSharedListPlacement, sharedListPlacements]);

  const handleReorder = useCallback(async (listId, newOrder) => {
    await saveList(listId, { order: newOrder });
  }, [saveList]);

  /**
   * handleSortLists — invoked from the sidebar's sort menu. Rewrites
   * every open list's `order` to match the chosen sort, keeping
   * archived lists at the end in their existing relative order.
   *
   * mode='canvas': sort by the visible canvas arrangement. In grid
   *   mode, that's (gridCol, gridRow) — column-major top-to-bottom.
   *   In free mode, it's (y, x) — eye-scan order top-to-bottom,
   *   left-to-right.
   * mode='alpha':  sort by title (case-insensitive, empty titles last).
   *
   * Walter 2026-05-16: fixes the "Tab cycles in a different order than
   * I see on the canvas" complaint — Tab reads from sidebar `order`,
   * so realigning the sidebar realigns Tab too. Writes are issued
   * in parallel; the snapshot listener reconciles ordering on the
   * next tick.
   */
  const handleSortLists = useCallback(async (mode) => {
    if (!Array.isArray(lists) || lists.length === 0) return;
    const open = lists.filter(l => !l.state || l.state === 'open' || l.state === 'closed');
    const archived = lists.filter(l => l.state === 'archived');

    let sortedOpen;
    if (mode === 'alpha') {
      sortedOpen = [...open].sort((a, b) => {
        const at = (a.title || '').trim().toLowerCase();
        const bt = (b.title || '').trim().toLowerCase();
        if (!at && bt) return 1;
        if (at && !bt) return -1;
        if (at !== bt) return at < bt ? -1 : 1;
        return (a.order || 0) - (b.order || 0);
      });
    } else if (mode === 'canvas') {
      const isGrid = boardMode === 'grid';
      sortedOpen = [...open].sort((a, b) => {
        if (isGrid) {
          const ac = Number.isFinite(a.gridCol) ? a.gridCol : 999;
          const bc = Number.isFinite(b.gridCol) ? b.gridCol : 999;
          if (ac !== bc) return ac - bc;
          const ar = Number.isFinite(a.gridRow) ? a.gridRow : 999;
          const br = Number.isFinite(b.gridRow) ? b.gridRow : 999;
          if (ar !== br) return ar - br;
        } else {
          // Free layout: top-to-bottom, then left-to-right.
          // Cluster rows that are within ~30px vertically as the same
          // "row" so a tile that's slightly higher than its neighbour
          // doesn't sort entirely above it.
          const ay = Number.isFinite(a.y) ? a.y : 999999;
          const by = Number.isFinite(b.y) ? b.y : 999999;
          if (Math.abs(ay - by) > 30) return ay - by;
          const ax = Number.isFinite(a.x) ? a.x : 999999;
          const bx = Number.isFinite(b.x) ? b.x : 999999;
          if (ax !== bx) return ax - bx;
        }
        return (a.order || 0) - (b.order || 0);
      });
    } else {
      return; // unknown mode
    }

    // Stable archived order — keep their relative positions, just put
    // them all after the sorted-open block.
    const archivedSorted = [...archived].sort((a, b) => (a.order || 0) - (b.order || 0));
    const all = [...sortedOpen, ...archivedSorted];
    await Promise.all(all.map((l, i) => saveList(l.id, { order: i })));
  }, [lists, boardMode, saveList]);

  const handleSidebarSelect = useCallback(async (list) => {
    if (!list.state || list.state === 'open') {
      focusPanel(list.id);
      return;
    }
    if (list.state === 'closed') {
      // Optimistically show the tile right now (don't wait on a possibly
      // cold Firestore round-trip). Reverted below if the write throws.
      setOptimisticListState(s => ({ ...s, [list.id]: 'open' }));
      nextZRef.current += 1;
      if (boardMode === 'grid') {
        // Try to drop the reopened tile back at its old gridCol/gridRow.
        // If the stored column is gone or the row is already taken, fall
        // back to appending at the bottom of the shortest current column
        // so we never stomp on another tile's slot.
        const n = Math.max(1, gridColumnCount);
        const openLists = lists.filter(
          l => (!l.state || l.state === 'open') && l.id !== list.id,
        );
        const byCol = Array.from({ length: n }, () => []);
        for (const l of openLists) {
          const c = Math.max(0, Math.min(n - 1, l.gridCol ?? 0));
          byCol[c].push(l);
        }
        for (const col of byCol) {
          col.sort((a, b) => (a.gridRow ?? 0) - (b.gridRow ?? 0));
        }
        const storedCol = list.gridCol;
        let targetCol, targetRow;
        if (storedCol != null && storedCol >= 0 && storedCol < n) {
          targetCol = storedCol;
          const storedRow = list.gridRow ?? 0;
          // Clamp: if the old row is past the current column length, just
          // tuck the reopened tile in at the end of that column.
          targetRow = Math.min(Math.max(0, storedRow), byCol[storedCol].length);
        } else {
          // Append to shortest column for a roughly-balanced feel.
          let shortest = 0;
          for (let i = 1; i < n; i++) {
            if (byCol[i].length < byCol[shortest].length) shortest = i;
          }
          targetCol = shortest;
          targetRow = byCol[shortest].length;
        }
        // Shift tiles at or past targetRow in targetCol down one slot so
        // the reopened tile has a clean row to land in.
        const shifts = [];
        for (let i = targetRow; i < byCol[targetCol].length; i++) {
          shifts.push(saveList(byCol[targetCol][i].id, { gridRow: i + 1 }));
        }
        await Promise.all(shifts);
        setFocusedId(list.id);
        try {
          await saveList(list.id, {
            state: 'open',
            z: nextZRef.current,
            gridCol: targetCol,
            gridRow: targetRow,
          });
        } catch (e) {
          setOptimisticListState(s => { const n = { ...s }; delete n[list.id]; return n; });
        }
        return;
      }
      setFocusedId(list.id);
      saveList(list.id, { state: 'open', z: nextZRef.current });
      return;
    }
    nextZRef.current += 1;
    setFocusedId(list.id);
    saveList(list.id, { z: nextZRef.current });
  }, [focusPanel, saveList, boardMode, gridColumnCount, lists]);

  const handleExpand = useCallback((listId, cursorOffset) => {
    if (expandedListId === listId) return;
    setExpandedListId(listId);
    setJumpTarget({ listId, charOffset: cursorOffset ?? 0 });
  }, [expandedListId]);

  const handleFocusedExit = useCallback((cursorOffset) => {
    const id = expandedListId;
    setExpandedListId(null);
    if (id != null && cursorOffset != null) {
      setJumpTarget({ listId: id, charOffset: cursorOffset });
    }
  }, [expandedListId]);

  const handleJump = useCallback((list, charOffset) => {
    if (list.boardId !== board?.id) {
      setExpandedListId(null);
      onSwitchBoard(list.boardId);
      setTimeout(() => {
        focusPanel(list.id);
        setJumpTarget({ listId: list.id, charOffset: charOffset ?? 0 });
      }, 300);
    } else if (expandedListId && expandedListId === list.id) {
      setJumpTarget({ listId: list.id, charOffset: charOffset ?? 0 });
    } else {
      setExpandedListId(null);
      focusPanel(list.id);
      setJumpTarget({ listId: list.id, charOffset: charOffset ?? 0 });
    }
  }, [board, onSwitchBoard, focusPanel, expandedListId]);

  // Walter 2026-05-28: if `board` is undefined for more than 5s, swap
  // the silent "loading" placeholder for an actionable recovery screen.
  // Most board switches resolve in <1s; the 5s threshold is the point
  // at which something is genuinely wrong (extension blocking Firestore
  // WebChannel — Ghostery / uBlock / Brave Shields — or a transient
  // network blip). The recovery screen names the most likely cause and
  // gives a Retry button. Replaces the prior "No board selected." stub
  // which left users on a blank cream page indefinitely with no path
  // forward. See components/LoadStuckScreen.js.
  if (!board) return <LoadStuckScreen context="board" />;

  // Mobile takeover: when the viewport is phone-sized we replace the whole
  // board-page UI with the two-state master/detail layout. All data + handlers
  // flow down from here; BoardPage stays the single source of truth for the
  // Firestore subscription, conflict resolver, toasts, etc.
  if (isMobile) {
    return (
      <>
        <MobileBoard
          user={user}
          board={board}
          boards={boards}
          lists={lists}
          listsLoading={listsLoading}
          userPrefs={userPrefs}
          updateUserPrefs={updateUserPrefs}
          createReminder={createReminder}
          reminders={reminders}
          updateReminder={updateReminder}
          updateReminderInAgenda={updateReminderAndSource}
          updateReminderNotes={updateReminderNotes}
          deleteReminder={deleteReminder}
          deactivateReminder={deactivateReminder}
          fullyDeleteReminder={fullyDeleteReminder}
          pushSub={pushSub}
          pushToast={pushToast}
          onSignOut={onSignOut}
          onLinkGoogle={onLinkGoogle}
          onLinkMagicLink={onLinkMagicLink}
          onSwitchBoard={onSwitchBoard}
          onNewBoard={handleNewBoardGuarded}
          onRenameBoard={onRenameBoard}
          onDeleteBoard={onDeleteBoard}
          onUpdateBoard={onUpdateBoard}
          onSetListColor={(listId, colorId) => saveList(listId, { color: colorId || null })}
          onReorderBoards={reorderBoards}
          onAddList={addList}
          // Walter 2026-06-15 (s1): mobile onboarding was missing the
          // "Set up a reminder" step because onTryReminderList wasn't
          // threaded to the mobile Sidebar. Pass the same seeded
          // reminder-list creator the desktop sidebar uses.
          onTryReminderList={addReminderList}
          onUpdate={handleUpdate}
          onArchive={handleArchive}
          onUnarchive={handleUnarchive}
          onDelete={handleDelete}
          onMoveList={moveListToBoard}
          onReorder={handleReorder}
          onToggleReminderList={toggleReminderList}
          onShareList={openShareForList}
          onPeekShare={openShareForList}
          onPlaceList={openPlaceListPicker}
          onOpenReminderEditor={openReminderEditor}
          onRequestNewReminder={openCreateReminder}
          // For shared boards (where current user is a guest editor),
          // we have to point lists at the OWNER's path, not the
          // current user's. boardOwnerUid is plumbed in from BoardRoute.
          boardOwnerUid={boardOwnerUid}
          isGuestOnBoard={isGuestOnBoard}
          sharedWithMe={sharedWithMe}
          sharedListPlacements={sharedListPlacements}
          saveStatus={saveStatus}
          queuedCount={queuedCount}
          onSaveStatus={handleSaveStatus}
          registerPendingFlush={registerPendingFlush}
          onForceSync={forceSyncAll}
        />
        <UndoToastStack
          toasts={toasts}
          onUndo={runUndo}
          onDismiss={dismissToast}
        />
        {showMobileHint && <MobileModeHint />}
        {/* "place in board…" picker shared with desktop. Mounted at
            BoardPage's top level so it survives MobileBoard re-mounts
            and so the modal state lives outside the canvas/sidebar
            split. */}
        <BoardPickerModal
          open={!!placeListTarget}
          onClose={() => setPlaceListTarget(null)}
          onPick={handlePlaceListPick}
          boards={boards}
          defaultBoardId={board?.id}
          listTitle={placeListTarget?.title}
        />
      </>
    );
  }

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <div style={{
        height: 'var(--header-h)',
        // Walter 2026-06-26: the board colour washes the top nav (so the
        // colour actually does something, not just a dot). Falls through to
        // plain --surface when no colour is set.
        background: getListColorHeaderBg(board?.color, 'var(--surface)', 0.16) || 'var(--surface)',
        display: 'flex', alignItems: 'stretch',
        flexShrink: 0, zIndex: 500, position: 'relative',
      }}>
        {/* Sidebar toggle (Walter 2026-05-05). Lives outside the
            "logo frame" div below so the top + bottom gray borders of
            that frame visually wrap only the logo / nav content and
            leave the toggle to the left, integrated with the sidebar
            column. Position is fixed at top-left whether the sidebar
            is open or collapsed. */}
        <button
          onClick={() => setSidebarOpen(v => !v)}
          title={sidebarOpen ? 'hide sidebar (\\)' : 'show sidebar (\\)'}
          style={{
            width: 40, display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'none', border: 'none',
            color: sidebarOpen ? 'var(--accent)' : 'var(--text-dim)',
            cursor: 'pointer', transition: 'color 0.15s, background 0.15s',
            flexShrink: 0,
          }}
          onMouseEnter={e => {
            if (!sidebarOpen) e.currentTarget.style.color = 'var(--text)';
            e.currentTarget.style.background = 'var(--surface)';
          }}
          onMouseLeave={e => {
            if (!sidebarOpen) e.currentTarget.style.color = 'var(--text-dim)';
            e.currentTarget.style.background = 'none';
          }}
        >
          {/* Closed rounded rectangle with a vertical divider — reads
              as "two-column / sidebar" rather than the previous open-
              bottom bracket which Walter said looked like an 'm'. */}
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <rect x="1" y="2" width="12" height="10" rx="1.5"
              stroke="currentColor" strokeWidth="1.3" fill="none" />
            <line x1="5.5" y1="2.5" x2="5.5" y2="11.5"
              stroke="currentColor" strokeWidth="1.3" />
          </svg>
        </button>

        {/* "Logo frame" — left + bottom gray borders bracket the
            logo / nav row. The left border (Walter 2026-05-05 v3) is
            the faint vertical separator between the toggle and the
            wordmark; the bottom border continues the divider that
            separates the header from the canvas below. Walter
            2026-05-15: dropped the top border — it ran along the
            very top edge of the viewport and added no visual
            structure (browser chrome / OS chrome already provides
            that edge). The two-sided frame reads cleaner. */}
        <div style={{
          flex: 1, display: 'flex', alignItems: 'center',
          padding: '0 14px', gap: '8px',
          borderBottom: '1px solid var(--border)',
          borderLeft: '1px solid var(--border)',
          minWidth: 0,
        }}>

        {/* Walter 2026-05-09: wordmark links to /landing so clicking
            the logo from anywhere in the app goes "home" — matches
            standard web convention + lets users find the marketing
            pages from inside the app. The /landing CTA flips to
            "Open your boards →" when signed in, so no risk of
            stranding the user. */}
        {/* Wordmark yields below 1000px — the brand is the least useful
            element in a cramped header, and the ~100px keeps the board name
            + format dropdown alive at super-narrow widths (Walter 2026-07-19). */}
        {showWordmark && (
          <Link to="/landing" style={{ textDecoration: 'none', display: 'inline-flex', marginRight: '8px', flexShrink: 0 }}>
            <Wordmark fontSize={15} />
          </Link>
        )}

        <div ref={boardMenuRef} style={{ position: 'relative', minWidth: 0 }}>
          {renamingBoard ? (
            // Walter 2026-06-26: inline rename (replaces window.prompt). The
            // name button becomes a text field in place; Enter commits, Escape
            // cancels (renameCancelRef stops the blur-commit clobbering it).
            <input
              autoFocus
              value={boardNameDraft}
              onChange={e => setBoardNameDraft(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') { e.preventDefault(); commitBoardRename(); }
                else if (e.key === 'Escape') { e.preventDefault(); cancelBoardRename(); }
              }}
              onBlur={commitBoardRename}
              style={{
                background: 'var(--surface)', border: '1px solid var(--accent)',
                color: 'var(--text)', fontFamily: 'var(--mono)',
                fontSize: 'calc(12px * var(--ui-scale, 1))', padding: '6px 12px',
                borderRadius: 'var(--radius)', outline: 'none', boxSizing: 'border-box',
                maxWidth: 'min(calc(22ch * var(--ui-scale, 1) + 30px), 100%)',
                width: '22ch', minWidth: 0,
              }}
            />
          ) : (
            <button
              onClick={() => setShowBoardMenu(v => !v)}
              // Hover tooltip carries the full name, since we ellipsize the
              // visible label below at ~22 chars to keep the header from
              // expanding into the action buttons on the right.
              title={board.name}
              style={{
                background: 'none', border: '1px solid var(--border)',
                color: 'var(--text)', fontFamily: 'var(--mono)',
                fontSize: 'calc(12px * var(--ui-scale, 1))', padding: '6px 12px',
                borderRadius: 'var(--radius)', display: 'flex',
                alignItems: 'center', gap: '6px', cursor: 'pointer',
                transition: 'border-color 0.15s',
                maxWidth: 'min(calc(22ch * var(--ui-scale, 1) + 30px), 100%)',
                minWidth: 0,
              }}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--border-bright)'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
            >
              {/* Walter 2026-06-26: board colour dot. */}
              <ColorDot colorId={board.color} />
              {/* Walter 2026-06-09: tiny "board:" prefix so new users
                  realise this control (and the nav names) ARE boards — a
                  tester didn't connect the header names with boards. */}
              {!headerCompact && (
                <span style={{
                  fontSize: 'calc(9px * var(--ui-scale, 1))',
                  color: 'var(--text-dimmer)',
                  textTransform: 'uppercase', letterSpacing: '0.5px',
                  flexShrink: 0,
                }}>board:</span>
              )}
              <span style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0,
              }}>{board.name}</span>
              <svg width="8" height="8" viewBox="0 0 8 8" fill="none" style={{ flexShrink: 0 }}>
                <path d="M1 2.5l3 3 3-3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
              </svg>
            </button>
          )}

          {showBoardMenu && !renamingBoard && (
            // Pure switcher now — per-board actions live in the board ⋯ menu.
            <div style={{
              position: 'absolute', top: '100%', left: 0, marginTop: '4px',
              background: 'var(--surface)', border: '1px solid var(--border-bright)',
              borderRadius: 'var(--radius)', minWidth: '220px',
              boxShadow: 'var(--shadow-popover)',
              zIndex: 1000, overflow: 'hidden',
              maxHeight: 'calc(100vh - 80px)', overflowY: 'auto',
            }}>
              {boards.length > 1 && (
                <>
                  <div style={{
                    padding: '8px 12px 4px', fontSize: 'calc(10px * var(--ui-scale, 1))',
                    color: 'var(--text-dimmer)', fontFamily: 'var(--mono)',
                    textTransform: 'uppercase', letterSpacing: '0.5px',
                  }}>switch to</div>
                  {boards.filter(b => b.id !== board.id).map(b => (
                    <MenuItem
                      key={b.id}
                      icon={<ColorDot colorId={b.color} />}
                      onClick={() => {
                        setShowBoardMenu(false);
                        setSidebarOpen(true);
                        onSwitchBoard?.(b.id);
                      }}
                    >
                      {b.name || '(untitled)'}
                    </MenuItem>
                  ))}
                  {!isGuestOnBoard && <div style={{ borderTop: '1px solid var(--border)', margin: '4px 0' }} />}
                </>
              )}
              {!isGuestOnBoard && (
                <MenuItem icon={<IconPlus />} onClick={() => {
                  setShowBoardMenu(false);
                  setSidebarOpen(true);
                  onNewBoard?.();
                }}>
                  new board
                </MenuItem>
              )}
            </div>
          )}
        </div>

        {/* Board ⋯ — per-board actions (rename / colour / share / sort /
            delete), split out of the switcher so the name button just
            switches. Owner-only; hidden mid-rename. Walter 2026-06-26. */}
        {!isGuestOnBoard && !renamingBoard && (
          <div ref={boardActionsRef} style={{ position: 'relative', flexShrink: 0 }}>
            <button
              onClick={() => setShowBoardActions(v => !v)}
              title="board options"
              style={{
                background: 'none', border: 'none', padding: '4px',
                color: showBoardActions ? 'var(--accent)' : 'var(--text-dim)',
                cursor: 'pointer', flexShrink: 0, transition: 'color 0.15s',
                display: 'inline-flex', alignItems: 'center',
              }}
              onMouseEnter={e => { if (!showBoardActions) e.currentTarget.style.color = 'var(--text)'; }}
              onMouseLeave={e => { if (!showBoardActions) e.currentTarget.style.color = 'var(--text-dim)'; }}
            >
              {/* Walter 2026-06-26: vertical 3-dot kebab, matching the list ⋯. */}
              <svg width="13" height="13" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                <circle cx="6" cy="2.5" r="1" fill="currentColor" />
                <circle cx="6" cy="6" r="1" fill="currentColor" />
                <circle cx="6" cy="9.5" r="1" fill="currentColor" />
              </svg>
            </button>
            {showBoardActions && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, marginTop: '4px',
                background: 'var(--surface)', border: '1px solid var(--border-bright)',
                borderRadius: 'var(--radius)', minWidth: '180px',
                boxShadow: 'var(--shadow-popover)', zIndex: 1000, padding: '2px 0',
              }}>
                <MenuItem icon={<IconPencil />} onClick={startBoardRename}>rename</MenuItem>
                <SubMenu icon={<IconColor />} label="colour">
                  <ColorSwatches
                    current={board.color}
                    onPick={(id) => { onUpdateBoard(board.id, { color: id || null }); setShowBoardActions(false); }}
                  />
                </SubMenu>
                <MenuItem icon={<IconShare />} onClick={() => { setShowBoardActions(false); setShowShareModal(true); }}>share</MenuItem>
                <SubMenu icon={<IconSort />} label="sort lists in nav">
                  <MenuItem onClick={() => { handleSortLists('canvas'); setShowBoardActions(false); }}>by canvas order</MenuItem>
                  <MenuItem onClick={() => { handleSortLists('alpha'); setShowBoardActions(false); }}>alphabetical</MenuItem>
                </SubMenu>
                {boards.length > 1 && (
                  <>
                    <div style={{ borderTop: '1px solid var(--border)', margin: '4px 0' }} />
                    <MenuItem
                      icon={<IconTrash />} color="var(--danger)"
                      onClick={() => { if (window.confirm(`Move board "${board.name}" and all its lists to trash?\n\nYou can restore it within 30 days from the trash menu (account popover → trash).`)) { onDeleteBoard(board.id); } setShowBoardActions(false); }}
                    >delete</MenuItem>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* Universal + dropdown — was "new list", now opens a small
            menu with new list / new board / (new column when in
            grid mode). Removes ambiguity about what "+" creates. */}
        <div ref={addMenuRef} style={{ position: 'relative' }}>
          <Btn accent onClick={() => setShowAddMenu(v => !v)} title="create new (list / reminder list / board / column)">
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
              <path d="M5 1v8M1 5h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            new
          </Btn>
          {showAddMenu && (
            <div style={{
              position: 'absolute', top: '100%', left: 0, marginTop: '4px',
              background: 'var(--surface)', border: '1px solid var(--border-bright)',
              borderRadius: 'var(--radius)', minWidth: '180px',
              boxShadow: 'var(--shadow-popover)',
              zIndex: 1000, overflow: 'hidden',
              fontFamily: 'var(--mono)',
              fontSize: 'calc(11px * var(--ui-scale, 1))',
            }}>
              {/* Owner OR guest-editor: list / reminder-list creation.
                  Walter 2026-05-11: previously the whole new-list block
                  was gated on `!isGuestOnBoard`; sharing refinements
                  let guest editors create lists on shared boards (with
                  the inherited ACL + a hint toast). Column ops stay
                  owner-only (gridColumnCount lives on the board doc,
                  which guests can't update). Guest viewers see the
                  footnote below explaining why these items are hidden. */}
              {canAddLists && (
                <>
                  <div
                    onClick={() => { setShowAddMenu(false); addList(); }}
                    style={addMenuItemStyle}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--surface2)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <span>new list</span>
                    <Hint>{SHORTCUT.newList}</Hint>
                  </div>
                  {/* Designated-reminder-list shortcut. Creates a list,
                      pre-titles it "Reminders" with a starter tip, and
                      registers it in userPrefs.designatedReminderListIds
                      so every paragraph parses as a reminder. Avoids
                      the "create list → settings → designate" 3-step
                      dance for what's a common case. */}
                  <div
                    onClick={() => { setShowAddMenu(false); addReminderList(); }}
                    style={addMenuItemStyle}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--surface2)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <span>new reminder list <span style={{
                      marginLeft: 6,
                      color: 'var(--accent)',
                      fontSize: 'calc(10px * var(--ui-scale, 1))',
                    }}>🔔</span></span>
                  </div>
                  {/* Walter 2026-05-11: log-list shortcut, parallel to the
                      reminder-list shortcut above. Creates a list pre-
                      titled "Log", seeds an explainer //Note, and
                      designates it so new lines auto-stamp with @today. */}
                  <div
                    onClick={() => { setShowAddMenu(false); addLogList(); }}
                    style={addMenuItemStyle}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--surface2)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <span>new log list <span style={{
                      marginLeft: 6,
                      color: 'var(--accent)',
                      fontSize: 'calc(10px * var(--ui-scale, 1))',
                    }}>@</span></span>
                  </div>
                </>
              )}
              <div
                onClick={() => { setShowAddMenu(false); onNewBoard?.(); }}
                style={addMenuItemStyle}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--surface2)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                new board
              </div>
              {!isGuestOnBoard && boardMode === 'grid' && (
                <>
                  <div
                    onClick={async () => {
                      setShowAddMenu(false);
                      // New leftmost column: shift every list's gridCol
                      // up by one so col 0 ends up empty, then bump
                      // gridColumnCount. Lists without a numeric
                      // gridCol (legacy free-mode docs) are skipped —
                      // they'll get assigned on next layout.
                      await Promise.all(
                        (lists || [])
                          .filter(l => typeof l.gridCol === 'number')
                          .map(l => saveList(l.id, { gridCol: l.gridCol + 1 }))
                      );
                      const cur = board?.gridColumnCount || 1;
                      await onUpdateBoard(board.id, { gridColumnCount: cur + 1 });
                    }}
                    style={addMenuItemStyle}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--surface2)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    new column (left)
                  </div>
                  <div
                    onClick={() => {
                      setShowAddMenu(false);
                      const cur = board?.gridColumnCount || 1;
                      onUpdateBoard(board.id, { gridColumnCount: cur + 1 });
                    }}
                    style={addMenuItemStyle}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--surface2)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    new column (right)
                  </div>
                </>
              )}
              {/* Guest viewer footnote — explains why list / column
                  items are hidden. Guest EDITORS see the list items
                  (per Walter 2026-05-11 sharing refinements); only
                  pure viewers still get the read-only message. */}
              {isGuestOnBoard && !isGuestEditor && (
                <div style={{
                  padding: '8px 12px',
                  borderTop: '1px solid var(--border)',
                  fontFamily: 'var(--mono)',
                  fontSize: 'calc(10px * var(--ui-scale, 1))',
                  color: 'var(--text-dimmer)',
                  lineHeight: 1.4,
                  fontStyle: 'italic',
                }}>
                  this shared board is read-only for you — ask the owner for editor access. you can still create your own boards from above.
                </div>
              )}
              {/* Column ops stay owner-only since gridColumnCount/
                  gridColumnWeights live on the board doc itself, which
                  guest editors can't update. Hidden for guests of any
                  role; the new-list path above covers their needs. */}
            </div>
          )}
        </div>

        <Btn onClick={() => { setShowFind(false); setShowSearch(true); }} title={searchCompact ? `search (${SHORTCUT.search})` : undefined}>
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <circle cx="4" cy="4" r="3" stroke="currentColor" strokeWidth="1.2"/>
            <path d="M6.5 6.5l2 2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
          </svg>
          {/* Label + shortcut hint give way EARLY (<1450) so the format strip
              and board name keep their room (Walter 2026-07-19). Icon stays. */}
          {!searchCompact && <>search<Hint>{SHORTCUT.search}</Hint></>}
        </Btn>

        {/* Free/Grid toggle — the single entry point for layout control.
            Replaces the old one-shot "arrange" button: flipping into grid
            mode does the arrange now, and grid mode itself handles ongoing
            reflow on viewport changes. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
          <ModeToggle mode={boardMode} onToggle={handleToggleMode} />

          {/* Walter 2026-06-24: Format menu — applies line formatting (highlight,
              header, done, reminder, clear) to the focused list. Greyed until a
              list is focused. Inline strip ≥1200px, else Format ▾ dropdown. */}
          <FormatMenu />
        </div>

        <div style={{ flex: 1, minWidth: 8 }} />

        {/* Walter 2026-05-15: replaced the text-based "saving\u2026/\u2713 saved"
            indicator with the richer SaveIndicator component. Reads the
            same saveStatus state but renders floppy + countdown +
            typing + syncing + synced / local variants, and is tappable
            to force-flush all pending list edits. See
            src/components/SaveIndicator.js + DESIGN_LOG ("Save
            indicator + sync vs save vocabulary"). */}
        <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
          <SaveIndicator status={saveStatus} onForceSync={forceSyncAll} queuedCount={queuedCount} />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
          {/* Bell button — opens the agenda panel. Walter 2026-05-06
              round 6: always shown regardless of the reminders
              feature toggle so the affordance is discoverable. When
              the agenda opens with no reminders and the feature is
              off, the empty-state copy explains how to turn it on.
              Badge counts open + dated reminders due-today or
              overdue. */}
          {true && (
            <button
              onClick={() => setShowAgenda(true)}
              title="reminders"
              style={{
                position: 'relative',
                width: 28, height: 28, display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--text-dim)', borderRadius: '4px',
                padding: 0, transition: 'color 0.15s, background 0.15s',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.color = 'var(--text)';
                e.currentTarget.style.background = 'var(--surface2)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.color = 'var(--text-dim)';
                e.currentTarget.style.background = 'transparent';
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="1.8"
                strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/>
                <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/>
              </svg>
              {(() => {
                const now = new Date();
                const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                // Walter 2026-05-06 round 6: when overdue is
                // "hide until tomorrow", don't include past-due
                // items in the badge (would defeat the point).
                const overdueHidden = overdueHiddenForBadge > Date.now();
                const dueOrOverdue = (reminders || []).filter(r => {
                  if (r.status === 'done') return false;
                  const d = r.parsedDate?.toDate?.() || (r.parsedDate ? new Date(r.parsedDate) : null);
                  if (!d) return false;
                  if (overdueHidden && d < now) return false;
                  return d <= now || (d >= today && d < new Date(today.getTime() + 86400000));
                }).length;
                if (dueOrOverdue === 0) return null;
                return (
                  <span style={{
                    position: 'absolute', top: '-2px', right: '-2px',
                    minWidth: '14px', height: '14px',
                    borderRadius: '7px',
                    background: 'var(--accent)',
                    color: 'var(--bg)',
                    fontSize: '9px', fontWeight: 700,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: '0 3px',
                    fontFamily: 'var(--mono)',
                  }}>{dueOrOverdue}</span>
                );
              })()}
            </button>
          )}
          {/* Walter 2026-05-06 round 7: share icon in the top
              header so users can reach the share modal for the
              current board with one click — the previous path
              (board-name dropdown → share board) was an extra
              click and not super discoverable. Hidden when the
              user is a guest on this board (the share UI is
              read-only for them anyway, but the icon would be a
              dead end). */}
          {!isGuestOnBoard && !user?.isAnonymous && board && (
            <button
              onClick={() => setShowShareModal(true)}
              title="share board (lists can be shared individually too)"
              style={{
                width: 28, height: 28, display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--text-dim)', borderRadius: '4px',
                padding: 0, transition: 'color 0.15s, background 0.15s',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.color = 'var(--text)';
                e.currentTarget.style.background = 'var(--surface2)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.color = 'var(--text-dim)';
                e.currentTarget.style.background = 'transparent';
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="1.8"
                strokeLinecap="round" strokeLinejoin="round">
                <circle cx="6" cy="12" r="2.5"/>
                <circle cx="18" cy="6" r="2.5"/>
                <circle cx="18" cy="18" r="2.5"/>
                <line x1="8.2" y1="10.7" x2="15.8" y2="7.3"/>
                <line x1="8.2" y1="13.3" x2="15.8" y2="16.7"/>
              </svg>
            </button>
          )}
          <button
            onClick={() => setShowSettings(true)}
            title="settings"
            style={{
              width: 28, height: 28, display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--text-dim)', borderRadius: '4px',
              padding: 0, transition: 'color 0.15s, background 0.15s',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.color = 'var(--text)';
              e.currentTarget.style.background = 'var(--surface2)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.color = 'var(--text-dim)';
              e.currentTarget.style.background = 'transparent';
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="1.8"
              strokeLinecap="round" strokeLinejoin="round">
              <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/>
              <circle cx="12" cy="12" r="3"/>
            </svg>
          </button>
          {/* Account button — Walter 2026-05-16: dropped the spelled-out
              first name + the larger colored initial-orb in favour of a
              28×28 icon-only button matching the bell / share / settings
              weight. The name + email are still shown inside the
              popover when expanded, and the tooltip on hover surfaces
              the email without clicking. Photo-URL users (Google
              accounts) get their actual avatar at the same 18px size;
              everyone else gets a head-and-shoulders silhouette. */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowAccount(v => !v)}
              title={user.isAnonymous ? 'guest session' : (user.email || 'account')}
              style={{
                // Walter 2026-05-26: when in guest mode, grow the
                // button to stack a tiny "guest" label below the
                // dashed person icon. Without the label users (per
                // tester feedback) didn't immediately recognise the
                // dashed silhouette as "you're not signed in". The
                // signed-in case keeps the 28×28 footprint that
                // matches the other header icons (bell / share /
                // settings) for visual rhythm. Width still 28; only
                // height grows, so the row alignment via items:center
                // keeps the icon vertically centred against
                // neighbours.
                width: 28,
                height: user.isAnonymous ? 'auto' : 28,
                minHeight: 28,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: user.isAnonymous ? '1px' : 0,
                background: showAccount ? 'var(--surface2)' : 'none',
                border: 'none',
                cursor: 'pointer',
                color: showAccount ? 'var(--text)' : 'var(--text-dim)',
                borderRadius: '4px',
                padding: user.isAnonymous ? '2px 0 3px' : 0,
                transition: 'color 0.15s, background 0.15s',
              }}
              onMouseEnter={e => {
                if (!showAccount) {
                  e.currentTarget.style.background = 'var(--surface2)';
                  e.currentTarget.style.color = 'var(--text)';
                }
              }}
              onMouseLeave={e => {
                if (!showAccount) {
                  e.currentTarget.style.background = 'none';
                  e.currentTarget.style.color = 'var(--text-dim)';
                }
              }}
            >
              {user.isAnonymous ? (
                <PersonIcon size={15} dashed />
              ) : user.photoURL ? (
                <img
                  src={user.photoURL}
                  alt=""
                  style={{ width: 18, height: 18, borderRadius: '50%' }}
                />
              ) : (
                <PersonIcon size={15} />
              )}
              {user.isAnonymous && (
                <span style={{
                  fontFamily: 'var(--mono)',
                  fontSize: '8px',
                  letterSpacing: '0.3px',
                  color: 'currentColor',
                  lineHeight: 1,
                }}>
                  guest
                </span>
              )}
            </button>
            {showAccount && (
              <AccountPopover
                user={user}
                boards={boards}
                listsCount={lists.length}
                onClose={() => setShowAccount(false)}
                onSignOut={() => { setShowAccount(false); onSignOut(); }}
                onLinkGoogle={async () => {
                  // AccountPopover catches auth/credential-already-in-use
                  // and any other errors itself — we just close the popover
                  // on success so the user sees the freshly linked state.
                  await onLinkGoogle();
                  setShowAccount(false);
                }}
                onLinkMagicLink={onLinkMagicLink}
              />
            )}
          </div>
        </div>
        {/* end of "logo frame" div */}
        </div>
      </div>

      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        {sidebarOpen && (
          <Sidebar
            lists={lists}
            focusedId={focusedId}
            width={sidebarWidth}
            boardColor={board?.color}
            onResize={setSidebarWidth}
            // Walter 2026-05-31: onboarding-checklist inputs. hasShared
            // is a light "have they shared anything" signal from the
            // user's own boards' ACLs (per-list shares on the active
            // board count too) — informational, drives the "share a
            // list" tip. onRequestSignIn opens the account popover.
            isAnonymous={!!user?.isAnonymous}
            onOpenSettings={() => setShowSettings(true)}
            hasShared={
              (Array.isArray(boards) && boards.some(b => (b?.editors?.length || 0) + (b?.viewers?.length || 0) > 0)) ||
              (Array.isArray(lists) && lists.some(l => (l?.editors?.length || 0) + (l?.viewers?.length || 0) > 0))
            }
            onRequestSignIn={() => setShowAccount(true)}
            onTryReminderList={addReminderList}
            hasReminderList={
              Array.isArray(userPrefs?.designatedReminderListIds)
              && userPrefs.designatedReminderListIds.length > 0
            }
            // Walter 2026-06-16: onboarding step 5 (desktop only) — opens the
            // keyboard cheat-sheet. Not wired on MobileBoard (no keyboard).
            onShowShortcuts={() => setShowShortcuts(true)}
            onSelectRow={handleSidebarSelect}
            onRowDoubleClick={(l) => {
              // Closed/archived tiles: reopen (or activate) first so
              // there's a valid panel to expand into.
              if (l.state === 'closed') {
                saveList(l.id, { state: 'open' });
              }
              handleExpand(l.id, 0);
            }}
            onArchive={(l) => handleArchive(l.id)}
            onUnarchive={(l) => handleUnarchive(l.id)}
            onDelete={(l) => handleDelete(l.id)}
            onReorder={handleReorder}
            onSortLists={handleSortLists}
            designatedReminderListIds={userPrefs?.designatedReminderListIds || []}
            onToggleReminderList={toggleReminderList}
            remindersFeatureOn={true}
            onShareList={openShareForList}
            onPeekShare={openShareForList}
            // Always wired — the badge per row only renders when the
            // list has share state, so there's no stray UI for users
            // who haven't been touched by sharing. ViewOnly mode in
            // the modal protects guests from invite/remove controls.
            onPlaceList={openPlaceListPicker}
            sharedWithMe={sharedWithMe}
            sharedListPlacements={sharedListPlacements}
            onNavigateBoard={onSwitchBoard}
            onNavigateList={(listId) => navigate(`/list/${listId}`)}
            onNewBoard={handleNewBoardGuarded}
            boards={boards}
            currentBoardId={board?.id}
            onRenameBoard={onRenameBoard}
            onUpdateBoard={onUpdateBoard}
            onDeleteBoard={onDeleteBoard}
            onSetListColor={(listId, colorId) => saveList(listId, { color: colorId || null })}
            reminders={reminders}
            listIdToBoardId={listIdToBoardId}
            onOpenTrash={() => navigate('/trash')}
            trashCount={trashCount}
            onMoveList={moveListToBoard}
            onFetchBoardLists={(targetBoardId) => fetchBoardLists(listsOwnerUid, targetBoardId)}
            onReorderBoards={reorderBoards}
          />
        )}

        {boardMode === 'grid' ? (
          <div
            ref={canvasRef}
            // Walter 2026-06-26: faint board-colour wash behind the canvas
            // (the .board-tinted rule turns the dot-grid base transparent so
            // this shows through; dots still draw on top).
            className={getListColorHeaderBg(board?.color) ? 'board-tinted' : undefined}
            // minWidth:0 is load-bearing here: in a flex row, children
            // default to min-width:auto (their intrinsic content width).
            // Without this the canvas wrapper can't shrink below the grid's
            // current inner pixel width, so horizontal window-shrink gets
            // stuck at whatever the widest-ever layout was.
            style={{ flex: 1, display: 'flex', minHeight: 0, minWidth: 0, isolation: 'isolate', background: getListColorHeaderBg(board?.color, 'var(--bg)', 0.1) || undefined }}
          >
            <GridCanvas
              lists={canvasLists}
              listsLoading={listsLoading}
              columnCount={gridColumnCount}
              columnWeights={board?.gridColumnWeights}
              onUpdate={handleUpdate}
              onAddList={() => addList()}
              onClose={handleClose}
              onArchive={handleArchive}
              onUnarchive={handleUnarchive}
              onDelete={handleDelete}
              onFocus={focusPanel}
              onExpand={handleExpand}
              focusedId={focusedId}
              onSaveStatus={handleSaveStatus}
              registerPendingFlush={registerPendingFlush}
              boards={boards}
              currentBoardId={board?.id}
              onMoveList={moveListToBoard}
              userPrefs={userPrefs}
              updateUserPrefs={updateUserPrefs}
              createReminder={createReminder}
              reminders={reminders}
              updateReminder={updateReminder}
              deleteReminder={deleteReminder}
              pushToast={pushToast}
              onOpenAgenda={() => setShowAgenda(true)}
              onOpenReminderEditor={openReminderEditor}
              onRequestNewReminder={openCreateReminder}
              // Sharing/collab plumbing — GridCanvas forwards these to
              // each ListPanel it renders. Without them the soft-lock
              // is dead because currentUid arrives undefined.
              currentUid={user?.uid}
              isGuest={!!user?.isAnonymous}
              onRequestSignIn={() => setShowAccount(true)}
              currentUserName={user?.displayName || user?.email || 'someone'}
              boardOwnerUid={boardOwnerUid || board?.ownerId || user?.uid}
              boardEditors={board?.editors || []}
              boardViewers={board?.viewers || []}
              onShareList={openShareForList}
              onPeekShare={openShareForList}
              onPlaceList={openPlaceListPicker}
              onRemoveColumn={removeGridColumn}
              onColumnCountChange={(newCount, newWeights) => {
                // GridCanvas passes newWeights along when it needs the two
                // fields updated atomically (e.g. inserting a column in
                // the middle requires splicing a weight at the same index).
                const patch = { gridColumnCount: newCount };
                if (newWeights) patch.gridColumnWeights = newWeights;
                onUpdateBoard(board.id, patch);
              }}
              onColumnWeightsChange={(weights) =>
                onUpdateBoard(board.id, { gridColumnWeights: weights })
              }
              jumpTarget={
                expandedListId == null && jumpTarget ? jumpTarget : null
              }
              onJumpHandled={() => setJumpTarget(null)}
              onTriggerOrganize={(listId) => triggerOrganize({ listId, sourceBoardId: board?.id })}
            />
          </div>
        ) : (
          <div
            ref={canvasRef}
            // Walter 2026-06-26: faint board-colour wash behind the free canvas
            // (see .board-tinted in index.css — turns the inner dot-grid base
            // transparent so this shows through).
            className={getListColorHeaderBg(board?.color) ? 'board-tinted' : undefined}
            onClick={(e) => {
              setShowBoardMenu(false);
              // Dismiss grid when clicking bare canvas (but not when a tile or
              // grid divider intercepts the click — those stop propagation).
              if (e.target === e.currentTarget || e.target === e.currentTarget.firstElementChild) {
                setGridMode(false);
              }
            }}
            style={{
              flex: 1, position: 'relative', overflow: 'auto',
              background: getListColorHeaderBg(board?.color, 'var(--bg)', 0.1) || undefined,
              // Isolate the tile stacking context. Each tile sets its own zIndex
              // (list.z), which climbs every time a tile is clicked. Without
              // isolation those values eventually climb past FocusedPanel's z
              // (601) and hide the focused panel behind the canvas tiles.
              isolation: 'isolate',
            }}
          >
            <div
              className="dot-grid"
              style={{
                position: 'relative',
                width:  Math.max(canvasContentSize.w, 1) + 'px',
                height: Math.max(canvasContentSize.h, 1) + 'px',
                minWidth: '100%', minHeight: '100%',
              }}
            >
              {canvasLists.map(list => (
                <ListPanel
                  key={list.id}
                  list={list}
                  onUpdate={handleUpdate}
                  onClose={handleClose}
                  onArchive={handleArchive}
                  onUnarchive={handleUnarchive}
                  onDelete={handleDelete}
                  onFocus={focusPanel}
                  onExpand={handleExpand}
                  isFocused={focusedId === list.id}
                  onSaveStatus={handleSaveStatus}
                  registerPendingFlush={registerPendingFlush}
                  boards={boards}
                  currentBoardId={board?.id}
                  onMoveList={moveListToBoard}
                  userPrefs={userPrefs}
                  updateUserPrefs={updateUserPrefs}
                  createReminder={createReminder}
                  reminders={reminders}
                  updateReminder={updateReminder}
                  deleteReminder={deleteReminder}
                  pushToast={pushToast}
                  onOpenAgenda={() => setShowAgenda(true)}
                  onOpenReminderEditor={openReminderEditor}
                  onRequestNewReminder={openCreateReminder}
                  jumpTarget={
                    expandedListId !== list.id && jumpTarget?.listId === list.id
                      ? jumpTarget
                      : null
                  }
                  onJumpHandled={() => setJumpTarget(null)}
                  // Edit-lock plumbing — only meaningful when this board
                  // has collaborators AND the user has alpha sharing on.
                  // useEditLock short-circuits to canEdit=true otherwise.
                  currentUid={user?.uid}
                  isGuest={!!user?.isAnonymous}
                  onRequestSignIn={() => setShowAccount(true)}
                  currentUserName={user?.displayName || user?.email || 'someone'}
                  boardOwnerUid={boardOwnerUid || board?.ownerId || user?.uid}
                  boardEditors={board?.editors || []}
                  boardViewers={board?.viewers || []}
                  onShareList={openShareForList}
                  onPeekShare={openShareForList}
                  onPlaceList={openPlaceListPicker}
                  isSharedWithMe={isGuestOnBoard || !!list._sharedFromOwnerUid}
                  // Walter 2026-05-13: pending-organize plumbing. The
                  // tile-header organize button calls onTriggerOrganize;
                  // when the list is the one with a pending organize,
                  // ListPanel locks editing + shows an Accept/Undo
                  // band at the top of the tile body.
                  onTriggerOrganize={() => triggerOrganize({ listId: list.id, sourceBoardId: board?.id })}
                />
              ))}

              {canvasLists.length === 0 && lists.length === 0 && !listsLoading && (
                // Walter 2026-06-16: gate on !listsLoading so the card doesn't
                // FLASH during the mount→first-snapshot window on every reload
                // (the s2a float made that flicker very visible). Only show
                // when lists have actually loaded and the board is truly empty.
                // Walter 2026-06-09: prominent first-step card (replaces a
                // dim dead-centre line testers missed) — near the top, a
                // real CTA button + a hint that boards live in the switcher.
                <div style={{
                  position: 'absolute', inset: 0,
                  display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'flex-start',
                  paddingTop: '11vh', userSelect: 'none', pointerEvents: 'none',
                }}>
                  <div style={{
                    pointerEvents: 'auto',
                    maxWidth: 380, width: 'calc(100% - 32px)',
                    background: 'var(--surface)', border: '1px solid var(--accent-dim)',
                    borderRadius: 'var(--radius)', boxShadow: 'var(--shadow-modal-flat)',
                    padding: '20px 22px', textAlign: 'center', fontFamily: 'var(--mono)',
                  }}>
                    <div style={{ fontSize: 'calc(14px * var(--ui-scale, 1))', fontWeight: 600, color: 'var(--text)', marginBottom: '6px' }}>
                      This is your board — let's add something.
                    </div>
                    <div style={{ fontSize: 'calc(11px * var(--ui-scale, 1))', color: 'var(--text-dim)', lineHeight: 1.55, marginBottom: '14px' }}>
                      A board holds your lists and notes. Add your first list to get going — or press <strong>{SHORTCUT.newList}</strong> any time.
                    </div>
                    <button type="button" onClick={() => addList()}
                      style={{ background: 'var(--accent)', color: 'var(--bg)', border: '1px solid var(--accent)', borderRadius: 'var(--radius)', fontFamily: 'var(--mono)', fontSize: 'calc(12px * var(--ui-scale, 1))', fontWeight: 600, padding: '9px 18px', cursor: 'pointer' }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'var(--accent-dim)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'var(--accent)'; }}>
                      + add your first list
                    </button>
                    <div style={{ marginTop: '9px', fontSize: 'calc(10px * var(--ui-scale, 1))', color: 'var(--text-dimmer)', lineHeight: 1.5 }}>
                      to add more lists, click <strong>+ new</strong> up top or just press <strong>{SHORTCUT.newList}</strong>
                    </div>
                    <div style={{ marginTop: '16px', paddingTop: '12px', borderTop: '1px solid var(--border)', fontSize: 'calc(10px * var(--ui-scale, 1))', color: 'var(--text-dimmer)', lineHeight: 1.5 }}>
                      Have more than one board? Switch between them from the <strong>boards</strong> menu next to the logo, top-left.
                    </div>
                  </div>
                </div>
              )}
              {canvasLists.length === 0 && lists.length > 0 && (
                <div style={{
                  position: 'absolute', inset: 0,
                  display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center',
                  color: 'var(--text-dimmer)', fontFamily: 'var(--mono)',
                  fontSize: 'calc(12px * var(--ui-scale, 1))',
                  gap: '10px', userSelect: 'none',
                  pointerEvents: 'none',
                }}>
                  <div style={{ fontSize: 'calc(32px * var(--ui-scale, 1))', opacity: 0.2 }}>{'\u2591'}</div>
                  <div>
                    {lists.length === 0
                      ? `empty board · click "+ new" or press ${SHORTCUT.newList} to add a list`
                      : `no notes open · click one in the sidebar or press ${SHORTCUT.newList}`}
                  </div>
                </div>
              )}

              {/* GridOverlay (old post-arrange green-line adjuster) removed:
                  replaced by grid mode itself, which owns column layout
                  persistently. */}
            </div>
          </div>
        )}
      </div>

      {expandedListId && lists.find(l => l.id === expandedListId) && (() => {
        const expandedList = lists.find(l => l.id === expandedListId);
        return (
          <FocusedPanel
            list={expandedList}
            onUpdate={handleUpdate}
            onExit={handleFocusedExit}
            onSaveStatus={handleSaveStatus}
            registerPendingFlush={registerPendingFlush}
            boards={boards}
            currentBoardId={board?.id}
            onMoveList={moveListToBoard}
            userPrefs={userPrefs}
            updateUserPrefs={updateUserPrefs}
            createReminder={createReminder}
            reminders={reminders}
            updateReminder={updateReminder}
            deleteReminder={deleteReminder}
            pushToast={pushToast}
            jumpTarget={jumpTarget?.listId === expandedListId ? jumpTarget : null}
            onJumpHandled={() => setJumpTarget(null)}
            // Edit-lock plumbing — same shape as ListPanel above.
            currentUid={user?.uid}
            isGuest={!!user?.isAnonymous}
            onRequestSignIn={() => setShowAccount(true)}
            currentUserName={user?.displayName || user?.email || 'someone'}
            boardOwnerUid={boardOwnerUid || board?.ownerId || user?.uid}
            boardEditors={board?.editors || []}
            boardViewers={board?.viewers || []}
            onShareList={openShareForList}
            onPeekShare={openShareForList}
            onPlaceList={openPlaceListPicker}
            isSharedWithMe={isGuestOnBoard || !!expandedList?._sharedFromOwnerUid}
            onOpenAgenda={() => setShowAgenda(true)}
            onOpenReminderEditor={openReminderEditor}
            onRequestNewReminder={openCreateReminder}
            // Walter 2026-06-11: organise/sort in the focused view too
            // (same plumbing as the ListPanel tile above).
            onTriggerOrganize={() => triggerOrganize({ listId: expandedList.id, sourceBoardId: board?.id })}
          />
        );
      })()}

      {showSearch && (
        <SearchModal
          userId={user.uid}
          boards={boards}
          currentBoardId={board.id}
          onJump={handleJump}
          onClose={() => setShowSearch(false)}
        />
      )}

      {/* Old arrange column picker removed — the grid-mode entry picker
          (below) owns column-count selection now. */}

      {/* Free → Grid column-count picker. Visually identical to the
          arrange picker above; kept separate so the two flows can evolve
          independently (grid-entry may grow per-column settings later). */}
      {enterGridPicker && (
        <div
          onClick={() => setEnterGridPicker(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 2000,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(0,0,0,0.35)',
            animation: 'lists-fadein 0.15s ease',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault(); e.stopPropagation();
                commitFreeToGrid(enterGridPicker.selected);
              }
            }}
            style={{
              background: 'var(--surface2)',
              border: '1px solid var(--border-bright)',
              borderRadius: 'var(--radius)',
              padding: '20px 24px',
              fontFamily: 'var(--mono)', color: 'var(--text)',
              boxShadow: 'var(--shadow-popover-mid)',
              display: 'flex', flexDirection: 'column', gap: '16px',
              minWidth: '300px',
            }}
          >
            <div style={{
              fontSize: 'calc(13px * var(--ui-scale, 1))',
              textAlign: 'center', color: 'var(--text-dim)',
            }}>
              switch to grid — how many columns?
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', alignItems: 'center' }}>
              {Array.from(
                // Walter 2026-05-05: ensure the detected free-mode
                // column count is always offered, even when the
                // viewport can't strictly fit it at MIN_COL (cap at 6
                // either way so the row of buttons never gets silly).
                { length: Math.min(Math.max(enterGridPicker.maxFit, enterGridPicker.detected), 6) },
                (_, k) => k + 1,
              ).map(n => {
                const isSelected = n === enterGridPicker.selected;
                const isDetected = n === enterGridPicker.detected;
                return (
                  <button
                    key={n}
                    onClick={() => setEnterGridPicker(p => p && { ...p, selected: n })}
                    title={isDetected ? 'detected count' : undefined}
                    style={{
                      padding: isDetected ? '8px 14px' : '6px 10px',
                      minWidth: isDetected ? '40px' : '32px',
                      fontSize: isDetected
                        ? 'calc(14px * var(--ui-scale, 1))'
                        : 'calc(12px * var(--ui-scale, 1))',
                      background: isSelected ? 'var(--accent)' : 'transparent',
                      color: isSelected ? 'var(--bg)' : 'var(--accent)',
                      border: `1px solid ${isSelected ? 'var(--accent)' : 'var(--accent-dim)'}`,
                      borderRadius: 'var(--radius)',
                      fontFamily: 'var(--mono)',
                      fontWeight: isDetected ? 600 : 400,
                      cursor: 'pointer',
                      transition: 'background 0.12s, color 0.12s, border-color 0.12s',
                    }}
                  >
                    {n}
                  </button>
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button
                onClick={() => setEnterGridPicker(null)}
                style={{
                  padding: '6px 16px', background: 'transparent',
                  color: 'var(--text-dim)',
                  border: '1px solid var(--border-bright)',
                  borderRadius: 'var(--radius)',
                  fontFamily: 'var(--mono)',
                  fontSize: 'calc(11px * var(--ui-scale, 1))',
                  cursor: 'pointer',
                }}
              >
                cancel
              </button>
              <button
                autoFocus
                onClick={() => commitFreeToGrid(enterGridPicker.selected)}
                style={{
                  padding: '6px 22px', background: 'var(--accent)',
                  color: 'var(--bg)', border: '1px solid var(--accent)',
                  borderRadius: 'var(--radius)',
                  fontFamily: 'var(--mono)',
                  fontSize: 'calc(11px * var(--ui-scale, 1))',
                  fontWeight: 500, cursor: 'pointer',
                }}
              >
                switch to grid
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Grid → Free: Keep vs Restore choice. Only surfaces when we have a
          prior free snapshot to restore from. If no snapshot, startGridToFree
          committed silently and this never renders. */}
      {exitGridChoice && (
        <div
          onClick={() => setExitGridChoice(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 2000,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(0,0,0,0.35)',
            animation: 'lists-fadein 0.15s ease',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'var(--surface2)',
              border: '1px solid var(--border-bright)',
              borderRadius: 'var(--radius)',
              padding: '20px 24px',
              fontFamily: 'var(--mono)', color: 'var(--text)',
              boxShadow: 'var(--shadow-popover-mid)',
              display: 'flex', flexDirection: 'column', gap: '18px',
              minWidth: '340px', maxWidth: '420px',
            }}
          >
            <div style={{
              fontSize: 'calc(13px * var(--ui-scale, 1))',
              textAlign: 'center', color: 'var(--text-dim)',
              lineHeight: 1.55,
            }}>
              switching back to free mode. what do you want to keep?
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={() => commitGridToFree('keep')}
                style={{
                  padding: '8px 16px', background: 'transparent',
                  color: 'var(--accent)',
                  border: '1px solid var(--accent-dim)',
                  borderRadius: 'var(--radius)',
                  fontFamily: 'var(--mono)',
                  fontSize: 'calc(11px * var(--ui-scale, 1))',
                  cursor: 'pointer', textAlign: 'center',
                }}
              >
                keep current grid layout
              </button>
              <button
                onClick={() => commitGridToFree('restore')}
                style={{
                  padding: '8px 16px', background: 'var(--accent)',
                  color: 'var(--bg)', border: '1px solid var(--accent)',
                  borderRadius: 'var(--radius)',
                  fontFamily: 'var(--mono)',
                  fontSize: 'calc(11px * var(--ui-scale, 1))',
                  fontWeight: 500, cursor: 'pointer',
                }}
              >
                restore previous free layout
              </button>
            </div>
            <button
              onClick={() => setExitGridChoice(false)}
              style={{
                padding: '4px', background: 'transparent',
                color: 'var(--text-dimmer)', border: 'none',
                fontFamily: 'var(--mono)',
                fontSize: 'calc(10px * var(--ui-scale, 1))',
                cursor: 'pointer',
              }}
            >
              cancel
            </button>
          </div>
        </div>
      )}

      {/* "place in board…" picker — opened from a shared list's ⋯-menu
          (own or guest view). Maps the shared list to one of the user's
          own boards via a sharedListPlacements doc. Walter 2026-05-11. */}
      <BoardPickerModal
        open={!!placeListTarget}
        onClose={() => setPlaceListTarget(null)}
        onPick={handlePlaceListPick}
        boards={boards}
        defaultBoardId={!isGuestOnBoard ? board?.id : undefined}
        listTitle={placeListTarget?.title}
      />

      {/* Share modal — alpha gated. Renders only when sharing is on,
          and only when the user explicitly opens it via the board
          dropdown (or in Phase 2, per-list ⋯ menu). target=null means
          the whole board; future: target.kind='list' for list-scoped.

          NOT alpha-gated. The alpha gate stays on the share-MENU items
          (those are owner-management surfaces); the modal itself can
          render unconditionally because the OPEN paths are still gated
          where it matters. The ShareBadge click is the new path that
          works for non-alpha users — they get viewOnly mode (see the
          viewOnly prop derivation), which is just an informational
          peek of who else has access. */}
      <ShareModal
        open={showShareModal}
        onClose={() => { setShowShareModal(false); setShareTarget(null); }}
        target={shareTarget || (board ? {
          kind: 'board',
          ownerUid: listsOwnerUid,
          boardId: board.id,
          title: board.name,
          editors: board.editors || [],
          viewers: board.viewers || [],
          shareToken: board.shareToken || null,
          shareTokenRole: board.shareTokenRole || 'viewer',
        } : null)}
        pushToast={pushToast}
        // viewOnly when the current user is a guest on the board
        // (per-board or per-list share recipient — they can read
        // share state but can't invite or remove). Walter
        // 2026-05-06 round 7: removed the sharingAlphaEnabled gate
        // since sharing is no longer alpha-toggled.
        viewOnly={isGuestOnBoard}
      />

      <ShortcutsModal
        open={showShortcuts}
        onClose={() => setShowShortcuts(false)}
      />

      <DemoWalkthrough
        open={showDemo}
        onClose={() => setShowDemo(false)}
      />

      <FindBar open={showFind} onClose={() => setShowFind(false)} />

      <SettingsModal
        open={showSettings}
        onClose={() => setShowSettings(false)}
        theme={theme}
        onThemeChange={setTheme}
        palette={palette}
        onPaletteChange={setPalette}
        fontSize={fontSize}
        onFontSizeChange={setFontSize}
        lineHeight={lineHeight}
        onLineHeightChange={setLineHeight}
        boardMode={boardMode}
        onBoardModeChange={(next) => { if (next !== boardMode) handleToggleMode(); }}
        onSignOut={onSignOut}
        user={user}
        boards={boards}
        lists={lists}
        listsCount={lists?.length || 0}
        userPrefs={userPrefs}
        updateUserPrefs={updateUserPrefs}
        pushSub={pushSub}
        currentBoardId={board?.id}
        onLinkGoogle={onLinkGoogle}
        onLinkMagicLink={onLinkMagicLink}
      />

      <AgendaPanel
        open={showAgenda}
        onClose={() => setShowAgenda(false)}
        defaultReminderTime={userPrefs?.defaultReminderTime}
        pendingEditReminderId={pendingEditReminderId}
        onConsumePendingEdit={() => setPendingEditReminderId(null)}
        // Walter 2026-10-09: open at the reminder his cursor was on. Resolved
        // here (not inside the panel) because the editor's memory of the caret
        // line has to be turned into a reminder id, which needs the hash the
        // capture engine uses. Null when the cursor wasn't on a reminder line.
        focusReminderId={showAgenda ? resolveCaretReminderId(reminders, hashReminderLine) : null}
        data-caret-tick={caretTick}   /* forces the recompute above; see #248 */
        reminders={reminders}
        lists={lists}
        currentUid={listsOwnerUid}
        listIdToBoardId={listIdToBoardId}
        updateReminder={updateReminderAndSource}
        updateNotes={updateReminderNotes}
        deactivateReminder={deactivateReminder}
        deleteReminder={fullyDeleteReminder}
        pushToast={pushToast}
        onJumpToReminder={(r) => {
          // Resolve which board this reminder lives on. New reminders
          // store sourceBoardId on the doc; older ones fall back to
          // the listIdToBoardId map (built from a one-time read across
          // all the user's boards). If we can't find it either way,
          // do nothing.
          //
          // Walter 2026-05-11: also compute the bell-line's character
          // offset inside the source list so the textarea scrolls to
          // (and places the caret at) the specific reminder, not just
          // the top of the list.
          const resolvedBoardId = r.sourceBoardId
            || (listIdToBoardId && listIdToBoardId[r.sourceListId])
            || null;
          const onAnotherBoard = resolvedBoardId && resolvedBoardId !== board?.id;
          // findBellLineOffset returns the char offset of the bell-
          // prefixed line whose hash matches r.sourceLineHash. Null
          // when the line can't be located (cross-board fallback
          // can't compute it without fetching; same-board uses local
          // `lists`).
          const findOffset = (sourceList) => {
            if (!sourceList) return null;
            const content = sourceList.content || '';
            const lines = content.split('\n');
            const BELL_RE = /^(?:~~)?(?:🔔|🔕|\[DONE\])↻?\s?/;
            // Index every bell line with its char-offset + post-prefix
            // content. We do two passes against this index: stored
            // hash first, then a text-content fallback.
            const candidates = [];
            let acc = 0;
            for (let i = 0; i < lines.length; i++) {
              const ln = lines[i];
              const m = ln.match(BELL_RE);
              if (m) {
                candidates.push({ offset: acc, post: ln.slice(m[0].length) });
              }
              acc += ln.length + 1;
            }
            // Pass 1: stored hash lookup. Fast path when the line
            // hasn't drifted from when the reminder was created.
            if (r.sourceLineHash) {
              for (const c of candidates) {
                if (hashReminderLine(c.post) === r.sourceLineHash) return c.offset;
              }
            }
            // Walter 2026-05-13 (s2): hash drift fallback. Migrations
            // like "~~🔔 task~~" → "[DONE] task" change the post-
            // content hash but don't always update the reminder's
            // stored sourceLineHash, so a strict hash-only lookup
            // misses and the caret falls through to offset 0 (lands
            // on the FIRST reminder visibly, not the clicked one).
            // Fall back to a text-content match: pick the candidate
            // whose post-content contains the reminder text, ties
            // broken by tightest match (longest overlap relative to
            // line length).
            const remText = (r.text || '').trim();
            if (remText) {
              let best = null;
              let bestScore = 0;
              for (const c of candidates) {
                if (c.post.includes(remText)) {
                  const score = remText.length / Math.max(1, c.post.length);
                  if (score > bestScore) { bestScore = score; best = c; }
                }
              }
              if (best) return best.offset;
            }
            // Walter 2026-05-12: third pass for the colon-strip era.
            // After commit 512593c we strip a leading ":\s*" from
            // restText on parse, so r.text on existing reminders may
            // differ from the literal text after the bell prefix
            // (e.g. doc text = "delta..." but source line tail =
            // ": delta..."). The includes() pass above misses that.
            // Strip the date/recurrence prefix from c.post (everything
            // up to the first colon) and compare the trailing payload
            // to r.text. Match if the payload exactly equals r.text
            // OR starts with it. We don't include the reverse-includes
            // direction — that would be too loose and could match
            // unrelated bell lines whose tails happen to be a prefix
            // of r.text.
            if (remText) {
              for (const c of candidates) {
                const colonIdx = c.post.indexOf(':');
                if (colonIdx < 0) continue;
                const tail = c.post.slice(colonIdx + 1).trim();
                if (tail === remText || tail.startsWith(remText)) return c.offset;
              }
            }
            // Final-pass miss — log so future drift is debuggable in
            // DevTools without needing repro-by-test. The caret will
            // fall to offset 0; user sees the list, just not the line.
            console.warn('[reminder-jump] no source-line match', {
              reminderId: r.id,
              text: remText,
              hash: r.sourceLineHash,
              candidates: candidates.length,
              candidatePosts: candidates.slice(0, 5).map(c => c.post),
            });
            return null;
          };
          if (onAnotherBoard) {
            // Cross-board: we don't have the source content locally
            // here. Send just the listId; the destination board's
            // jumpTarget effect handles the scroll once it loads.
            setShowAgenda(false);
            setJumpTarget({ listId: r.sourceListId });
            navigate(`/board/${resolvedBoardId}`);
            return;
          }
          const targetList = lists.find(l => l.id === r.sourceListId);
          if (targetList) {
            const charOffset = findOffset(targetList) ?? 0;
            setShowAgenda(false);
            setJumpTarget({ listId: r.sourceListId, charOffset });
            focusPanel(r.sourceListId);
          }
        }}
        onRequestNewReminder={async (anchorRect) => {
          // Walter 2026-05-21: do NOT close the agenda. The
          // new-reminder popover opens on top of the agenda; after
          // creating, the user expects to be back in the agenda.
          // Clicking "+" inside a panel should not dismiss the panel.
          // (Earlier this closed the agenda on a successful open —
          // Walter found that disorienting.)
          await openCreateReminderFromAgenda(anchorRect);
        }}
      />

      <UndoToastStack
        toasts={toasts}
        onUndo={runUndo}
        onDismiss={dismissToast}
      />

      {/* Create-mode reminder popover. Walter 2026-05-12: triggered by
          the "+" button in the designated reminder list's tile header.
          Renders the standard ReminderEditPopover in create mode (no
          reminder.id, onCreate set) — same NL editor + preview as the
          edit popover, just blank and routed to creation on save. */}
      {createReminderTarget && (
        <ReminderEditPopover
          reminder={{ text: '', parsedDate: null, recurrence: null, status: 'open' }}
          defaultReminderTime={userPrefs?.defaultReminderTime}
          sourceList={createReminderTarget.list}
          notes={[]}
          anchorRect={createReminderTarget.anchorRect}
          onCreate={handleCreateReminderFromPopover}
          onClose={() => setCreateReminderTarget(null)}
        />
      )}
     </div>
  );
}

// Segmented Free/Grid switch in the desktop header. Taps the opposite
// mode to trigger the toggle; the current mode is highlighted. Icons
// (chaotic tiles vs tidy grid) replace the previous text labels — the
// shapes carry the spatial story better than the words "free" and
// "grid" do, especially for first-time users. Tooltips spell out
// the action for anyone who hesitates.
function ModeToggle({ mode, onToggle }) {
  const isGrid = mode === 'grid';
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'stretch',
      border: '1px solid var(--border-bright)',
      borderRadius: 'var(--radius)',
      overflow: 'hidden', flexShrink: 0,
    }}>
      <ModeSegment
        active={!isGrid}
        onClick={!isGrid ? undefined : onToggle}
        title={!isGrid ? 'free layout (current)' : 'switch to free layout'}
      >
        <FreeModeIcon />
      </ModeSegment>
      <ModeSegment
        active={isGrid}
        onClick={isGrid ? undefined : onToggle}
        title={isGrid ? 'grid layout (current)' : 'switch to grid layout'}
      >
        <GridModeIcon />
      </ModeSegment>
    </div>
  );
}

function ModeSegment({ children, active, onClick, title }) {
  return (
    <button
      onClick={onClick}
      disabled={!onClick}
      title={title}
      style={{
        padding: '5px 10px',
        background: active ? 'var(--accent)' : 'transparent',
        color: active ? 'var(--bg)' : 'var(--text-dim)',
        border: 'none',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'background 0.12s, color 0.12s',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onMouseEnter={e => {
        if (!active && onClick) e.currentTarget.style.color = 'var(--text)';
      }}
      onMouseLeave={e => {
        if (!active && onClick) e.currentTarget.style.color = 'var(--text-dim)';
      }}
    >
      {children}
    </button>
  );
}

// FreeModeIcon — three slightly-offset rectangles. The mild rotation
// + overlap suggests the canvas's "tiles wherever you drag them" feel
// without going full chaos (which would read as broken).
function FreeModeIcon() {
  return (
    <svg width="16" height="14" viewBox="0 0 16 14" fill="none" aria-hidden="true">
      <rect x="0.5" y="3" width="6" height="5" rx="1" stroke="currentColor" strokeWidth="1.2" transform="rotate(-6 3.5 5.5)"/>
      <rect x="6" y="6" width="7" height="5.5" rx="1" stroke="currentColor" strokeWidth="1.2" transform="rotate(4 9.5 8.75)"/>
      <rect x="9" y="0.5" width="5.5" height="4" rx="1" stroke="currentColor" strokeWidth="1.2" transform="rotate(-3 11.75 2.5)"/>
    </svg>
  );
}

// GridModeIcon — perfect 2x2, equal gaps. Filled rectangles read more
// boldly at this size than outlines do, and the contrast against the
// outlined Free icon makes the active/inactive state cleaner.
function GridModeIcon() {
  return (
    <svg width="16" height="14" viewBox="0 0 16 14" fill="none" aria-hidden="true">
      <rect x="0.5" y="0.5"  width="6.5" height="5.5" rx="1" fill="currentColor"/>
      <rect x="9"   y="0.5"  width="6.5" height="5.5" rx="1" fill="currentColor"/>
      <rect x="0.5" y="8"    width="6.5" height="5.5" rx="1" fill="currentColor"/>
      <rect x="9"   y="8"    width="6.5" height="5.5" rx="1" fill="currentColor"/>
    </svg>
  );
}

function Btn({ children, onClick, accent, title }) {
  return (
    <button
      onClick={onClick}
      title={title}
      style={{
        padding: '5px 10px',
        background: accent ? 'var(--accent)' : 'transparent',
        color: accent ? 'var(--bg)' : 'var(--text-dim)',
        border: accent ? '1px solid var(--accent)' : '1px solid var(--border-bright)',
        borderRadius: 'var(--radius)',
        fontFamily: 'var(--mono)',
        fontSize: 'calc(11px * var(--ui-scale, 1))',
        fontWeight: accent ? 600 : 400,
        cursor: 'pointer',
        // Header priority-collapse: never squish/wrap, so the label can't spill
        // onto neighbours (the overlap Walter hit). Width is reclaimed by hiding
        // labels at narrow widths instead — see headerCompact.
        flexShrink: 0, whiteSpace: 'nowrap',
        display: 'inline-flex', alignItems: 'center', gap: '6px',
        transition: 'background 0.12s, color 0.12s, border-color 0.12s',
      }}
      onMouseEnter={e => {
        if (accent) {
          e.currentTarget.style.background = 'var(--accent-dim)';
          e.currentTarget.style.borderColor = 'var(--accent-dim)';
        } else {
          e.currentTarget.style.color = 'var(--text)';
          e.currentTarget.style.borderColor = 'var(--text-dim)';
        }
      }}
      onMouseLeave={e => {
        if (accent) {
          e.currentTarget.style.background = 'var(--accent)';
          e.currentTarget.style.borderColor = 'var(--accent)';
        } else {
          e.currentTarget.style.color = 'var(--text-dim)';
          e.currentTarget.style.borderColor = 'var(--border-bright)';
        }
      }}
    >
      {children}
    </button>
  );
}

function Hint({ children }) {
  return (
    <span style={{
      fontFamily: 'var(--mono)',
      fontSize: 'calc(10px * var(--ui-scale, 1))',
      color: 'var(--text-dimmer)',
      marginLeft: '4px',
    }}>
      {children}
    </span>
  );
}

// Tiny top-center notification that appears when the window narrows into
// mobile mode. Fades out over ~2s and never blocks input (pointerEvents:none).
function MobileModeHint() {
  const [opacity, setOpacity] = useState(1);
  useEffect(() => {
    // Mount at full opacity, then trigger a CSS transition to 0. The 40ms
    // delay ensures the initial frame renders at opacity:1 before the fade
    // begins — without it the transition sometimes gets skipped.
    const t = setTimeout(() => setOpacity(0), 40);
    return () => clearTimeout(t);
  }, []);
  return (
    <div style={{
      position: 'fixed',
      top: 'calc(var(--header-h) + 8px)',
      left: '50%',
      transform: 'translateX(-50%)',
      padding: '7px 14px',
      background: 'var(--surface2)',
      border: '1px solid var(--border-bright)',
      borderRadius: 'var(--radius)',
      fontFamily: 'var(--mono)',
      fontSize: 'calc(11px * var(--ui-scale, 1))',
      color: 'var(--text-dim)',
      boxShadow: 'var(--shadow-banner)',
      opacity,
      transition: 'opacity 4.75s ease',
      pointerEvents: 'none',
      zIndex: 3000,
      whiteSpace: 'nowrap',
    }}>
      narrow window {'>'} mobile view
    </div>
  );
}

// Avatar fallback when the user has no photoURL — shows the first letter of
// their name/email in a small circle. Same visual weight as a real photo.
// Walter 2026-05-16: head-and-shoulders silhouette for the header
// account button. Matches the line-art weight of the bell / share /
// settings icons. `dashed` flag swaps the body to a dashed outline
// for the guest (anonymous) case, picking up the same "tentative"
// affordance the old GuestAvatar's dashed-ring carried.
function PersonIcon({ size = 14, dashed = false }) {
  return (
    <svg
      width={size} height={size} viewBox="0 0 16 16"
      fill="none" aria-hidden="true"
      style={{ display: 'block' }}
    >
      <circle cx="8" cy="5.5" r="2.8" stroke="currentColor" strokeWidth="1.3"
        strokeDasharray={dashed ? '2 1.5' : undefined}/>
      <path d="M2.5 14 C2.5 10.5, 5 9, 8 9 C11 9, 13.5 10.5, 13.5 14"
        stroke="currentColor" strokeWidth="1.3"
        strokeLinecap="round"
        strokeDasharray={dashed ? '2 1.5' : undefined}/>
    </svg>
  );
}

function InitialAvatar({ text }) {
  const ch = (text || '?').trim().charAt(0).toUpperCase() || '?';
  return (
    <div style={{
      width: 22, height: 22, borderRadius: '50%',
      background: 'var(--surface2)',
      border: '1px solid var(--border-bright)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: 'var(--mono)',
      fontSize: '10px', fontWeight: 600,
      color: 'var(--text-dim)',
      flexShrink: 0,
    }}>
      {ch}
    </div>
  );
}

// Guest "?" badge — visually distinct so users notice the unsaved state at
// a glance. Border-only (no fill) to underscore the in-flight nature.
function GuestAvatar() {
  return (
    <div style={{
      width: 22, height: 22, borderRadius: '50%',
      background: 'transparent',
      border: '1px dashed var(--border-bright)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: 'var(--mono)',
      fontSize: '11px', fontWeight: 600,
      color: 'var(--text-dim)',
      flexShrink: 0,
    }}>
      ?
    </div>
  );
}
